import { fundingHqFetch, getFundingApiErrorMessage } from './fundingApi';
import { resolveSelectedClientId } from './fundingClientsService';
import {
  FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL,
  PROXY_BADGE_AUTHORIZED,
  assertPrimaryBrowserScriptFilename,
  normalizeAuthorizedQaProxySummary,
  resolveAuthorizedQaProxyLaunchConfigUrl,
  PRIMARY_LAUNCHER,
  QA_AUTHORIZED_LAUNCHER,
  QA_LAUNCHER_TEMPLATE_VERSION,
  qaLauncherCompatibilityMarker,
  qaLauncherVersionMarker,
} from './fundingAuthorizedQaProxyService';

const PROFILE_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;
const CLIENT_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

const BROWSER_PROFILE_MANAGER_ROLES = new Set([
  'platform_owner',
  'platform_admin',
  'super_admin',
  'internal_admin',
  'owner',
  'admin',
  'agency_owner',
  'agency_admin',
]);

export const PRIMARY_BROWSER_PROFILE_SLUG = 'primary';
export const PRIMARY_BROWSER_DISPLAY_NAME = 'Primary Browser';
export const BACKUP_BROWSER_DISPLAY_NAME = 'Backup Browser';
export const AUTHORIZED_QA_PROXY_SESSION_SLUG = 'authorized_qa_proxy_session';
export const AUTHORIZED_QA_PROXY_SESSION_DISPLAY_NAME = 'Authorized QA Proxy Session';
export const AUTHORIZED_QA_PROXY_SESSION_BANNER =
  'Authorized QA Proxy Session — all browser traffic in this session uses the approved QA proxy.';
export const AUTHORIZED_QA_PROXY_SESSION_BLOCK_MESSAGE =
  'This website is not approved for QA Proxy use.';

export const DEFAULT_STARTUP_URL = 'https://www.google.com';
export const BROWSERLEAKS_STARTUP_URL = 'https://browserleaks.com/ip';
export const DEFAULT_SEARCH_ENGINE = 'google';

export const STARTUP_URL_PRESETS = [
  { id: 'google', label: 'Google', url: DEFAULT_STARTUP_URL },
  { id: 'browserleaks', label: 'BrowserLeaks IP check', url: BROWSERLEAKS_STARTUP_URL },
  { id: 'custom', label: 'Custom URL', url: '' },
];

export const DISPLAY_MODE_ADAPTIVE = 'adaptive';
export const DISPLAY_MODE_FIXED = 'fixed';
export const CAMOUFOX_OS_MACOS = 'macos';
export const CAMOUFOX_OS_WINDOWS = 'windows';
export const CAMOUFOX_OS_LINUX = 'linux';
export const GENERIC_FALLBACK_WIDTH = 1280;
export const GENERIC_FALLBACK_HEIGHT = 800;
export const ADAPTIVE_DISPLAY_EXPLANATION =
  'Adaptive display detects this computer when the browser launches, so pages remain centered, scroll correctly, and fit the user’s monitor.';

export const CAMOUFOX_OS_OPTIONS = [
  { id: CAMOUFOX_OS_MACOS, label: 'macOS' },
  { id: CAMOUFOX_OS_WINDOWS, label: 'Windows' },
  { id: CAMOUFOX_OS_LINUX, label: 'Linux' },
];

/**
 * Browser Profiles is enabled by default in FundGenius HQ.
 * Set REACT_APP_FUNDING_BROWSER_PROFILES_ENABLED=false to hide the tab.
 */
export const isFundingBrowserProfilesEnabled = () => {
  const flag = process.env.REACT_APP_FUNDING_BROWSER_PROFILES_ENABLED;
  if (flag == null || String(flag).trim() === '') {
    return true;
  }
  return String(flag).trim().toLowerCase() !== 'false';
};

export function canManageFundingBrowserProfiles(user) {
  const role = String(user?.role || '').toLowerCase();
  return BROWSER_PROFILE_MANAGER_ROLES.has(role);
}

export const sanitizeBrowserProfileName = (value = '') => {
  const trimmed = String(value).trim();

  if (!trimmed) {
    return '';
  }

  if (!PROFILE_NAME_PATTERN.test(trimmed)) {
    throw new Error('Profile name can only include letters, numbers, dash, and underscore.');
  }

  if (trimmed.includes('..') || trimmed.includes('/') || trimmed.includes('\\')) {
    throw new Error('Unsafe profile name.');
  }

  return trimmed;
};

export const resolveBrowserProfileClientId = (client) => {
  if (!client) return '';
  const persistedId = resolveSelectedClientId(client)
    || client?.id
    || client?.clientId
    || client?.client_id
    || client?.fundingClientId
    || client?.funding_client_id
    || client?._id;
  return persistedId ? String(persistedId) : '';
};

export const buildBrowserProfilePath = ({ clientId, profileName }) => {
  const safeProfileName = sanitizeBrowserProfileName(profileName);

  if (!clientId) {
    throw new Error('Client ID is required.');
  }

  if (!CLIENT_ID_PATTERN.test(clientId)) {
    throw new Error('Unsafe client ID.');
  }

  return `profiles/${clientId}/${safeProfileName}`;
};

export function buildBrowserProfileScriptFilename(clientId, profileSlug, storedFilename) {
  if (storedFilename) return storedFilename;
  const safeSlug = sanitizeBrowserProfileName(profileSlug);
  return `${clientId}_${safeSlug}.py`;
}

const MARKDOWN_LINK_RE = /\[([^\]]*)]\((https?:\/\/[^)\s]+)\)/i;
const MARKDOWN_ESCAPE_RE = /\\([\\`*_\[\]()#+.!{}-])/g;

/**
 * Store and emit only a bare http(s) URL.
 * Legacy Markdown links like [label](https://example.com) are unwrapped.
 */
export function normalizeStartupUrl(value) {
  let trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return DEFAULT_STARTUP_URL;
  }

  // Undo accidental Markdown escaping (e.g. https://example.com/path\_a)
  trimmed = trimmed.replace(MARKDOWN_ESCAPE_RE, '$1');

  const markdownMatch = trimmed.match(MARKDOWN_LINK_RE);
  if (markdownMatch) {
    trimmed = String(markdownMatch[2] || '').trim();
  }

  // Bare URL may still be wrapped in angle brackets from rich editors
  if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
    trimmed = trimmed.slice(1, -1).trim();
  }

  if (!isSafeStartupUrl(trimmed)) {
    return DEFAULT_STARTUP_URL;
  }
  return trimmed;
}

export function resolveBrowserProfileStartupUrl(profileOrUrl) {
  if (typeof profileOrUrl === 'string') {
    return normalizeStartupUrl(profileOrUrl);
  }
  const url = profileOrUrl?.startupUrl ?? profileOrUrl?.startup_url;
  return normalizeStartupUrl(url);
}

/** Detect Markdown corruption that must never appear in copy/setup payloads. */
export function launcherTextHasMarkdownCorruption(text) {
  const value = String(text ?? '');
  if (!value) return false;
  if (value.includes('\\_')) return true;
  if (value.includes('[https://')) return true;
  if (value.includes('](https://')) return true;
  if (value.includes('&quot;') || value.includes('&amp;') || value.includes('&#')) return true;
  return false;
}

/**
 * Last-line defense: never ship Markdown-escaped launcher text to Copy buttons.
 * Generators should already emit raw text; this repairs legacy/corrupt inputs.
 */
export function ensureRawLauncherText(text) {
  let out = String(text ?? '');
  if (!out) return out;
  out = out.replace(MARKDOWN_ESCAPE_RE, '$1');
  out = out.replace(
    /STARTUP_URL = "\[([^\]]*)]\((https?:\/\/[^)\s]+)\)"/g,
    'STARTUP_URL = "$2"',
  );
  out = out.replace(
    /STARTUP_URL = '\[([^\]]*)]\((https?:\/\/[^)\s]+)\)'/g,
    "STARTUP_URL = '$2'",
  );
  return out;
}

export const TERMINAL_TARGET_UNIX = 'unix';
export const TERMINAL_TARGET_WINDOWS_POWERSHELL = 'windows_powershell';
export const TERMINAL_TARGET_WINDOWS_CMD = 'windows_cmd';

export const TERMINAL_TARGET_OPTIONS = [
  { id: TERMINAL_TARGET_UNIX, label: 'macOS/Linux Terminal' },
  { id: TERMINAL_TARGET_WINDOWS_POWERSHELL, label: 'Windows PowerShell' },
  { id: TERMINAL_TARGET_WINDOWS_CMD, label: 'Windows Command Prompt' },
];

export function normalizeTerminalTarget(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === TERMINAL_TARGET_WINDOWS_POWERSHELL || raw === 'powershell' || raw === 'pwsh') {
    return TERMINAL_TARGET_WINDOWS_POWERSHELL;
  }
  if (raw === TERMINAL_TARGET_WINDOWS_CMD || raw === 'cmd' || raw === 'command_prompt') {
    return TERMINAL_TARGET_WINDOWS_CMD;
  }
  if (raw === TERMINAL_TARGET_UNIX || raw === 'macos' || raw === 'linux' || raw === 'bash' || raw === 'zsh') {
    return TERMINAL_TARGET_UNIX;
  }
  return '';
}

export function inferTerminalTargetFromOs(os) {
  return isWindowsCamoufoxOs(os)
    ? TERMINAL_TARGET_WINDOWS_POWERSHELL
    : TERMINAL_TARGET_UNIX;
}

export function camoufoxOsForTerminalTarget(terminal, fallbackOs) {
  const target = normalizeTerminalTarget(terminal);
  if (target === TERMINAL_TARGET_WINDOWS_POWERSHELL || target === TERMINAL_TARGET_WINDOWS_CMD) {
    return CAMOUFOX_OS_WINDOWS;
  }
  const normalized = normalizeCamoufoxOs(fallbackOs);
  if (normalized === CAMOUFOX_OS_WINDOWS) {
    return CAMOUFOX_OS_MACOS;
  }
  return normalized || CAMOUFOX_OS_MACOS;
}

export function buildGoogleQuickSearchUrl(query = '') {
  const trimmed = String(query ?? '').trim();
  if (!trimmed) return '';
  return `https://www.google.com/search?q=${encodeURIComponent(trimmed)}`;
}

export function isPrimaryBrowserProfile(profile) {
  if (!profile) return false;
  const slug = profile.profileName || profile.profile_name;
  return slug === PRIMARY_BROWSER_PROFILE_SLUG || profile.isPrimary === true;
}

export function hasPrimaryBrowserProfile(profiles = []) {
  return profiles.some(isPrimaryBrowserProfile);
}

export function getBrowserProfileDisplayName(profile) {
  if (!profile) return 'Browser Profile';
  if (profile.displayName || profile.display_name) {
    return profile.displayName || profile.display_name;
  }
  const slug = profile.profileName || profile.profile_name;
  if (slug === PRIMARY_BROWSER_PROFILE_SLUG) return PRIMARY_BROWSER_DISPLAY_NAME;
  return slug || 'Browser Profile';
}

export function getBrowserProfileSubtitle(profile, clientId) {
  if (isPrimaryBrowserProfile(profile)) {
    return 'Secure Camoufox profile for this client';
  }
  return clientId
    ? `Camoufox profile linked to client ${clientId}`
    : 'Camoufox browser profile';
}

export function getCreateBrowserProfileButtonLabel(profiles = []) {
  return hasPrimaryBrowserProfile(profiles)
    ? 'Add Backup / Testing Browser'
    : 'Create Primary Browser';
}

export function isLegacyBrowserProfile(profile) {
  if (!profile) return false;
  if (isPrimaryBrowserProfile(profile)) return false;
  const slug = profile.profileName || profile.profile_name;
  const displayName = profile.displayName || profile.display_name || getBrowserProfileDisplayName(profile);
  return slug !== PRIMARY_BROWSER_PROFILE_SLUG && displayName !== PRIMARY_BROWSER_DISPLAY_NAME;
}

export function canConvertLegacyToPrimary(profile, profiles = []) {
  return isLegacyBrowserProfile(profile) && !hasPrimaryBrowserProfile(profiles);
}

export function normalizeCamoufoxOs(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (['macos', 'darwin', 'mac', 'osx', 'mac os', 'mac os x'].includes(raw)) {
    return CAMOUFOX_OS_MACOS;
  }
  if (['windows', 'win32', 'win64', 'win'].includes(raw)) {
    return CAMOUFOX_OS_WINDOWS;
  }
  if (['linux', 'x11', 'android', 'cros', 'chrome os'].includes(raw)) {
    return CAMOUFOX_OS_LINUX;
  }
  return '';
}

export function getCamoufoxOsLabel(os) {
  const normalized = normalizeCamoufoxOs(os);
  if (normalized === CAMOUFOX_OS_MACOS) return 'macOS';
  if (normalized === CAMOUFOX_OS_WINDOWS) return 'Windows';
  if (normalized === CAMOUFOX_OS_LINUX) return 'Linux';
  return 'Unknown';
}

export function inferCamoufoxOsFromNavigator({
  platform = '',
  userAgent = '',
  userAgentDataPlatform = '',
} = {}) {
  const haystack = `${userAgentDataPlatform} ${platform} ${userAgent}`.toLowerCase();
  if (/(windows|win32|win64|wow64)/.test(haystack)) return CAMOUFOX_OS_WINDOWS;
  if (/(mac os|macintosh|darwin|iphone|ipad|ipod)/.test(haystack)) return CAMOUFOX_OS_MACOS;
  if (/(linux|x11|android|cros)/.test(haystack)) return CAMOUFOX_OS_LINUX;
  return CAMOUFOX_OS_MACOS;
}

export function resolveFallbackDisplaySize({
  width,
  height,
  screenWidth,
  screenHeight,
  innerWidth,
  innerHeight,
} = {}) {
  const resolvedWidth = Number(width)
    || Number(screenWidth)
    || Number(innerWidth)
    || GENERIC_FALLBACK_WIDTH;
  const resolvedHeight = Number(height)
    || Number(screenHeight)
    || Number(innerHeight)
    || GENERIC_FALLBACK_HEIGHT;
  return {
    width: Math.round(resolvedWidth),
    height: Math.round(resolvedHeight),
  };
}

export function detectBrowserDeviceInfo(globalObj) {
  const scope = globalObj || (typeof window !== 'undefined' ? window : {});
  const nav = scope.navigator || {};
  const screenObj = scope.screen || {};
  const platform = String(nav.platform || '');
  const userAgent = String(nav.userAgent || '');
  const userAgentDataPlatform = String(nav.userAgentData?.platform || '');
  const screenWidth = Number(screenObj.width) || 0;
  const screenHeight = Number(screenObj.height) || 0;
  const innerWidth = Number(scope.innerWidth) || 0;
  const innerHeight = Number(scope.innerHeight) || 0;
  const devicePixelRatio = Number(scope.devicePixelRatio) || 1;
  const os = inferCamoufoxOsFromNavigator({
    platform,
    userAgent,
    userAgentDataPlatform,
  });
  const fallback = resolveFallbackDisplaySize({
    screenWidth,
    screenHeight,
    innerWidth,
    innerHeight,
  });

  return {
    platform,
    userAgent,
    userAgentDataPlatform,
    screenWidth,
    screenHeight,
    innerWidth,
    innerHeight,
    devicePixelRatio,
    os,
    osLabel: getCamoufoxOsLabel(os),
    fallbackWidth: fallback.width,
    fallbackHeight: fallback.height,
    recommendedMode: 'Adaptive display',
  };
}

export function resolveDisplayMode(profile) {
  const explicit = profile?.displayMode || profile?.display_mode;
  if (explicit === DISPLAY_MODE_ADAPTIVE || explicit === DISPLAY_MODE_FIXED) {
    return explicit;
  }
  if (profile && (profile.width || profile.height || profile.zoom)) {
    return DISPLAY_MODE_FIXED;
  }
  return DISPLAY_MODE_ADAPTIVE;
}

export function isAdaptiveDisplayProfile(profile) {
  return resolveDisplayMode(profile) === DISPLAY_MODE_ADAPTIVE;
}

export function needsAdaptiveDisplayUpgrade(profile) {
  return Boolean(profile) && !isAdaptiveDisplayProfile(profile);
}

export function isSafeStartupUrl(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return false;
  if (/^\d+\s*,\s*\d+$/.test(trimmed)) return false;
  if (/^--/.test(trimmed)) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function escapePythonString(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
}

function normalizePythonSource(script) {
  return script.endsWith('\n') ? script : `${script}\n`;
}

function isWindowsCamoufoxOs(os) {
  return normalizeCamoufoxOs(os) === CAMOUFOX_OS_WINDOWS;
}

function buildDeviceSettingsPython({ fallbackWidth, fallbackHeight, fallbackOs } = {}) {
  const fallback = resolveFallbackDisplaySize({
    width: fallbackWidth,
    height: fallbackHeight,
  });
  const safeOs = normalizeCamoufoxOs(fallbackOs) || CAMOUFOX_OS_MACOS;

  return `FALLBACK_OS = "${safeOs}"
FALLBACK_WIDTH = ${fallback.width}
FALLBACK_HEIGHT = ${fallback.height}
MIN_WIDTH = 320
MIN_HEIGHT = 240
MAX_WIDTH = 16384
MAX_HEIGHT = 16384
# Outer-window chrome allowances so the launched window fits the usable work area.
MACOS_MENU_BAR_PX = 28
MACOS_DOCK_PX = 70
WINDOWS_TASKBAR_PX = 48
LINUX_PANEL_PX = 32
BROWSER_SIDE_INSET_PX = 0
FIREFOX_CHROME_HEIGHT_PX = 86
QA_BROWSER_DISPLAY_CURRENT = "current"


def validate_display_size(width, height):
    try:
        parsed_width = int(width)
        parsed_height = int(height)
    except (TypeError, ValueError):
        return None
    if parsed_width < MIN_WIDTH or parsed_height < MIN_HEIGHT:
        return None
    if parsed_width > MAX_WIDTH or parsed_height > MAX_HEIGHT:
        return None
    return parsed_width, parsed_height


def normalize_qa_browser_display(value):
    raw = str(value or QA_BROWSER_DISPLAY_CURRENT).strip().lower()
    if raw in {"0", "main", "primary"}:
        return "1"
    if raw == QA_BROWSER_DISPLAY_CURRENT or raw in {"1", "2", "3", "4", "5"}:
        return raw
    return QA_BROWSER_DISPLAY_CURRENT


def cocoa_rect_to_css(origin_x, origin_y, width, height, desktop_max_y):
    css_left = int(round(origin_x))
    css_top = int(round(desktop_max_y - (origin_y + height)))
    return css_left, css_top, int(round(width)), int(round(height))


def _point_in_cocoa_rect(x, y, origin_x, origin_y, width, height):
    return origin_x <= x <= origin_x + width and origin_y <= y <= origin_y + height


def _display_record(index, logical, work, frame_origin, work_origin, desktop_max_y, is_main, source):
    logical_width, logical_height = logical
    window_width, window_height = work
    if window_width > logical_width:
        window_width = logical_width
    if window_height > logical_height:
        window_height = logical_height
    window_x, window_y, _, _ = cocoa_rect_to_css(
        work_origin[0], work_origin[1], window_width, window_height, desktop_max_y
    )
    return {
        "index": index,
        "label": "Monitor %s (%sx%s)" % (index, logical_width, logical_height),
        "is_main": bool(is_main),
        "logical_width": logical_width,
        "logical_height": logical_height,
        "window_width": window_width,
        "window_height": window_height,
        "window_x": window_x,
        "window_y": window_y,
        "cocoa_frame_x": int(round(frame_origin[0])),
        "cocoa_frame_y": int(round(frame_origin[1])),
        "cocoa_work_x": int(round(work_origin[0])),
        "cocoa_work_y": int(round(work_origin[1])),
        "source": source,
    }


def list_attached_displays():
    """Enumerate attached monitors. Must run on the launcher main thread only."""
    system = platform.system()
    displays = []
    pointer = None
    if system == "Darwin":
        try:
            from AppKit import NSEvent, NSScreen  # type: ignore

            screens = list(NSScreen.screens() or [])
            desktop_max_y = 0.0
            for screen in screens:
                frame = screen.frame()
                desktop_max_y = max(desktop_max_y, float(frame.origin.y) + float(frame.size.height))
            main = NSScreen.mainScreen()
            for index, screen in enumerate(screens, start=1):
                frame = screen.frame()
                visible = screen.visibleFrame()
                logical = validate_display_size(int(frame.size.width), int(frame.size.height))
                work = validate_display_size(int(visible.size.width), int(visible.size.height))
                if not logical:
                    continue
                if not work:
                    work = (
                        max(MIN_WIDTH, logical[0] - BROWSER_SIDE_INSET_PX),
                        max(MIN_HEIGHT, logical[1] - MACOS_MENU_BAR_PX - MACOS_DOCK_PX),
                    )
                displays.append(
                    _display_record(
                        index,
                        logical,
                        work,
                        (float(frame.origin.x), float(frame.origin.y)),
                        (float(visible.origin.x), float(visible.origin.y)),
                        desktop_max_y,
                        screen == main or index == 1,
                        "AppKit NSScreen",
                    )
                )
            try:
                mouse = NSEvent.mouseLocation()
                pointer = (float(mouse.x), float(mouse.y))
            except Exception:
                pointer = None
        except Exception as exc:
            print("Could not enumerate macOS NSScreen displays (%s)." % (exc,))
        if not displays:
            try:
                import Quartz  # type: ignore

                main_id = Quartz.CGMainDisplayID()
                bounds = Quartz.CGDisplayBounds(main_id)
                logical = validate_display_size(int(bounds.size.width), int(bounds.size.height))
                if logical:
                    work = (
                        max(MIN_WIDTH, logical[0] - BROWSER_SIDE_INSET_PX),
                        max(MIN_HEIGHT, logical[1] - MACOS_MENU_BAR_PX - MACOS_DOCK_PX),
                    )
                    displays.append(
                        _display_record(
                            1,
                            logical,
                            work,
                            (float(bounds.origin.x), float(bounds.origin.y)),
                            (float(bounds.origin.x), float(bounds.origin.y)),
                            float(bounds.origin.y) + logical[1],
                            True,
                            "Quartz CGDisplayBounds",
                        )
                    )
            except Exception as exc:
                print("Could not read Quartz CGDisplayBounds (%s)." % (exc,))
        return displays, pointer

    if system == "Windows":
        try:
            import ctypes
            from ctypes import wintypes

            user32 = ctypes.windll.user32
            class RECT(ctypes.Structure):
                _fields_ = [
                    ("left", wintypes.LONG),
                    ("top", wintypes.LONG),
                    ("right", wintypes.LONG),
                    ("bottom", wintypes.LONG),
                ]

            class MONITORINFO(ctypes.Structure):
                _fields_ = [
                    ("cbSize", wintypes.DWORD),
                    ("rcMonitor", RECT),
                    ("rcWork", RECT),
                    ("dwFlags", wintypes.DWORD),
                ]

            monitors = []

            def _cb(handle, _hdc, _rect, _data):
                info = MONITORINFO()
                info.cbSize = ctypes.sizeof(MONITORINFO)
                if user32.GetMonitorInfoW(handle, ctypes.byref(info)):
                    monitors.append(info)
                return 1

            MONITORENUMPROC = ctypes.WINFUNCTYPE(
                ctypes.c_int, wintypes.HMONITOR, wintypes.HDC, ctypes.POINTER(RECT), wintypes.LPARAM
            )
            user32.EnumDisplayMonitors(0, 0, MONITORENUMPROC(_cb), 0)
            desktop_max_y = 0
            for info in monitors:
                desktop_max_y = max(desktop_max_y, int(info.rcMonitor.bottom))
            for index, info in enumerate(monitors, start=1):
                mon = info.rcMonitor
                work = info.rcWork
                logical = validate_display_size(int(mon.right - mon.left), int(mon.bottom - mon.top))
                usable = validate_display_size(int(work.right - work.left), int(work.bottom - work.top))
                if not logical:
                    continue
                if not usable:
                    usable = (
                        max(MIN_WIDTH, logical[0] - BROWSER_SIDE_INSET_PX),
                        max(MIN_HEIGHT, logical[1] - WINDOWS_TASKBAR_PX),
                    )
                displays.append(
                    _display_record(
                        index,
                        logical,
                        usable,
                        (float(mon.left), float(desktop_max_y - mon.bottom)),
                        (float(work.left), float(desktop_max_y - work.bottom)),
                        float(desktop_max_y),
                        bool(info.dwFlags & 1),
                        "Windows GetMonitorInfo",
                    )
                )
            class POINT(ctypes.Structure):
                _fields_ = [("x", wintypes.LONG), ("y", wintypes.LONG)]

            pt = POINT()
            user32.GetCursorPos(ctypes.byref(pt))
            pointer = (float(pt.x), float(desktop_max_y - pt.y)) if displays else None
        except Exception as exc:
            print("Could not enumerate Windows monitors (%s)." % (exc,))
        return displays, pointer

    return displays, pointer


def pick_display(displays, preference, pointer=None):
    if not displays:
        return None, "none"
    pref = normalize_qa_browser_display(preference)
    if pref != QA_BROWSER_DISPLAY_CURRENT:
        idx = int(pref)
        for item in displays:
            if int(item.get("index") or 0) == idx:
                return item, "explicit_index"
    if pointer:
        px, py = pointer
        for item in displays:
            if _point_in_cocoa_rect(
                px,
                py,
                float(item.get("cocoa_frame_x") or 0),
                float(item.get("cocoa_frame_y") or 0),
                float(item.get("logical_width") or 0),
                float(item.get("logical_height") or 0),
            ):
                return item, "current_pointer"
    for item in displays:
        if item.get("is_main"):
            return item, "current_main"
    return displays[0], "current_first"


def geometry_tuple(display, camoufox_os):
    return (
        camoufox_os,
        int(display["logical_width"]),
        int(display["logical_height"]),
        int(display["window_width"]),
        int(display["window_height"]),
        int(display.get("window_x") or 0),
        int(display.get("window_y") or 0),
        int(display.get("index") or 1),
        str(display.get("label") or ""),
        int(display.get("cocoa_work_x") or 0),
        int(display.get("cocoa_work_y") or 0),
    )


def fallback_geometry(camoufox_os):
    logical = validate_display_size(FALLBACK_WIDTH, FALLBACK_HEIGHT) or (${GENERIC_FALLBACK_WIDTH}, ${GENERIC_FALLBACK_HEIGHT})
    display = {
        "index": 1,
        "label": "Fallback (%sx%s)" % logical,
        "is_main": True,
        "logical_width": logical[0],
        "logical_height": logical[1],
        "window_width": logical[0],
        "window_height": max(MIN_HEIGHT, logical[1] - MACOS_MENU_BAR_PX),
        "window_x": 0,
        "window_y": MACOS_MENU_BAR_PX,
        "cocoa_frame_x": 0,
        "cocoa_frame_y": 0,
        "cocoa_work_x": 0,
        "cocoa_work_y": 0,
        "source": "SaaS fallback",
    }
    return geometry_tuple(display, camoufox_os)


def store_display_inventory(displays, pointer):
    import json

    os.environ["FUNDELITE_DISPLAYS_JSON"] = json.dumps(displays)
    if pointer:
        os.environ["FUNDELITE_POINTER_X"] = str(pointer[0])
        os.environ["FUNDELITE_POINTER_Y"] = str(pointer[1])
    os.environ["FUNDELITE_DISPLAY_COUNT"] = str(len(displays))


def load_display_inventory():
    import json

    raw = os.environ.get("FUNDELITE_DISPLAYS_JSON") or ""
    if not raw:
        return [], None
    try:
        displays = json.loads(raw)
    except Exception:
        return [], None
    if not isinstance(displays, list):
        return [], None
    pointer = None
    try:
        pointer = (float(os.environ.get("FUNDELITE_POINTER_X")), float(os.environ.get("FUNDELITE_POINTER_Y")))
    except Exception:
        pointer = None
    return displays, pointer


def apply_selected_display_env(display, camoufox_os, reason):
    os.environ["FUNDELITE_CAMOUFOX_OS"] = str(camoufox_os)
    os.environ["FUNDELITE_LOGICAL_WIDTH"] = str(display["logical_width"])
    os.environ["FUNDELITE_LOGICAL_HEIGHT"] = str(display["logical_height"])
    os.environ["FUNDELITE_WINDOW_WIDTH"] = str(display["window_width"])
    os.environ["FUNDELITE_WINDOW_HEIGHT"] = str(display["window_height"])
    os.environ["FUNDELITE_WINDOW_X"] = str(display.get("window_x") or 0)
    os.environ["FUNDELITE_WINDOW_Y"] = str(display.get("window_y") or 0)
    os.environ["FUNDELITE_DISPLAY_INDEX"] = str(display.get("index") or 1)
    os.environ["FUNDELITE_DISPLAY_LABEL"] = str(display.get("label") or "")
    os.environ["FUNDELITE_DISPLAY_REASON"] = str(reason)
    os.environ["FUNDELITE_COCOA_WORK_X"] = str(display.get("cocoa_work_x") or 0)
    os.environ["FUNDELITE_COCOA_WORK_Y"] = str(display.get("cocoa_work_y") or 0)
    print(
        "QA browser display: %s reason=%s logical=%sx%s work_area=%sx%s origin=%s,%s"
        % (
            display.get("label"),
            reason,
            display["logical_width"],
            display["logical_height"],
            display["window_width"],
            display["window_height"],
            display.get("window_x") or 0,
            display.get("window_y") or 0,
        )
    )


def select_display_geometry(preference=None, camoufox_os=None):
    system = platform.system()
    if camoufox_os not in {"macos", "windows", "linux"}:
        camoufox_os = {"Darwin": "macos", "Windows": "windows", "Linux": "linux"}.get(system, FALLBACK_OS)
    displays, pointer = load_display_inventory()
    if not displays:
        displays, pointer = list_attached_displays()
        if displays:
            store_display_inventory(displays, pointer)
    if not displays:
        print("Detected display size is unavailable. Using fallback sizing from the SaaS UI.")
        return fallback_geometry(camoufox_os)
    chosen, reason = pick_display(displays, preference or os.environ.get("FUNDELITE_QA_DISPLAY"), pointer)
    if not chosen:
        return fallback_geometry(camoufox_os)
    apply_selected_display_env(chosen, camoufox_os, reason)
    return geometry_tuple(chosen, camoufox_os)


def get_device_settings():
    """Shared adaptive geometry for proxy and non-proxy launches.

    Returns camoufox_os, logical WxH, outer work-area WxH (and env for placement).
    """
    env_os = (os.environ.get("FUNDELITE_CAMOUFOX_OS") or "").strip().lower()
    env_logical = (
        os.environ.get("FUNDELITE_LOGICAL_WIDTH"),
        os.environ.get("FUNDELITE_LOGICAL_HEIGHT"),
    )
    env_window = (
        os.environ.get("FUNDELITE_WINDOW_WIDTH"),
        os.environ.get("FUNDELITE_WINDOW_HEIGHT"),
    )
    if env_os in {"macos", "windows", "linux"}:
        try:
            logical = validate_display_size(env_logical[0], env_logical[1])
            window = validate_display_size(env_window[0], env_window[1])
            if logical and window and os.environ.get("FUNDELITE_DISPLAYS_JSON"):
                print("Using supervisor-provided display geometry (worker skips display APIs).")
                return env_os, logical[0], logical[1], window[0], window[1]
        except Exception:
            pass
    selected = select_display_geometry()
    return selected[0], selected[1], selected[2], selected[3], selected[4]


def place_browser_on_selected_display(page, window_x, window_y, window_width, window_height):
    """Move/resize the real OS window onto the selected monitor work area."""
    try:
        page.evaluate(
            """([x, y, w, h]) => {
                try { window.moveTo(x, y); } catch (e) {}
                try { window.resizeTo(w, h); } catch (e) {}
            }""",
            [int(window_x or 0), int(window_y or 0), int(window_width), int(window_height)],
        )
    except Exception as exc:
        print("Could not move browser window via page APIs (%s)." % (exc,))
    if platform.system() != "Darwin":
        return
    try:
        script = (
            'tell application "System Events"\\n'
            "  set matched to {}\\n"
            '  repeat with procName in {"Camoufox", "Firefox", "Nightly"}\\n'
            "    try\\n"
            "      if exists process procName then set end of matched to procName\\n"
            "    end try\\n"
            "  end repeat\\n"
            "  if (count of matched) is 0 then return\\n"
            "  tell process (item 1 of matched)\\n"
            "    set frontmost to true\\n"
            "    try\\n"
            "      set position of window 1 to {%s, %s}\\n"
            "      set size of window 1 to {%s, %s}\\n"
            "    end try\\n"
            "  end tell\\n"
            "end tell"
            % (int(window_x or 0), int(window_y or 0), int(window_width), int(window_height))
        )
        subprocess.run(["osascript", "-e", script], check=False, capture_output=True, timeout=4)
        print(
            "macos_window_placed work_area=%sx%s css_origin=%s,%s"
            % (window_width, window_height, window_x, window_y),
            flush=True,
        )
    except Exception as exc:
        print("Could not place browser window with System Events (%s)." % (exc,))
`;
}

function buildAuthorizedQaProxyPythonBlock({ launchConfigUrl } = {}) {
  const redeemUrl = escapePythonString(
    launchConfigUrl
      || 'https://api.fundelite.ai/api/funding-hq/authorized-qa-proxy/launch-config',
  );
  return `
AUTHORIZED_QA_PROXY_ENABLED = True
AUTHORIZED_QA_PROXY_LAUNCH_CONFIG_URL = "${redeemUrl}"
# Camoufox proxy= applies to the entire browser context. There is no supported
# per-host proxy switch and geoip is also context-wide. Supported mode for this
# launcher is therefore browser_context_global (not selective DIRECT/PROXY).
AUTHORIZED_QA_PROXY_ROUTING_MODE = "browser_context_global"
QA_PROXY_STARTUP_NAVIGATION_TIMEOUT_MS = 10000
QA_PROXY_BLOCKED_PROBE_TIMEOUT_SECONDS = 2
QA_PROXY_UPSTREAM_CONNECT_TIMEOUT_SECONDS = 8


def _parse_upstream_proxy(proxy):
    from urllib.parse import unquote, urlparse as _urlparse

    proxy = proxy or {}
    server = str(proxy.get("server") or "").strip()
    parts = _urlparse(server) if server else _urlparse("")
    username = proxy.get("username")
    password = proxy.get("password")
    if username is None and parts.username:
        username = unquote(parts.username)
    if password is None and parts.password:
        password = unquote(parts.password)
    return {
        "scheme": (parts.scheme or "http").lower() or "http",
        "host": parts.hostname,
        "port": int(parts.port or 8080),
        "username": username or "",
        "password": password or "",
    }


class QaUpstreamConnectError(OSError):
    """Upstream refused CONNECT. Carries only its status code and reason token."""

    def __init__(self, status_code, reason):
        self.status_code = status_code
        self.reason = reason
        OSError.__init__(
            self, "Upstream CONNECT failed: %s %s" % (status_code or "no_response", reason or "")
        )


def _upstream_connect_error(status_line):
    import re as _re

    parts = str(status_line or "").split(" ", 2)
    code = int(parts[1]) if len(parts) > 1 and parts[1].isdigit() else None
    reason = _re.sub(r"[^A-Za-z0-9_ .-]", "", parts[2] if len(parts) > 2 else "")[:48].strip()
    return QaUpstreamConnectError(code, reason)


# Upstream provider for this launch, from the redeemed config (labels/logs only).
QA_UPSTREAM_PROVIDER = {"id": "dataimpulse", "label": "DataImpulse"}
_QA_UPSTREAM_PROVIDER_LABELS = {"dataimpulse": "DataImpulse", "911proxy": "911Proxy"}


def set_qa_upstream_provider(provider_id):
    key = str(provider_id or "").strip().lower()
    if key in _QA_UPSTREAM_PROVIDER_LABELS:
        QA_UPSTREAM_PROVIDER["id"] = key
        QA_UPSTREAM_PROVIDER["label"] = _QA_UPSTREAM_PROVIDER_LABELS[key]


def qa_provider_endpoint_expired_message():
    return (
        "%s session expired before the QA browser could start. Generate a new QA session."
        % (QA_UPSTREAM_PROVIDER["label"],)
    )


def is_provider_endpoint_expired_error(exc):
    text = str(exc or "").lower()
    return (
        "port expire" in text
        or "tunnel connection failed: 509" in text
        or "http error 509" in text
    )


# Classification -> status returned to the browser. Nothing here retries or rotates the
# provider session: a failed tunnel always fails closed on the launch session.
QA_UPSTREAM_FAILURE_BROWSER_STATUS = {
    "provider_destination_blocked": 403,
    "provider_endpoint_expired": 502,
    "upstream_proxy_rejected": 502,
    "upstream_connect_timeout": 504,
    "upstream_peer_unavailable": 502,
    "destination_connect_failed": 502,
    "gateway_tunnel_failed": 502,
}
_QA_UPSTREAM_FAILURES = {}


def classify_upstream_connect_failure(exc):
    import socket as _socket

    if isinstance(exc, QaUpstreamConnectError):
        if exc.status_code == 509 or "expire" in str(exc.reason or "").lower():
            return "provider_endpoint_expired"
        if exc.status_code == 403:
            return "provider_destination_blocked"
        if exc.status_code == 407:
            return "upstream_proxy_rejected"
        if exc.status_code is None:
            return "upstream_peer_unavailable"
        return "destination_connect_failed"
    if isinstance(exc, (_socket.timeout, TimeoutError)):
        return "upstream_connect_timeout"
    if isinstance(exc, OSError):
        return "upstream_peer_unavailable"
    return "gateway_tunnel_failed"


def record_upstream_failure(host, classification):
    if len(_QA_UPSTREAM_FAILURES) > 256:
        _QA_UPSTREAM_FAILURES.clear()
    _QA_UPSTREAM_FAILURES[host] = classification


# 911Proxy drops an endpoint after a short idle period regardless of the requested lease,
# so a 911Proxy session sends one tiny request through its own endpoint on an interval.
# Never a second fetch, never another provider, never a direct connection.
QA_UPSTREAM_KEEPALIVE_PROVIDERS = {"911proxy"}
QA_UPSTREAM_KEEPALIVE_DEFAULT_SECONDS = 30
QA_UPSTREAM_KEEPALIVE_MIN_SECONDS = 10
QA_UPSTREAM_KEEPALIVE_MAX_SECONDS = 50
QA_UPSTREAM_KEEPALIVE_HOST = "api.ipify.org"
QA_UPSTREAM_KEEPALIVE_TIMEOUT_SECONDS = 8
QA_UPSTREAM_KEEPALIVE_DEGRADED_AFTER = 3
# healthy <-> degraded (transient, same endpoint keeps being checked); expired is terminal
# and only set on explicit provider evidence (509 / "port expire").
QA_UPSTREAM_SESSION = {"expired": False, "degraded": False}
# Local QA session monitor state. QA_SESSION_TTL is set by the health monitor on TTL expiry;
# QA_SESSION_MONITOR holds the status server/health-monitor handles (QA launches only).
QA_SESSION_TTL = {"expired": False}
QA_SESSION_MONITOR = {"server": None, "health": None, "status_store": None, "deadline": None, "stop_event": None, "monitor_config": None}
_QA_UPSTREAM_KEEPALIVE_TLS = {}


def _qa_upstream_keepalive_tls_context():
    if "context" not in _QA_UPSTREAM_KEEPALIVE_TLS:
        _QA_UPSTREAM_KEEPALIVE_TLS["context"] = build_verified_tls_context()
    return _QA_UPSTREAM_KEEPALIVE_TLS["context"]


def qa_upstream_keepalive_interval(provider_id, configured_seconds=None):
    """Keepalive interval for this launch's provider, or None when it gets no keepalive."""
    if str(provider_id or "").strip().lower() not in QA_UPSTREAM_KEEPALIVE_PROVIDERS:
        return None
    try:
        seconds = int(configured_seconds)
    except (TypeError, ValueError):
        seconds = QA_UPSTREAM_KEEPALIVE_DEFAULT_SECONDS
    return max(QA_UPSTREAM_KEEPALIVE_MIN_SECONDS, min(QA_UPSTREAM_KEEPALIVE_MAX_SECONDS, seconds))


def qa_provider_session_idle_expired_message():
    return (
        "%s proxy endpoint ended before the authorized QA time elapsed. "
        "Launch a new QA session to continue."
        % (QA_UPSTREAM_PROVIDER["label"],)
    )


def print_qa_provider_endpoint_early_guidance():
    """Honest guidance when the provider ended the endpoint early (FundElite TTL not reached).

    Distinct from a FundElite session-TTL expiry: residential endpoints can end before the
    authorized window, and FundElite never silently swaps in a replacement exit under the
    operator's open third-party tabs. A fresh authorized launch is required."""
    rule = "=" * 50
    new_tab = (
        "Open a NEW Terminal tab (Command + T)."
        if sys.platform == "darwin"
        else "Open a NEW Terminal window or tab."
    )
    print("\\n".join([
        "", rule, "Proxy Endpoint Ended Early", rule, "",
        "The proxy provider ended this endpoint before your authorized QA time elapsed.",
        "",
        "FundElite did not replace the exit IP because doing so could change the",
        "connection under pages that are already open (e.g. bank or lender sites).",
        "",
        "1. Return to FundElite.",
        '2. Click "Start Fresh QA Session".',
        "3. %s" % (new_tab,),
        "4. Paste the new launch command.",
        "5. Press Enter.",
        "",
        "Your saved QA Browser profile is preserved.",
        "Do not reuse the previous launch command.",
        "",
        rule,
    ]), flush=True)


def mark_qa_upstream_session_expired(source):
    """Terminal for this launch: the gateway stops using the endpoint; nothing replaces it.

    This is a provider-endpoint expiry (early or at the provider's endpoint lifetime), kept
    distinct from a FundElite session-TTL expiry (which the health monitor reports as
    qa_proxy_session_ttl_expired)."""
    if QA_UPSTREAM_SESSION["expired"]:
        return
    QA_UPSTREAM_SESSION["expired"] = True
    set_qa_runtime_state("expired")
    print(
        "qa_upstream_session status=expired provider=%s classification=provider_endpoint_expired "
        "code=911_ENDPOINT_EXPIRED source=%s" % (QA_UPSTREAM_PROVIDER["id"], source),
        flush=True,
    )
    print(qa_provider_session_idle_expired_message(), flush=True)
    print_qa_provider_endpoint_early_guidance()
    report_qa_ip_usage("expired")


def qa_upstream_keepalive_request(upstream, timeout_seconds=None):
    """One HTTPS GET to the exit-IP check through the launch upstream.

    Returns (classification, exit_ip_or_None, details). Classification is "ok" or a failure:
    before the tunnel is up the usual upstream classifications (stage tcp/connect); after
    the endpoint accepted CONNECT, tunnel_no_response (no TLS reply), tunnel_response_timeout
    or tunnel_reset. details holds the stage and timings only — never the endpoint or body.
    """
    import ipaddress as _ipaddress

    timeout_seconds = timeout_seconds or QA_UPSTREAM_KEEPALIVE_TIMEOUT_SECONDS
    details = {}
    try:
        sock = _open_upstream_connect_tunnel(
            QA_UPSTREAM_KEEPALIVE_HOST, 443, upstream, timeout_seconds, timings=details
        )
    except Exception as exc:
        return classify_upstream_connect_failure(exc), None, details
    try:
        details["stage"] = "tls"
        tls = _qa_upstream_keepalive_tls_context().wrap_socket(sock, server_hostname=QA_UPSTREAM_KEEPALIVE_HOST)
        tls.settimeout(timeout_seconds)
        details["stage"] = "response"
        tls.sendall((
            "GET / HTTP/1.1\\r\\nHost: %s\\r\\nAccept: text/plain\\r\\nConnection: close\\r\\n\\r\\n"
            % (QA_UPSTREAM_KEEPALIVE_HOST,)
        ).encode("ascii"))
        data = b""
        while len(data) < 4096:
            chunk = tls.recv(4096)
            if not chunk:
                break
            if not data:
                details["first_byte_ms"] = _elapsed_ms(details["start"])
            data += chunk
        details["stage"] = "done"
        head, _, body = data.partition(b"\\r\\n\\r\\n")
        status = head.split(b"\\r\\n", 1)[0].decode("latin1", "replace").split(" ")
        if len(status) < 2 or status[1] != "200":
            return "keepalive_http_error", None, details
        text = body.decode("utf-8", "replace").strip()
        try:
            _ipaddress.ip_address(text)
        except ValueError:
            text = None
        return "ok", text, details
    except Exception as exc:
        timed_out = isinstance(exc, TimeoutError) or "timed out" in str(exc).lower()
        if timed_out:
            if details.get("stage") == "tls":
                return "tunnel_no_response", None, details
            return "tunnel_response_timeout", None, details
        return "tunnel_reset", None, details
    finally:
        try:
            sock.close()
        except Exception:
            pass


class QaUpstreamKeepalive(object):
    """Background keepalive bound to exactly one launch upstream.

    One request per interval and no in-between retries. A provider 509 is terminal: the
    session is marked expired and the thread exits. Other failures are transient: after
    QA_UPSTREAM_KEEPALIVE_DEGRADED_AFTER in a row the session is marked degraded, the next
    scheduled check still uses the same endpoint, and one success marks it recovered.
    Degraded never refuses traffic and never switches endpoint, provider or route.
    """

    def __init__(self, upstream_proxy, interval_seconds, expected_exit_ip=None, request_fn=None):
        import threading

        self._hash_salt = os.urandom(16)
        self._upstream = _parse_upstream_proxy(upstream_proxy)
        self.interval_seconds = interval_seconds
        self._expected_exit_ip = str(expected_exit_ip or "").strip() or None
        self._request_fn = request_fn or qa_upstream_keepalive_request
        self._stop = threading.Event()
        self._thread = threading.Thread(target=self._run, name="qa-upstream-keepalive", daemon=True)
        self.requests = 0
        self.consecutive_failures = 0
        self.outcome = None

    def start(self):
        print(
            "qa_upstream_keepalive status=started provider=%s interval_seconds=%s exit_ip_hash=%s"
            % (QA_UPSTREAM_PROVIDER["id"], self.interval_seconds, self.exit_ip_hash(self._expected_exit_ip)),
            flush=True,
        )
        self._thread.start()
        return self

    def stop(self, reason="session_end"):
        self._stop.set()
        if self._thread.is_alive():
            self._thread.join(timeout=QA_UPSTREAM_KEEPALIVE_TIMEOUT_SECONDS * 2 + 2)
        if self.outcome is None:
            self.outcome = "stopped"
            print(
                "qa_upstream_keepalive status=stopped provider=%s reason=%s requests=%s"
                % (QA_UPSTREAM_PROVIDER["id"], reason, self.requests),
                flush=True,
            )

    def is_alive(self):
        return self._thread.is_alive()

    def exit_ip_hash(self, exit_ip):
        """Per-session salted hash so logs can show the exit IP is unchanged without the IP."""
        import hashlib

        if not exit_ip:
            return "none"
        return hashlib.sha256(self._hash_salt + str(exit_ip).encode("utf-8")).hexdigest()[:12]

    def _run(self):
        while not self._stop.wait(self.interval_seconds):
            if QA_UPSTREAM_SESSION["expired"]:
                self.outcome = "expired"
                return
            started = time.time()
            try:
                result = tuple(self._request_fn(self._upstream))
            except Exception:
                result = ("gateway_tunnel_failed", None)
            classification, exit_ip = result[0], result[1]
            details = result[2] if len(result) > 2 and isinstance(result[2], dict) else {}
            latency_ms = int((time.time() - started) * 1000)
            self.requests += 1
            if self._stop.is_set():
                return
            if classification == "ok":
                recovered_after = self.consecutive_failures
                self.consecutive_failures = 0
                same = "unknown"
                if exit_ip and self._expected_exit_ip:
                    same = "yes" if exit_ip == self._expected_exit_ip else "no"
                print(
                    "qa_upstream_keepalive status=ok provider=%s latency_ms=%s exit_ip_same=%s "
                    "exit_ip_hash=%s"
                    % (QA_UPSTREAM_PROVIDER["id"], latency_ms, same, self.exit_ip_hash(exit_ip)),
                    flush=True,
                )
                report_qa_ip_usage("keepalive_ok")
                if QA_UPSTREAM_SESSION["degraded"]:
                    QA_UPSTREAM_SESSION["degraded"] = False
                    print(
                        "qa_upstream_session status=recovered provider=%s failed_checks=%s"
                        % (QA_UPSTREAM_PROVIDER["id"], recovered_after),
                        flush=True,
                    )
                    report_qa_ip_usage("recovered")
                continue
            if classification == "provider_endpoint_expired":
                self.outcome = "expired"
                print(
                    "qa_upstream_keepalive status=expired provider=%s latency_ms=%s "
                    "classification=provider_endpoint_expired"
                    % (QA_UPSTREAM_PROVIDER["id"], latency_ms),
                    flush=True,
                )
                mark_qa_upstream_session_expired("keepalive")
                return
            self.consecutive_failures += 1
            print(
                "qa_upstream_keepalive status=transient_failure provider=%s latency_ms=%s "
                "classification=%s stage=%s tcp_ms=%s connect_ms=%s consecutive=%s"
                % (
                    QA_UPSTREAM_PROVIDER["id"], latency_ms, classification,
                    details.get("stage") or "-", details.get("tcp_ms", "-"),
                    details.get("connect_ms", "-"), self.consecutive_failures,
                ),
                flush=True,
            )
            if (
                self.consecutive_failures >= QA_UPSTREAM_KEEPALIVE_DEGRADED_AFTER
                and not QA_UPSTREAM_SESSION["degraded"]
            ):
                QA_UPSTREAM_SESSION["degraded"] = True
                print(
                    "qa_upstream_session status=degraded provider=%s classification=%s consecutive=%s "
                    "action=keep_same_endpoint_next_check_in_%ss"
                    % (
                        QA_UPSTREAM_PROVIDER["id"], classification, self.consecutive_failures,
                        self.interval_seconds,
                    ),
                    flush=True,
                )
                print(
                    "%s connection is unstable. The QA session keeps its current endpoint; pages may "
                    "load slowly or fail until it recovers." % (QA_UPSTREAM_PROVIDER["label"],),
                    flush=True,
                )
                report_qa_ip_usage("degraded")


QA_IP_USAGE_EVENTS_PATH = "/api/funding-hq/authorized-qa-proxy/session-events"
QA_IP_USAGE_TOUCH_SECONDS = 300
# Providers that report the accepted exit to the admin IP Usage registry. 911Proxy requires
# an exact endpoint match; DataImpulse reports its location-compatible browser exit.
QA_IP_USAGE_PROVIDERS = {"911proxy", "dataimpulse"}
QA_IP_USAGE_POST_TIMEOUT_SECONDS = 5
QA_IP_USAGE_CLOSE_WAIT_SECONDS = 3
QA_IP_USAGE_REPORTER = {"reporter": None}


def qa_ip_usage_events_url(decide_url):
    """Same FundElite API origin as the website-access decide URL; '' when unknown."""
    url = str(decide_url or "")
    idx = url.find("/api/")
    if idx <= 0:
        return ""
    return url[:idx] + QA_IP_USAGE_EVENTS_PATH


def _post_qa_ip_usage_event(url, body):
    import json
    import urllib.request

    request = urllib.request.Request(
        url,
        data=json.dumps(body).encode("utf-8"),
        headers={"Content-Type": "application/json", "Accept": "application/json"},
        method="POST",
    )
    with urllib.request.urlopen(
        request, timeout=QA_IP_USAGE_POST_TIMEOUT_SECONDS, context=build_verified_tls_context()
    ) as response:
        response.read()


class QaIpUsageReporter(object):
    """Fire-and-forget reports for the admin 911Proxy IP Usage registry.

    Posts go straight to the FundElite API on a background thread, never through the QA
    proxy. A failed report is dropped: it never blocks, retries, or changes the session's
    routing, keepalive, endpoint or provider. Keepalive successes are sent at most once per
    QA_IP_USAGE_TOUCH_SECONDS.
    """

    def __init__(self, url, token, post_fn=None):
        import queue
        import threading

        self._url = url
        self._token = token
        self._post = post_fn or _post_qa_ip_usage_event
        self._queue = queue.Queue(maxsize=32)
        self._last_touch = time.time()
        self._closed = False
        self.sent = 0
        self.failed = 0
        self._thread = threading.Thread(target=self._run, name="qa-ip-usage-report", daemon=True)
        self._thread.start()

    def emit(self, event, **fields):
        if self._closed:
            return
        body = dict(fields)
        body["token"] = self._token
        body["event"] = event
        try:
            self._queue.put_nowait(body)
        except Exception:
            pass

    def touch(self):
        now = time.time()
        if now - self._last_touch < QA_IP_USAGE_TOUCH_SECONDS:
            return
        self._last_touch = now
        self.emit("keepalive_ok")

    def close(self, wait_seconds=QA_IP_USAGE_CLOSE_WAIT_SECONDS):
        if self._closed:
            return
        self.emit("closed")
        self._closed = True
        try:
            self._queue.put(None, timeout=wait_seconds)
        except Exception:
            pass
        self._thread.join(wait_seconds)

    def _run(self):
        while True:
            body = self._queue.get()
            if body is None:
                return
            try:
                self._post(self._url, body)
                self.sent += 1
            except Exception:
                self.failed += 1
                print("qa_ip_usage_report status=failed event=%s" % (body.get("event"),), flush=True)


def report_qa_ip_usage(event):
    reporter = QA_IP_USAGE_REPORTER["reporter"]
    if reporter is None:
        return
    if event == "keepalive_ok":
        reporter.touch()
    else:
        reporter.emit(event)


def start_qa_ip_usage_reporter(proxy_launch, verification, post_fn=None):
    """Records the browser-verified accepted exit for the admin IP Usage registry.

    911Proxy requires an exact endpoint match. DataImpulse reports its accepted
    (location-compatible) browser exit even though residential IPs can rotate. The registry
    is visibility-only and never affects routing, keepalive, endpoint or provider.
    """
    if QA_UPSTREAM_PROVIDER["id"] not in QA_IP_USAGE_PROVIDERS:
        return None
    if not isinstance(verification, dict):
        return None
    observed_ip = str(verification.get("observed_ip") or "").strip()
    if not observed_ip:
        return None
    if QA_UPSTREAM_PROVIDER["id"] == "911proxy" and not verification.get("exact"):
        return None
    token = (proxy_launch or {}).get("website_access_session_token") or ""
    url = qa_ip_usage_events_url((proxy_launch or {}).get("website_access_decide_url"))
    if not token or not url:
        return None
    location = verification.get("location") if isinstance(verification.get("location"), dict) else {}
    reporter = QaIpUsageReporter(url, token, post_fn=post_fn)
    QA_IP_USAGE_REPORTER["reporter"] = reporter
    reporter.emit(
        "verified",
        exitIp=observed_ip,
        observed={
            "country": location.get("country") or None,
            "state": location.get("region") or None,
            "city": location.get("city") or None,
            "postal": location.get("postal") or None,
        },
    )
    stage_log("qa_ip_usage_report", "event=verified queued=yes")
    return reporter


def stop_qa_ip_usage_reporter():
    reporter = QA_IP_USAGE_REPORTER["reporter"]
    QA_IP_USAGE_REPORTER["reporter"] = None
    if reporter is not None:
        reporter.close()


def start_qa_upstream_keepalive(upstream_proxy, provider_id, configured_seconds=None, expected_exit_ip=None):
    """Start the keepalive for providers that need it; None for every other provider."""
    interval = qa_upstream_keepalive_interval(provider_id, configured_seconds)
    if interval is None or not upstream_proxy:
        return None
    keepalive = QaUpstreamKeepalive(upstream_proxy, interval, expected_exit_ip=expected_exit_ip)
    atexit.register(keepalive.stop, "exit")
    return keepalive.start()


def _refuse_if_session_expired(handler, host):
    """After a terminal 509 the expired endpoint is never contacted again."""
    if not QA_UPSTREAM_SESSION["expired"]:
        return False
    record_upstream_failure(host, "provider_endpoint_expired")
    print(
        "qa_tunnel host=%s upstream=%s status=refused classification=provider_endpoint_expired "
        "reason=session_expired" % (host, QA_UPSTREAM_PROVIDER["id"]),
        flush=True,
    )
    try:
        handler.send_error(
            QA_UPSTREAM_FAILURE_BROWSER_STATUS["provider_endpoint_expired"], "provider_endpoint_expired"
        )
    except Exception:
        pass
    return True


def _split_connect_authority(authority):
    """Split a CONNECT target into (host, port); bracketed IPv6 keeps every colon in the host."""
    value = str(authority or "").strip()
    if value.startswith("["):
        end = value.find("]")
        if end < 0:
            return "", ""
        rest = value[end + 1:]
        return value[1:end], rest[1:] if rest.startswith(":") else ""
    if value.count(":") == 1:
        host, _, port = value.partition(":")
        return host, port
    return value, ""


def _elapsed_ms(started):
    return int((time.time() - started) * 1000)


def _open_upstream_connect_tunnel(target_host, target_port, upstream, timeout_seconds=8, timings=None):
    """CONNECT through the launch's provider upstream. Never direct-connects.

    Proxy-Authorization is sent only for authenticated upstreams (DataImpulse); 911Proxy
    endpoints are authorized by the backend's whitelisted source IP and get no header.
    timings (optional dict) receives start/tcp_ms/connect_ms and the stage reached:
    "tcp" (reaching the endpoint) then "connect" (waiting for its CONNECT reply).
    """
    import base64
    import socket as _socket

    timings = timings if timings is not None else {}
    timings["start"] = time.time()
    timings["stage"] = "tcp"
    if not upstream or not upstream.get("host"):
        raise OSError("Upstream proxy required")
    sock = _socket.create_connection((upstream["host"], int(upstream["port"])), timeout=timeout_seconds)
    timings["tcp_ms"] = _elapsed_ms(timings["start"])
    timings["stage"] = "connect"
    sock.settimeout(timeout_seconds)
    authority = "[%s]" % target_host if ":" in str(target_host) else target_host
    request = "CONNECT %s:%s HTTP/1.1\\r\\nHost: %s:%s\\r\\n" % (
        authority, int(target_port), authority, int(target_port)
    )
    if upstream.get("username") or upstream.get("password"):
        token = base64.b64encode(
            ("%s:%s" % (upstream.get("username") or "", upstream.get("password") or "")).encode("utf-8")
        ).decode("ascii")
        request += "Proxy-Authorization: Basic %s\\r\\n" % token
    request += "Proxy-Connection: keep-alive\\r\\n\\r\\n"
    sock.sendall(request.encode("ascii"))
    header = b""
    while b"\\r\\n\\r\\n" not in header and len(header) < 65536:
        chunk = sock.recv(4096)
        if not chunk:
            break
        header += chunk
    status_line = header.split(b"\\r\\n", 1)[0].decode("latin1", "replace")
    if " 200 " not in (" " + status_line + " ") and not status_line.endswith(" 200"):
        sock.close()
        raise _upstream_connect_error(status_line)
    timings["connect_ms"] = _elapsed_ms(timings["start"])
    timings["stage"] = "established"
    return sock


QA_TUNNEL_IDLE_SECONDS = 30
QA_TUNNEL_SLOW_FIRST_BYTE_MS = 5000


def _qa_gateway_tunnel_bytes(client_sock, remote_sock, stats=None):
    """Relay a CONNECT tunnel. stats (optional dict) receives byte counts, first-byte times
    (ms since stats["start"]) and why it ended — never any payload."""
    import select

    stats = stats if stats is not None else {}
    stats.setdefault("start", time.time())
    stats.update({"bytes_up": 0, "bytes_down": 0, "first_up_ms": None, "first_down_ms": None, "end": None})
    sockets = [client_sock, remote_sock]
    try:
        while True:
            readable, _, errored = select.select(sockets, [], sockets, QA_TUNNEL_IDLE_SECONDS)
            if errored:
                stats["end"] = "socket_error"
                return stats
            if not readable:
                stats["end"] = "idle_timeout"
                return stats
            for sock in readable:
                upward = sock is client_sock
                other = remote_sock if upward else client_sock
                try:
                    data = sock.recv(65536)
                except OSError:
                    stats["end"] = "client_reset" if upward else "upstream_reset"
                    return stats
                if not data:
                    stats["end"] = "client_closed" if upward else "upstream_closed"
                    return stats
                key = "up" if upward else "down"
                if stats["first_%s_ms" % key] is None:
                    stats["first_%s_ms" % key] = _elapsed_ms(stats["start"])
                stats["bytes_%s" % key] += len(data)
                try:
                    other.sendall(data)
                except OSError:
                    stats["end"] = "upstream_reset" if upward else "client_reset"
                    return stats
    finally:
        for sock in (client_sock, remote_sock):
            try:
                sock.close()
            except Exception:
                pass


def classify_tunnel_outcome(stats):
    """Sanitized verdict for one relayed tunnel (counts and timings only)."""
    up, down = stats.get("bytes_up") or 0, stats.get("bytes_down") or 0
    end = stats.get("end") or "unknown"
    if down == 0:
        return "no_response_bytes" if up else "unused"
    if end in ("upstream_reset", "socket_error"):
        return "reset_after_traffic"
    first_up, first_down = stats.get("first_up_ms"), stats.get("first_down_ms")
    if first_up is not None and first_down is not None and first_down - first_up >= QA_TUNNEL_SLOW_FIRST_BYTE_MS:
        return "slow_first_byte"
    return "ok"


def format_qa_tunnel_summary(host, port, stats):
    def _ms(value):
        return "-" if value is None else value

    return (
        "qa_tunnel_summary host=%s port=%s upstream=%s tcp_ms=%s connect_ms=%s first_up_ms=%s "
        "first_down_ms=%s duration_ms=%s bytes_up=%s bytes_down=%s end=%s outcome=%s"
        % (
            host, port, QA_UPSTREAM_PROVIDER["id"], _ms(stats.get("tcp_ms")),
            _ms(stats.get("connect_ms")), _ms(stats.get("first_up_ms")),
            _ms(stats.get("first_down_ms")), _elapsed_ms(stats["start"]),
            stats.get("bytes_up") or 0, stats.get("bytes_down") or 0,
            stats.get("end") or "unknown", classify_tunnel_outcome(stats),
        )
    )


def start_qa_allowlist_gateway(upstream_proxy, approved_hosts, website_access_decision_fn=None, web_access_mode="restricted_allowlist"):
    """Local HTTP/HTTPS proxy gateway for Camoufox/Firefox.

    Supports:
      - HTTPS CONNECT tunneling
      - ordinary absolute-form HTTP proxy requests (GET/POST/...)

    Every permitted request exits through the launch's provider upstream.
    There is no direct-network fallback.

    restricted_allowlist: required/catalog hosts go upstream; others fail immediately.
    full_web: public http(s) hosts go upstream; private/metadata/permanent unsafe hosts fail.

    CRITICAL: after CONNECT, close_connection must be True so BaseHTTPRequestHandler
    does not try to parse TLS ClientHello bytes as a second HTTP request (which
    produces Bad HTTP/0.9 / Bad request version / BrokenPipeError).
    """
    from http.server import BaseHTTPRequestHandler, ThreadingHTTPServer
    from urllib.parse import urlsplit

    approved = [str(item) for item in (approved_hosts or []) if item]
    upstream = _parse_upstream_proxy(upstream_proxy)
    mode = str(web_access_mode or "restricted_allowlist").strip().lower()
    full_web = mode in {"full_web", "full_web_proxied", "full-web"}
    _HOP_BY_HOP = {
        "connection", "keep-alive", "proxy-authenticate", "proxy-authorization",
        "te", "trailers", "transfer-encoding", "upgrade", "proxy-connection",
    }

    def _permitted(host):
        if is_private_or_unsafe_network_host(host):
            return False
        if full_web:
            return True
        if is_hostname_allowlisted(host, approved):
            return True
        if is_permanently_blocked_qa_host(host):
            return False
        if website_access_decision_fn is None:
            return False
        try:
            return website_access_decision_fn(host) == "allow"
        except Exception:
            return False

    def _deny(handler, host):
        print(
            "qa_allowlist_gateway hostname=%s decision=BLOCKED fail_fast=1 mode=%s" % (
                host or "(unknown)",
                "full_web" if full_web else "restricted",
            ),
            flush=True,
        )
        body = qa_website_block_page_text(host).encode("utf-8")
        handler.send_response(403, "Forbidden")
        handler.send_header("Content-Type", "text/plain; charset=utf-8")
        handler.send_header("Content-Length", str(len(body)))
        handler.send_header("Connection", "close")
        handler.end_headers()
        try:
            handler.wfile.write(body)
        except Exception:
            pass

    class _Handler(BaseHTTPRequestHandler):
        protocol_version = "HTTP/1.1"

        def log_message(self, fmt, *args):
            return

        def log_error(self, fmt, *args):
            try:
                print("qa_gateway_parse_error %s" % (fmt % args,), flush=True)
            except Exception:
                try:
                    print("qa_gateway_parse_error %s" % (fmt,), flush=True)
                except Exception:
                    pass

        def do_CONNECT(self):
            # Never parse post-CONNECT TLS bytes as another HTTP request.
            self.close_connection = True
            host, port = _split_connect_authority(self.path)
            host = _normalize_hostname(host)
            try:
                port_i = int(port or "443")
            except ValueError:
                port_i = 443
            if not _permitted(host):
                _deny(self, host)
                return
            print(
                "qa_request host=%s type=connect decision=PROXY mode=%s" % (
                    host,
                    "full_web" if full_web else "restricted",
                ),
                flush=True,
            )
            if _refuse_if_session_expired(self, host):
                return
            tunnel = {}
            try:
                remote = _open_upstream_connect_tunnel(
                    host, port_i, upstream, QA_PROXY_UPSTREAM_CONNECT_TIMEOUT_SECONDS, timings=tunnel
                )
            except Exception as exc:
                classification = classify_upstream_connect_failure(exc)
                record_upstream_failure(host, classification)
                print(
                    "qa_tunnel host=%s port=%s upstream=%s status=failed upstream_status=%s "
                    "classification=%s stage=%s elapsed_ms=%s error=%s"
                    % (
                        host, port_i, QA_UPSTREAM_PROVIDER["id"],
                        getattr(exc, "status_code", None) or "-", classification,
                        tunnel.get("stage") or "-", _elapsed_ms(tunnel.get("start") or time.time()),
                        sanitize_launcher_error_text(exc)[:160],
                    ),
                    flush=True,
                )
                try:
                    self.send_error(
                        QA_UPSTREAM_FAILURE_BROWSER_STATUS.get(classification, 502), classification
                    )
                except Exception:
                    pass
                return
            _QA_UPSTREAM_FAILURES.pop(host, None)
            try:
                # Raw socket write — do not use wfile/end_headers after CONNECT.
                # BaseHTTPRequestHandler keep-alive would otherwise read the TLS
                # ClientHello as a second HTTP request (Bad HTTP/0.9).
                self.connection.sendall(
                    b"HTTP/1.1 200 Connection Established\\r\\n"
                    b"Connection: close\\r\\n"
                    b"\\r\\n"
                )
                print(
                    "qa_tunnel host=%s port=%s upstream=%s status=connected"
                    % (host, port_i, QA_UPSTREAM_PROVIDER["id"]),
                    flush=True,
                )
                _qa_gateway_tunnel_bytes(self.connection, remote, tunnel)
                print(format_qa_tunnel_summary(host, port_i, tunnel), flush=True)
            except BrokenPipeError:
                pass
            except Exception as exc:
                print(
                    "qa_tunnel host=%s port=%s upstream=%s status=relay_fail error=%s"
                    % (host, port_i, QA_UPSTREAM_PROVIDER["id"], sanitize_launcher_error_text(exc)[:160]),
                    flush=True,
                )
                try:
                    remote.close()
                except Exception:
                    pass

        def _proxy_http(self):
            """Handle absolute-form HTTP proxy requests (Firefox ordinary HTTP)."""
            self.close_connection = True
            raw_path = self.path or ""
            method = (self.command or "GET").upper()
            if raw_path.startswith("http://") or raw_path.startswith("https://"):
                parts = urlsplit(raw_path)
                host = _normalize_hostname(parts.hostname or "")
                scheme = (parts.scheme or "http").lower()
                try:
                    port_i = int(parts.port or (443 if scheme == "https" else 80))
                except ValueError:
                    port_i = 443 if scheme == "https" else 80
                origin_path = parts.path or "/"
                if parts.query:
                    origin_path = "%s?%s" % (origin_path, parts.query)
            else:
                host_header = self.headers.get("Host") or ""
                host = _normalize_hostname(host_header)
                port_i = 80
                if ":" in str(host_header) and not str(host_header).startswith("["):
                    _h, _, _p = str(host_header).partition(":")
                    try:
                        port_i = int(_p or "80")
                    except ValueError:
                        port_i = 80
                origin_path = raw_path or "/"
                scheme = "http"

            if scheme == "https":
                try:
                    self.send_error(400, "HTTPS requires CONNECT")
                except Exception:
                    pass
                return
            if not _permitted(host):
                _deny(self, host)
                return
            if not upstream or not upstream.get("host"):
                try:
                    self.send_error(502, "Upstream proxy required")
                except Exception:
                    pass
                return
            print(
                "qa_allowlist_gateway hostname=%s decision=PROXY method=%s mode=%s" % (
                    host,
                    method,
                    "full_web" if full_web else "restricted",
                ),
                flush=True,
            )
            if _refuse_if_session_expired(self, host):
                return
            try:
                remote = _open_upstream_connect_tunnel(
                    host, port_i, upstream, QA_PROXY_UPSTREAM_CONNECT_TIMEOUT_SECONDS
                )
            except Exception as exc:
                classification = classify_upstream_connect_failure(exc)
                record_upstream_failure(host, classification)
                print(
                    "qa_allowlist_gateway hostname=%s decision=UPSTREAM_FAIL upstream_status=%s "
                    "classification=%s error=%s"
                    % (
                        host, getattr(exc, "status_code", None) or "-", classification,
                        sanitize_launcher_error_text(exc)[:160],
                    ),
                    flush=True,
                )
                try:
                    self.send_error(
                        QA_UPSTREAM_FAILURE_BROWSER_STATUS.get(classification, 502), classification
                    )
                except Exception:
                    pass
                return
            _QA_UPSTREAM_FAILURES.pop(host, None)

            lines = ["%s %s HTTP/1.1" % (method, origin_path)]
            has_host = False
            for key in self.headers.keys():
                lk = str(key).lower()
                if lk in _HOP_BY_HOP:
                    continue
                if lk == "host":
                    has_host = True
                lines.append("%s: %s" % (key, self.headers.get(key)))
            if not has_host:
                if port_i in (80, 443):
                    lines.append("Host: %s" % host)
                else:
                    lines.append("Host: %s:%s" % (host, port_i))
            lines.append("Connection: close")
            lines.append("")
            header_blob = ("\\r\\n".join(lines) + "\\r\\n").encode("latin1", "replace")
            body = b""
            try:
                length = int(self.headers.get("Content-Length") or 0)
            except ValueError:
                length = 0
            if length > 0:
                body = self.rfile.read(length)
            try:
                remote.sendall(header_blob + body)
                while True:
                    chunk = remote.recv(65536)
                    if not chunk:
                        break
                    self.connection.sendall(chunk)
            except BrokenPipeError:
                pass
            except Exception as exc:
                print(
                    "qa_allowlist_gateway hostname=%s decision=RELAY_FAIL error=%s"
                    % (host, sanitize_launcher_error_text(exc)[:160]),
                    flush=True,
                )
            finally:
                try:
                    remote.close()
                except Exception:
                    pass

        def do_GET(self):
            self._proxy_http()

        def do_POST(self):
            self._proxy_http()

        def do_HEAD(self):
            self._proxy_http()

        def do_PUT(self):
            self._proxy_http()

        def do_DELETE(self):
            self._proxy_http()

        def do_PATCH(self):
            self._proxy_http()

        def do_OPTIONS(self):
            self._proxy_http()

    httpd = ThreadingHTTPServer(("127.0.0.1", 0), _Handler)
    thread = threading.Thread(target=httpd.serve_forever, daemon=True)
    thread.start()
    host, port = httpd.server_address[:2]
    print("qa_allowlist_gateway listening=127.0.0.1:%s fail_fast_blocked=1" % (port,), flush=True)

    def _stop():
        try:
            httpd.shutdown()
        except Exception:
            pass
        try:
            httpd.server_close()
        except Exception:
            pass

    atexit.register(_stop)
    return {"host": host, "port": int(port), "server": "http://%s:%s" % (host, port), "stop": _stop}


def probe_gateway_connect(gateway_port, hostname, timeout_seconds=2):
    import socket as _socket

    t0 = time.time()
    sock = _socket.create_connection(("127.0.0.1", int(gateway_port)), timeout=timeout_seconds)
    try:
        sock.settimeout(timeout_seconds)
        sock.sendall(
            ("CONNECT %s:443 HTTP/1.1\\r\\nHost: %s:443\\r\\nConnection: close\\r\\n\\r\\n" % (hostname, hostname)).encode("ascii")
        )
        payload = b""
        while b"\\r\\n\\r\\n" not in payload and len(payload) < 4096:
            chunk = sock.recv(1024)
            if not chunk:
                break
            payload += chunk
    finally:
        try:
            sock.close()
        except Exception:
            pass
    elapsed_ms = int((time.time() - t0) * 1000)
    status_line = payload.split(b"\\r\\n", 1)[0].decode("latin1", "replace")
    blocked = status_line.startswith("HTTP/") and " 403" in status_line
    established = status_line.startswith("HTTP/") and " 200" in status_line
    return status_line, elapsed_ms, blocked, established


def print_google_block_probe(gateway_port):
    try:
        status_line, elapsed_ms, blocked, _established = probe_gateway_connect(
            gateway_port, "www.google.com", QA_PROXY_BLOCKED_PROBE_TIMEOUT_SECONDS
        )
    except Exception as exc:
        print(
            "google_block_probe result=ERROR elapsed_ms=n/a error=%s"
            % (sanitize_launcher_error_text(exc),),
            flush=True,
        )
        return
    print(
        "google_block_probe hostname=www.google.com result=%s elapsed_ms=%s status=%s"
        % ("BLOCKED" if blocked else "UNEXPECTED", elapsed_ms, status_line[:80]),
        flush=True,
    )
    if not blocked or elapsed_ms > 2500:
        print("WARNING: Google block did not fail fast.", flush=True)


QA_BROWSER_CANCELLATION_ERRORS = ("NS_BINDING_ABORTED", "NS_ERROR_ABORT")


def attach_qa_network_diagnostics(page, approved_hosts, web_access_mode="restricted_allowlist"):
    def on_console(msg):
        try:
            print(
                "qa_console type=%s text=%s"
                % (getattr(msg, "type", "?"), sanitize_launcher_error_text(getattr(msg, "text", ""))[:180]),
                flush=True,
            )
        except Exception:
            pass

    def document_frame_label(request):
        try:
            return "main" if request.frame == page.main_frame else "sub"
        except Exception:
            return "unknown"

    def on_request(request):
        host = _normalize_hostname(getattr(request, "url", "") or "")
        if not host:
            return
        decision = session_route_decision(host, approved_hosts, True, web_access_mode=web_access_mode)
        resource_type = getattr(request, "resource_type", "unknown")
        if resource_type == "document":
            redirected_from = None
            try:
                redirected_from = request.redirected_from
            except Exception:
                pass
            print(
                "qa_request host=%s type=document decision=%s frame=%s redirect_from=%s"
                % (
                    host,
                    decision,
                    document_frame_label(request),
                    _normalize_hostname(getattr(redirected_from, "url", "") or "") if redirected_from else "-",
                ),
                flush=True,
            )
            return
        print(
            "qa_request host=%s type=%s decision=%s" % (host, resource_type, decision),
            flush=True,
        )

    def on_response(response):
        """Document responses only: status and redirect target host — never query strings or cookies."""
        try:
            request = response.request
            if getattr(request, "resource_type", None) != "document":
                return
            location = ""
            try:
                location = response.headers.get("location") or ""
            except Exception:
                pass
            print(
                "qa_document_response host=%s status=%s location_host=%s frame=%s"
                % (
                    _normalize_hostname(getattr(request, "url", "") or "") or "(unknown)",
                    getattr(response, "status", "?"),
                    (_normalize_hostname(location) or "(same-host)") if location else "-",
                    document_frame_label(request),
                ),
                flush=True,
            )
        except Exception:
            pass

    def on_requestfinished(request):
        if getattr(request, "resource_type", None) != "document":
            return
        print(
            "qa_document_done host=%s frame=%s"
            % (_normalize_hostname(getattr(request, "url", "") or "") or "(unknown)", document_frame_label(request)),
            flush=True,
        )

    def on_framenavigated(frame):
        try:
            if frame != page.main_frame:
                return
            print(
                "qa_navigation host=%s scheme=%s"
                % (
                    _normalize_hostname(getattr(frame, "url", "") or "") or "(none)",
                    str(getattr(frame, "url", "") or "").split(":", 1)[0] or "(none)",
                ),
                flush=True,
            )
        except Exception:
            pass

    def on_requestfailed(request):
        host = _normalize_hostname(getattr(request, "url", "") or "")
        failure = ""
        try:
            failure = request.failure
        except Exception:
            pass
        resource_type = getattr(request, "resource_type", None)
        failure_text = sanitize_launcher_error_text(failure)[:160]
        if resource_type == "document" and any(code in failure_text for code in QA_BROWSER_CANCELLATION_ERRORS):
            # The browser, a newer navigation, a download or an add-on cancelled this load.
            # That is not a proxy failure, so the page Firefox continues with must not be replaced.
            print(
                "qa_request_cancelled host=%s type=document frame=%s error=%s"
                % (host or "(unknown)", document_frame_label(request), failure_text),
                flush=True,
            )
            return
        print(
            "qa_request_failed host=%s type=%s error=%s"
            % (
                host or "(unknown)",
                resource_type or "unknown",
                failure_text,
            ),
            flush=True,
        )
        # Subresource failures (PostHog, fonts, XHR, images, …) must NEVER replace
        # the top-level document. Only failed top-level document navigations may.
        if resource_type != "document":
            return
        try:
            if not request.is_navigation_request():
                return
        except Exception:
            pass
        # iframe navigations are documents too; only the top-level page may be replaced.
        if document_frame_label(request) != "main":
            return
        if not host:
            return
        # Policy BLOCKED → allowlist block page.
        # Permitted PROXY failure → truthful proxy/network error (do not leave FundElite DOM).
        decision = session_route_decision(
            host, approved_hosts, True, web_access_mode=web_access_mode
        )
        try:
            if decision == "BLOCKED":
                page.set_content(qa_website_block_page_html(host))
            elif decision == "PROXY":
                page.set_content(qa_proxy_connection_error_html(
                    host, failure, classification=_QA_UPSTREAM_FAILURES.get(host),
                ))
        except Exception:
            pass

    page.on("console", on_console)
    page.on("request", on_request)
    page.on("response", on_response)
    page.on("requestfinished", on_requestfinished)
    page.on("requestfailed", on_requestfailed)
    page.on("framenavigated", on_framenavigated)
    page.on("close", lambda _page: print("qa_page_closed", flush=True))


def attach_qa_network_diagnostics_to_context(context, approved_hosts, web_access_mode="restricted_allowlist"):
    """Instrument every page — address-bar / new-tab navigations must not be silent."""

    def on_page(page):
        try:
            attach_qa_network_diagnostics(page, approved_hosts, web_access_mode=web_access_mode)
        except Exception:
            pass
        try:
            print(
                "qa_page_opened host=%s"
                % (_normalize_hostname(getattr(page, "url", "") or "") or "(blank)",),
                flush=True,
            )
        except Exception:
            pass

    try:
        context.on("page", on_page)
    except Exception:
        pass


def log_qa_pages(browser_or_context, label="qa_pages"):
    """Hostname-only snapshot of open pages (no query strings / secrets)."""
    try:
        pages = list(browser_or_context.pages) if getattr(browser_or_context, "pages", None) else []
    except Exception:
        pages = []
    hosts = []
    for page in pages:
        try:
            hosts.append(_normalize_hostname(getattr(page, "url", "") or "") or "(blank)")
        except Exception:
            hosts.append("(error)")
    print(
        "%s count=%s urls=[%s]" % (label, len(hosts), ",".join(hosts)),
        flush=True,
    )


QA_ALL_WINDOWS_CLOSED_GRACE_SECONDS = 3.0
QA_OPERATOR_INPUT_IGNORED_NOTICE = (
    "Terminal input is ignored while the QA Browser is open. To end this session, close "
    "the browser window or press Ctrl+C. To start another QA session, open a NEW Terminal tab."
)


def print_qa_relaunch_guidance(title, reason_lines, next_heading):
    """Recovery steps after a session can no longer be used; never prints tokens or credentials."""
    rule = "=" * 50
    new_tab = (
        "Open a NEW Terminal tab with Command + T."
        if sys.platform == "darwin"
        else "Open a NEW Terminal window or tab."
    )
    lines = ["", rule, title, rule, ""] + list(reason_lines) + [
        "",
        next_heading,
        "",
        "1. Return to FundElite.",
        '2. Click "Launch QA Session".',
        "3. %s" % (new_tab,),
        "4. Paste the new launch command into the new tab.",
        "",
        "Do not reuse the previous launch command or token.",
        "",
        rule,
    ]
    print("\\n".join(lines), flush=True)


def print_qa_session_expired_guidance():
    """Recovery steps once the provider endpoint expired; never prints tokens or credentials."""
    rule = "=" * 50
    new_tab = (
        "Open a NEW Terminal tab (Command + T)."
        if sys.platform == "darwin"
        else "Open a NEW Terminal window or tab."
    )
    print("\\n".join([
        "", rule, "FundElite QA Session Expired", rule, "",
        "The previous QA proxy connection has expired.",
        "",
        "That browser session cannot be reused.",
        "",
        "1. Return to FundElite.",
        '2. Click "Start Fresh QA Session".',
        "3. %s" % (new_tab,),
        "4. Paste the new launch command.",
        "5. Press Enter.",
        "",
        "Your saved QA Browser profile is preserved.",
        "Previous third-party tabs will not be resumed automatically.",
        "This expired QA Browser closes when the fresh session starts.",
        "",
        "Do not reuse the previous launch command.",
        "",
        rule,
    ]), flush=True)


# One QA Browser runtime per local QA profile. The lock holds no secrets, IPs or tokens.
# Nothing here ever signals another process: a healthy runtime refuses the new launch, and
# an expired runtime is asked (through a request file it polls) to close itself.
QA_RUNTIME_LOCK_FILENAME = ".fundelite_qa_runtime.json"
QA_RUNTIME_REPLACE_FILENAME = ".fundelite_qa_runtime_replace.json"
QA_RUNTIME_HEARTBEAT_SECONDS = 5
QA_RUNTIME_STALE_AFTER_SECONDS = 30
QA_RUNTIME_HANDOFF_WAIT_SECONDS = 25
QA_RUNTIME_REPLACE_REQUEST_MAX_AGE_SECONDS = 60
QA_RUNTIME_ALREADY_ACTIVE_EXIT_CODE = 7
QA_RUNTIME_STATES = ("starting", "active", "expired")
QA_RUNTIME = {"runtime_id": None, "state": None, "stop": None, "mutex": None}


def _qa_runtime_file(name):
    return os.path.join(PROFILE_PATH, name)


def _read_qa_runtime_json(name):
    """None when absent, {} when unreadable (treated as stale), else the parsed dict."""
    import json

    try:
        with open(_qa_runtime_file(name), "r", encoding="utf-8") as handle:
            data = json.load(handle)
    except FileNotFoundError:
        return None
    except Exception:
        return {}
    return data if isinstance(data, dict) else {}


def _write_qa_runtime_json(name, data):
    import json

    path = _qa_runtime_file(name)
    tmp = "%s.%s.tmp" % (path, QA_RUNTIME["runtime_id"] or os.getpid())
    fd = os.open(tmp, os.O_WRONLY | os.O_CREAT | os.O_TRUNC, 0o600)
    with os.fdopen(fd, "w", encoding="utf-8") as handle:
        json.dump(data, handle)
    os.replace(tmp, path)


def _qa_pid_exists(pid):
    """Existence check only (signal 0 delivers nothing). Windows: unknown, rely on heartbeat."""
    if os.name == "nt":
        return True
    try:
        os.kill(int(pid), 0)
    except ProcessLookupError:
        return False
    except Exception:
        return True
    return True


def qa_runtime_lock_is_live(lock, now=None):
    """A lock is live only with a fresh heartbeat and, on POSIX, an existing owner pid.
    A reused pid cannot keep a lock alive: its heartbeat stops with the original owner."""
    now = time.time() if now is None else now
    if lock == {}:
        # Unreadable or mid-write: live while the file itself is fresh.
        try:
            return abs(now - os.path.getmtime(_qa_runtime_file(QA_RUNTIME_LOCK_FILENAME))) <= QA_RUNTIME_STALE_AFTER_SECONDS
        except OSError:
            return False
    if not isinstance(lock, dict) or lock.get("state") not in QA_RUNTIME_STATES:
        return False
    try:
        beat = float(lock.get("heartbeat_at"))
    except (TypeError, ValueError):
        return False
    if abs(now - beat) > QA_RUNTIME_STALE_AFTER_SECONDS:
        return False
    return _qa_pid_exists(lock.get("pid"))


def _own_qa_runtime_lock_data():
    return {
        "runtime_id": QA_RUNTIME["runtime_id"],
        "pid": os.getpid(),
        "state": QA_RUNTIME["state"],
        "heartbeat_at": time.time(),
        "launcher_version": QA_LAUNCHER_TEMPLATE_VERSION,
    }


def _qa_runtime_mutex():
    import threading

    if QA_RUNTIME["mutex"] is None:
        QA_RUNTIME["mutex"] = threading.Lock()
    return QA_RUNTIME["mutex"]


def _write_own_qa_runtime_lock():
    # Serialized with release so a heartbeat can never recreate a lock that was just released.
    with _qa_runtime_mutex():
        if QA_RUNTIME["runtime_id"] is None:
            return
        current = _read_qa_runtime_json(QA_RUNTIME_LOCK_FILENAME)
        if current is None or current.get("runtime_id") != QA_RUNTIME["runtime_id"]:
            return
        _write_qa_runtime_json(QA_RUNTIME_LOCK_FILENAME, _own_qa_runtime_lock_data())


def set_qa_runtime_state(state):
    if QA_RUNTIME["runtime_id"] is None or state not in QA_RUNTIME_STATES or QA_RUNTIME["state"] == state:
        return
    QA_RUNTIME["state"] = state
    try:
        _write_own_qa_runtime_lock()
    except Exception:
        pass
    stage_log("qa_runtime", "state=%s" % (state,))


def _qa_runtime_heartbeat(stop):
    while not stop.wait(QA_RUNTIME_HEARTBEAT_SECONDS):
        try:
            _write_own_qa_runtime_lock()
        except Exception:
            pass


def _print_qa_runtime_refusal(title, lines):
    rule = "=" * 50
    print("\\n".join(["", rule, title, rule, ""] + list(lines) + ["", rule]), flush=True)


def acquire_qa_runtime_lock(wait_seconds=None):
    """Claim this profile before the launch token is redeemed, so a refused launch never
    consumes its token or a provider endpoint. An expired runtime is replaced; a starting
    or active one is never touched."""
    import threading

    os.makedirs(PROFILE_PATH, exist_ok=True)
    QA_RUNTIME["runtime_id"] = QA_RUNTIME["runtime_id"] or os.urandom(8).hex()
    wait_seconds = QA_RUNTIME_HANDOFF_WAIT_SECONDS if wait_seconds is None else wait_seconds
    path = _qa_runtime_file(QA_RUNTIME_LOCK_FILENAME)
    handoff_deadline = None
    QA_RUNTIME["state"] = "starting"
    while True:
        try:
            fd = os.open(path, os.O_CREAT | os.O_EXCL | os.O_WRONLY, 0o600)
        except FileExistsError:
            lock = _read_qa_runtime_json(QA_RUNTIME_LOCK_FILENAME)
            if lock is None:
                continue
            if not qa_runtime_lock_is_live(lock):
                again = _read_qa_runtime_json(QA_RUNTIME_LOCK_FILENAME)
                if again == lock:
                    try:
                        os.remove(path)
                    except FileNotFoundError:
                        pass
                    stage_log("qa_runtime_lock", "status=stale_cleared")
                continue
            if lock.get("state") != "expired":
                stage_log("qa_runtime_lock", "status=refused reason=session_already_active")
                _print_qa_runtime_refusal("QA Browser Session Already Running", [
                    "A QA Browser session is already running for this profile.",
                    "",
                    "Keep using the open QA Browser window, or close it first",
                    "and then start a fresh QA session from FundElite.",
                    "",
                    "This launch command was not used.",
                ])
                raise SystemExit(QA_RUNTIME_ALREADY_ACTIVE_EXIT_CODE)
            if handoff_deadline is None:
                handoff_deadline = time.time() + wait_seconds
                _write_qa_runtime_json(QA_RUNTIME_REPLACE_FILENAME, {
                    "target_runtime_id": lock.get("runtime_id"),
                    "requested_at": time.time(),
                })
                stage_log("qa_runtime_lock", "status=replacing_expired_runtime")
                print("Closing the expired QA Browser before starting a fresh session...", flush=True)
            elif time.time() > handoff_deadline:
                stage_log("qa_runtime_lock", "status=refused reason=expired_runtime_still_open")
                _print_qa_runtime_refusal("Expired QA Browser Still Open", [
                    "The previous, expired QA Browser did not close.",
                    "",
                    "Close that browser window, then run this launch command again.",
                    "",
                    "This launch command was not used.",
                ])
                raise SystemExit(QA_RUNTIME_ALREADY_ACTIVE_EXIT_CODE)
            time.sleep(0.5)
            continue
        import json

        try:
            os.write(fd, json.dumps(_own_qa_runtime_lock_data()).encode("utf-8"))
        finally:
            os.close(fd)
        try:
            os.remove(_qa_runtime_file(QA_RUNTIME_REPLACE_FILENAME))
        except FileNotFoundError:
            pass
        stop = threading.Event()
        QA_RUNTIME["stop"] = stop
        threading.Thread(target=_qa_runtime_heartbeat, args=(stop,), name="qa-runtime-heartbeat", daemon=True).start()
        atexit.register(release_qa_runtime_lock)
        stage_log("qa_runtime_lock", "status=acquired")
        return True


def qa_runtime_replacement_requested(now=None):
    request = _read_qa_runtime_json(QA_RUNTIME_REPLACE_FILENAME)
    if not request or request.get("target_runtime_id") != QA_RUNTIME["runtime_id"]:
        return False
    try:
        age = (time.time() if now is None else now) - float(request.get("requested_at"))
    except (TypeError, ValueError):
        return False
    return 0 <= age <= QA_RUNTIME_REPLACE_REQUEST_MAX_AGE_SECONDS


def release_qa_runtime_lock():
    stop = QA_RUNTIME.get("stop")
    if stop is not None:
        stop.set()
    with _qa_runtime_mutex():
        runtime_id = QA_RUNTIME["runtime_id"]
        if runtime_id is None:
            return
        QA_RUNTIME["runtime_id"] = None
        lock = _read_qa_runtime_json(QA_RUNTIME_LOCK_FILENAME)
        if lock and lock.get("runtime_id") == runtime_id:
            try:
                os.remove(_qa_runtime_file(QA_RUNTIME_LOCK_FILENAME))
            except FileNotFoundError:
                pass


def release_qa_runtime_lock_for_pid(pid):
    """Supervisor-side cleanup after its own worker exited (e.g. force-killed mid-cleanup)."""
    lock = _read_qa_runtime_json(QA_RUNTIME_LOCK_FILENAME)
    if lock and str(lock.get("pid")) == str(pid):
        try:
            os.remove(_qa_runtime_file(QA_RUNTIME_LOCK_FILENAME))
        except FileNotFoundError:
            pass


def show_qa_session_expired_recovery(pages):
    """Replace every open tab with the recovery page in place. No navigation and no network:
    the old third-party documents are never reloaded, on this endpoint or any other."""
    for page in list(pages or []):
        host = "(blank)"
        try:
            host = _normalize_hostname(getattr(page, "url", "") or "") or "(blank)"
            page.set_content(qa_session_expired_recovery_html(host))
        except Exception:
            pass
    stage_log("qa_session_expired_recovery", "pages=%s" % (len(list(pages or [])),))


def discard_pending_operator_input():
    """Drop keystrokes typed or pasted before the prompt so they cannot end the session."""
    try:
        if not sys.stdin or not sys.stdin.isatty():
            return
        if os.name == "nt":
            import msvcrt

            while msvcrt.kbhit():
                msvcrt.getwch()
        else:
            import termios

            termios.tcflush(sys.stdin.fileno(), termios.TCIFLUSH)
    except Exception:
        pass


def wait_until_operator_enter(page, prompt_text):
    """Keep the QA session open until the operator explicitly ends it.

    The session ends only when the operator closes every browser window or
    interrupts the launcher (Ctrl+C, SIGTERM, terminal closed). Terminal input
    never ends it: Enter, typed text and pasted commands (e.g. a new launch
    command pasted into this tab) are read and discarded, so they neither close
    the browser nor reach the shell after the launcher exits. End-of-file or an
    unreadable stdin (harness, detached terminal) is logged and ignored.

    stdin is drained on a thread: blocking on input() freezes Playwright's sync
    dispatcher, so route.continue_() and page event handlers never run.
    """
    import threading

    context = page.context if page is not None and hasattr(page, "context") else None
    state = {"reason": None}

    def _stdin_reader():
        ignored = 0
        while True:
            try:
                line = sys.stdin.readline()
            except Exception as exc:
                stage_log("stdin_closed", "reason=error error=%s" % (type(exc).__name__,))
                return
            if line == "":
                stage_log("stdin_closed", "reason=eof")
                return
            ignored += 1
            if ignored == 1:
                print(QA_OPERATOR_INPUT_IGNORED_NOTICE, flush=True)
            if ignored == 1 or ignored % 20 == 0:
                stage_log("operator_input_ignored", "lines=%s" % (ignored,))

    def _on_browser_closed(*_args):
        state["reason"] = state["reason"] or "browser_disconnected"

    for target in (context, getattr(context, "browser", None)):
        if target is None or not hasattr(target, "on"):
            continue
        for event_name in ("close", "disconnected"):
            try:
                target.on(event_name, _on_browser_closed)
            except Exception:
                pass

    interactive = False
    try:
        interactive = bool(sys.stdin) and sys.stdin.isatty()
    except Exception:
        interactive = False
    discard_pending_operator_input()
    print(prompt_text, flush=True)
    if interactive:
        stage_log("operator_wait", "stdin=interactive end=close_windows input=ignored")
        threading.Thread(target=_stdin_reader, name="qa-operator-input-drain", daemon=True).start()
    else:
        stage_log("operator_wait", "stdin=non_interactive end=close_windows")
        print(
            "No interactive terminal attached. Close the QA browser window to end this session.",
            flush=True,
        )

    last_pages_log = 0.0
    no_pages_since = None
    expired_recovery_shown = False
    while state["reason"] is None:
        live_pages = []
        try:
            live_pages = list(context.pages) if context is not None else ([page] if page is not None else [])
        except Exception:
            live_pages = []
        pump = live_pages[0] if live_pages else None
        try:
            if pump is not None and hasattr(pump, "wait_for_timeout"):
                pump.wait_for_timeout(250)
            else:
                time.sleep(0.25)
        except Exception:
            time.sleep(0.25)
        now = time.time()
        if context is not None and not live_pages:
            no_pages_since = no_pages_since or now
            if now - no_pages_since >= QA_ALL_WINDOWS_CLOSED_GRACE_SECONDS:
                state["reason"] = state["reason"] or "all_windows_closed"
        else:
            no_pages_since = None
        # Session TTL expired is terminal: end the operator wait so the safe cleanup path runs.
        if QA_SESSION_TTL.get("expired") and state["reason"] is None:
            state["reason"] = "ttl_expired"
        # Expired is terminal: tabs show the recovery page and this runtime only waits to be
        # closed (by the operator or by the fresh session that replaces it).
        if QA_UPSTREAM_SESSION["expired"] and state["reason"] is None:
            if not expired_recovery_shown:
                expired_recovery_shown = True
                show_qa_session_expired_recovery(live_pages)
                set_qa_runtime_state("expired")
            if qa_runtime_replacement_requested():
                state["reason"] = "replaced_after_expiry"
        if now - last_pages_log >= 30:
            last_pages_log = now
            try:
                log_qa_pages(context if context is not None else page)
            except Exception:
                pass
    discard_pending_operator_input()
    stage_log("session_end", "reason=%s" % (state["reason"],))
    if state["reason"] == "replaced_after_expiry":
        print(
            "This expired QA Browser was closed because a fresh QA session started in another "
            "Terminal tab. You can close this tab.",
            flush=True,
        )
    elif state["reason"] == "ttl_expired":
        print(
            "The authorized QA session reached its configured time limit and was closed. "
            "Launch a new QA session to continue.",
            flush=True,
        )


def camoufox_monitor_config(logical_width, logical_height, window_width, window_height, window_x, window_y):
    inner_width = max(MIN_WIDTH, int(window_width))
    inner_height = max(MIN_HEIGHT, int(window_height) - FIREFOX_CHROME_HEIGHT_PX)
    return {
        "screen.width": int(logical_width),
        "screen.height": int(logical_height),
        "screen.availWidth": int(window_width),
        "screen.availHeight": int(window_height),
        "screen.availLeft": int(window_x or 0),
        "screen.availTop": max(0, int(window_y or 0)),
        "window.outerWidth": int(window_width),
        "window.outerHeight": int(window_height),
        "window.innerWidth": inner_width,
        "window.innerHeight": inner_height,
        "window.screenX": int(window_x or 0),
        "window.screenY": int(window_y or 0),
    }


def place_browser_on_selected_display(page, window_x, window_y, window_width, window_height):
    """Move/resize the real OS window onto the selected monitor work area."""
    try:
        page.evaluate(
            """([x, y, w, h]) => {
                try { window.moveTo(x, y); } catch (e) {}
                try { window.resizeTo(w, h); } catch (e) {}
            }""",
            [int(window_x or 0), int(window_y or 0), int(window_width), int(window_height)],
        )
    except Exception as exc:
        print("Could not move QA window via page APIs (%s)." % (sanitize_launcher_error_text(exc),))
    if platform.system() != "Darwin":
        return
    try:
        cocoa_x = os.environ.get("FUNDELITE_COCOA_WORK_X") or "0"
        cocoa_y = os.environ.get("FUNDELITE_COCOA_WORK_Y") or "0"
        script = (
            'tell application "System Events"\\n'
            "  set matched to {}\\n"
            '  repeat with procName in {"Camoufox", "Firefox", "Nightly"}\\n'
            "    try\\n"
            "      if exists process procName then set end of matched to procName\\n"
            "    end try\\n"
            "  end repeat\\n"
            "  if (count of matched) is 0 then return\\n"
            "  tell process (item 1 of matched)\\n"
            "    set frontmost to true\\n"
            "    try\\n"
            "      set position of window 1 to {%s, %s}\\n"
            "      set size of window 1 to {%s, %s}\\n"
            "    end try\\n"
            "  end tell\\n"
            "end tell"
            % (int(window_x or 0), int(window_y or 0), int(window_width), int(window_height))
        )
        subprocess.run(["osascript", "-e", script], check=False, capture_output=True, timeout=4)
        print(
            "macos_window_placed work_area=%sx%s css_origin=%s,%s cocoa_work=%s,%s"
            % (window_width, window_height, window_x, window_y, cocoa_x, cocoa_y),
            flush=True,
        )
    except Exception as exc:
        print("Could not place QA window with System Events (%s)." % (sanitize_launcher_error_text(exc),))


def inspect_startup_load(page, started_at):
    elapsed_ms = int((time.time() - started_at) * 1000)
    url_host = _normalize_hostname(getattr(page, "url", "") or "")
    title = ""
    root_chars = 0
    inner_w = inner_h = outer_w = outer_h = screen_w = screen_h = None
    try:
        metrics = page.evaluate(
            """() => ({
                title: document.title || "",
                rootChars: ((document.querySelector("#root") || {}).innerHTML || "").length,
                innerWidth: window.innerWidth,
                innerHeight: window.innerHeight,
                outerWidth: window.outerWidth,
                outerHeight: window.outerHeight,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
            })"""
        )
        title = (metrics.get("title") or "")[:80]
        root_chars = int(metrics.get("rootChars") or 0)
        inner_w, inner_h = metrics.get("innerWidth"), metrics.get("innerHeight")
        outer_w, outer_h = metrics.get("outerWidth"), metrics.get("outerHeight")
        screen_w, screen_h = metrics.get("screenWidth"), metrics.get("screenHeight")
    except Exception as exc:
        print("Could not inspect FundElite startup DOM (%s)." % (sanitize_launcher_error_text(exc),))
    print(
        "startup_load_inspect elapsed_ms=%s url_host=%s title=%s root_chars=%s screen=%sx%s outer=%sx%s inner=%sx%s"
        % (elapsed_ms, url_host, title, root_chars, screen_w, screen_h, outer_w, outer_h, inner_w, inner_h),
        flush=True,
    )
    if elapsed_ms > QA_PROXY_STARTUP_NAVIGATION_TIMEOUT_MS:
        print("WARNING: FundElite startup exceeded 10s.", flush=True)
    if screen_w in (960, "960") or screen_h in (540, "540"):
        print("WARNING: Fingerprint screen is a fake 960x540 viewport.", flush=True)
    return elapsed_ms <= QA_PROXY_STARTUP_NAVIGATION_TIMEOUT_MS and root_chars > 0



def _normalize_hostname(value):
    if not isinstance(value, str):
        return ""
    host = value.strip().lower()
    if "://" in host:
        try:
            from urllib.parse import urlparse

            host = (urlparse(host).hostname or "").lower()
        except Exception:
            host = host.split("://", 1)[-1]
    host = host.split("/", 1)[0].split("?", 1)[0].split("#", 1)[0]
    if host.startswith("[") and "]" in host:
        host = host[1:host.index("]")]
    elif ":" in host and host.count(":") == 1:
        host = host.split(":", 1)[0]
    return host.rstrip(".")


def is_private_or_unsafe_network_host(hostname):
    """Always block localhost, RFC1918, link-local, and cloud metadata — even in full_web mode."""
    import ipaddress

    host = _normalize_hostname(hostname)
    if not host:
        return True
    if host in {
        "localhost",
        "localhost.localdomain",
        "ip6-localhost",
        "metadata",
        "metadata.google.internal",
    }:
        return True
    if host.endswith(".local") or host.endswith(".internal") or host.endswith(".localhost"):
        return True
    if host == "169.254.169.254" or host.startswith("169.254."):
        return True
    try:
        ip = ipaddress.ip_address(host)
    except ValueError:
        # A non-IP host with ":" or brackets is malformed, and a single-label name only
        # resolves on an internal network; neither may reach the upstream proxy.
        if ":" in host or "[" in host or "]" in host:
            return True
        return len([label for label in host.split(".") if label]) < 2
    if (
        ip.is_private or ip.is_loopback or ip.is_link_local or ip.is_reserved
        or ip.is_multicast or ip.is_unspecified
    ):
        return True
    if getattr(ip, "ipv4_mapped", None) is not None:
        return is_private_or_unsafe_network_host(str(ip.ipv4_mapped))
    return False


def is_hostname_allowlisted(hostname, approved_hosts):
    host = _normalize_hostname(hostname)
    if not host:
        return False
    for entry in approved_hosts or []:
        approved = _normalize_hostname(str(entry or ""))
        if not approved:
            continue
        if approved.startswith("*."):
            suffix = approved[1:]  # .example.com
            if host.endswith(suffix) and host != suffix.lstrip("."):
                return True
            continue
        if host == approved or host.endswith("." + approved):
            return True
    return False


QA_PERMANENT_BLOCK_REGISTRABLE = (
    "google.com", "googleapis.com", "gstatic.com", "googleusercontent.com",
    "youtube.com", "youtu.be", "bing.com", "yahoo.com", "duckduckgo.com",
    "facebook.com", "fb.com", "instagram.com", "tiktok.com", "twitter.com", "x.com",
    "linkedin.com", "reddit.com", "discord.com", "telegram.org", "t.me", "whatsapp.com",
    "netflix.com", "twitch.tv", "gmail.com", "outlook.com", "hotmail.com",
)
QA_PERMANENT_BLOCK_KEYWORDS = ("vpn", "proxy", "porn", "casino", "gambling", "torrent", "warez")


def is_permanently_blocked_qa_host(hostname):
    host = _normalize_hostname(hostname)
    if not host:
        return True
    if host in ("localhost", "127.0.0.1") or host.endswith(".local"):
        return False
    labels = [part for part in host.split(".") if part]
    registrable = ".".join(labels[-2:]) if len(labels) >= 2 else host
    if host in QA_PERMANENT_BLOCK_REGISTRABLE or registrable in QA_PERMANENT_BLOCK_REGISTRABLE:
        return True
    compact = host.replace("-", "").replace(".", "")
    return any(keyword in compact for keyword in QA_PERMANENT_BLOCK_KEYWORDS)


def qa_website_block_page_text(domain):
    host = _normalize_hostname(domain) or "this website"
    return (
        "This website is not approved for QA Proxy use.\\n"
        "Domain requested: %s\\n"
        "Contact your administrator.\\n"
        % host
    )


def qa_website_block_page_html(domain):
    host = _normalize_hostname(domain) or "this website"
    safe = []
    for ch in host:
        code = ord(ch)
        if code == 38:
            safe.append("&" + "amp;")
        elif code == 60:
            safe.append("&" + "lt;")
        elif code == 62:
            safe.append("&" + "gt;")
        elif code == 34:
            safe.append("&" + "quot;")
        else:
            safe.append(ch)
    safe_host = "".join(safe)
    return (
        "<!DOCTYPE html><html lang=\\"en\\"><head><meta charset=\\"utf-8\\" />"
        "<title>QA Proxy</title></head><body>"
        "<h1>This website is not approved for QA Proxy use.</h1>"
        "<p>Domain requested: %s</p>"
        "<p>Contact your administrator.</p>"
        "</body></html>"
    ) % safe_host


QA_UPSTREAM_FAILURE_COPY = {
    "provider_destination_blocked": (
        "Website blocked by the proxy provider",
        "The QA proxy session is active, but the proxy provider ({provider}) refuses "
        "connections to this website. Retrying or starting a new QA session will not "
        "change this. FundElite did not bypass the proxy.",
    ),
    "provider_endpoint_expired": (
        "{provider} proxy endpoint ended",
        "The {provider} proxy endpoint ended before your authorized QA time elapsed. "
        "Start a fresh QA session from FundElite to continue.",
    ),
    "upstream_peer_unavailable": (
        "Proxy session unavailable",
        "The residential proxy session for this QA browser is no longer available. "
        "Close this browser and start a new QA session from FundElite.",
    ),
    "upstream_connect_timeout": (
        "Proxy connection timed out",
        "The residential proxy did not respond in time. Retry this page on the same "
        "session; if every site fails, start a new QA session.",
    ),
    "destination_connect_failed": (
        "Destination connection failed",
        "The QA proxy is active, but the provider could not connect to this website. "
        "Retry this page on the same session; if every site fails, start a new QA session.",
    ),
    "upstream_proxy_rejected": (
        "Proxy session rejected",
        "The proxy provider rejected this QA session. Start a new QA session from FundElite.",
    ),
}


def _qa_html_text(value):
    out = []
    for ch in str(value or ""):
        code = ord(ch)
        if code == 38:
            out.append("&" + "amp;")
        elif code == 60:
            out.append("&" + "lt;")
        elif code == 62:
            out.append("&" + "gt;")
        elif code == 34:
            out.append("&" + "quot;")
        elif code == 39:
            out.append("&" + "#39;")
        elif code < 32:
            continue
        else:
            out.append(ch)
    return "".join(out)


QA_SESSION_EXPIRED_RECOVERY_STYLE = (
    "body{margin:0;background:#0f1115;color:#e7e9ee;font:16px/1.5 -apple-system,"
    "BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}"
    "main{max-width:560px;margin:72px auto;padding:32px;background:#171a21;"
    "border:1px solid #2a2f3a;border-radius:16px}"
    ".brand{margin:0 0 20px;font-size:13px;font-weight:700;letter-spacing:.08em;"
    "text-transform:uppercase;color:#d4a94e}"
    "h1{margin:0 0 16px;font-size:28px}"
    "p{margin:0 0 12px}"
    ".cta{margin:24px 0;padding:14px 18px;border-radius:10px;background:#d4a94e;"
    "color:#14161b;font-weight:700}"
    "ul{margin:0 0 16px;padding-left:20px;color:#b8bdc8}"
    ".meta{margin-top:20px;font-size:12px;color:#7d8494}"
)


def qa_session_expired_recovery_html(domain=""):
    """Terminal page for an expired QA session: static text only (no links, scripts or forms),
    so it cannot navigate, reconnect or call any backend."""
    host = _normalize_hostname(domain) or "(blank)"
    return (
        "<!DOCTYPE html><html lang=\\"en\\"><head><meta charset=\\"utf-8\\" />"
        "<meta name=\\"fundelite-qa-session\\" content=\\"expired\\" />"
        "<title>QA Session Expired - FundElite QA</title>"
        "<style>" + QA_SESSION_EXPIRED_RECOVERY_STYLE + "</style></head><body><main>"
        "<p class=\\"brand\\">FundElite QA</p>"
        "<h1>QA Session Expired</h1>"
        "<p>Your secure QA connection has expired.</p>"
        "<p>For your protection, this browser session cannot continue using the expired connection.</p>"
        "<p>Start a fresh QA session from FundElite to continue.</p>"
        "<div class=\\"cta\\">Return to FundElite and click Start Fresh QA Session</div>"
        "<ul>"
        "<li>Your saved QA Browser profile is preserved.</li>"
        "<li>A fresh session will verify a new proxy connection before browsing is enabled.</li>"
        "<li>Do not refresh this page to continue the old session.</li>"
        "</ul>"
        "<p class=\\"meta\\">Website: " + _qa_html_text(host) + " &middot; Reason: provider_endpoint_expired</p>"
        "</main></body></html>"
    )


def qa_proxy_connection_error_html(domain, failure_text="", classification=None):
    """Shown when a permitted PROXY navigation fails (not a policy block)."""
    if classification == "provider_endpoint_expired" and QA_UPSTREAM_SESSION["expired"]:
        return qa_session_expired_recovery_html(domain)
    host = _normalize_hostname(domain) or "this website"
    safe = []
    for ch in host:
        code = ord(ch)
        if code == 38:
            safe.append("&" + "amp;")
        elif code == 60:
            safe.append("&" + "lt;")
        elif code == 62:
            safe.append("&" + "gt;")
        elif code == 34:
            safe.append("&" + "quot;")
        else:
            safe.append(ch)
    safe_host = "".join(safe)
    detail = sanitize_launcher_error_text(failure_text)[:180] if failure_text else ""
    detail_html = ("<p>Detail: %s</p>" % detail) if detail else ""
    copy = QA_UPSTREAM_FAILURE_COPY.get(classification or "")
    if copy:
        retry_html = (
            "<p><a href=\\"https://%s/\\">Retry on this QA session</a></p>" % safe_host
            if classification in {"upstream_connect_timeout", "destination_connect_failed"}
            else ""
        )
        return (
            "<!DOCTYPE html><html lang=\\"en\\"><head><meta charset=\\"utf-8\\" />"
            "<title>%s</title></head><body>"
            "<h1>%s</h1>"
            "<p>%s</p>"
            "<p>Host: %s</p>"
            "<p>Reason: %s</p>"
            "%s"
            "</body></html>"
        ) % (
            copy[0].replace("{provider}", QA_UPSTREAM_PROVIDER["label"]),
            copy[0].replace("{provider}", QA_UPSTREAM_PROVIDER["label"]),
            copy[1].replace("{provider}", QA_UPSTREAM_PROVIDER["label"]),
            safe_host, classification, retry_html,
        )
    return (
        "<!DOCTYPE html><html lang=\\"en\\"><head><meta charset=\\"utf-8\\" />"
        "<title>QA proxy connection failed</title></head><body>"
        "<h1>QA proxy connection failed</h1>"
        "<p>FundElite could not complete this navigation through the QA proxy.</p>"
        "<p>Host: %s</p>"
        "%s"
        "<p>This is not a website-policy block. Check the terminal for "
        "qa_tunnel / qa_request_failed diagnostics, then retry Launch QA Session.</p>"
        "</body></html>"
    ) % (safe_host, detail_html)


def website_access_decide_url(payload):
    info = payload.get("websiteAccess") or payload.get("website_access") or {}
    path = info.get("decidePath") or info.get("decide_path") or "/api/funding-hq/authorized-qa-proxy/website-access/decide"
    if isinstance(path, str) and path.startswith("http"):
        return path
    base = AUTHORIZED_QA_PROXY_LAUNCH_CONFIG_URL
    marker = "/api/"
    idx = base.find(marker)
    origin = base[:idx] if idx >= 0 else base.rstrip("/")
    if not isinstance(path, str) or not path.startswith("/"):
        path = "/api/funding-hq/authorized-qa-proxy/website-access/decide"
    return origin + path


def fetch_website_access_decision(host, session_token, decide_url):
    import json
    import urllib.request

    if not session_token or not decide_url:
        return "block"
    try:
        body = json.dumps({"token": session_token, "host": host}).encode("utf-8")
        request = urllib.request.Request(
            decide_url,
            data=body,
            headers={"Content-Type": "application/json", "Accept": "application/json"},
            method="POST",
        )
        ssl_context = build_verified_tls_context()
        with urllib.request.urlopen(request, timeout=2.5, context=ssl_context) as response:
            payload = json.loads(response.read().decode("utf-8"))
        if payload.get("allowed") is True:
            return "allow"
        return "block"
    except Exception:
        return "block"


QA_PROXY_SESSION_BLOCK_MESSAGE = (
    "This website is not approved for QA Proxy use."
)
QA_PROXY_SESSION_BLOCK_PAGE_HTML = qa_website_block_page_html("")


def session_route_decision(hostname, approved_hosts, proxy_configured, web_access_mode="restricted_allowlist"):
    """QA session route decisions. Never fall back to DIRECT networking.

    Missing/invalid proxy configuration fails closed (BLOCKED). Full web and
    restricted modes still always stay proxied when a proxy is configured.
    """
    if not proxy_configured:
        return "BLOCKED"
    if is_private_or_unsafe_network_host(hostname):
        return "BLOCKED"
    mode = str(web_access_mode or "restricted_allowlist").strip().lower()
    if mode in {"full_web", "full_web_proxied", "full-web"}:
        return "PROXY"
    if is_hostname_allowlisted(hostname, approved_hosts):
        return "PROXY"
    if is_permanently_blocked_qa_host(hostname):
        return "BLOCKED"
    return "CATALOG"


def print_authorized_qa_proxy_route_diagnostic(
    *,
    url_or_host,
    approved_hosts,
    proxy_configured,
    routing_mode,
    profile_id=None,
    assignment_id=None,
    web_access_mode="restricted_allowlist",
):
    """Safe routing diagnostic — never prints tokens, passwords, or cookies."""
    hostname = _normalize_hostname(url_or_host)
    decision = session_route_decision(
        hostname, approved_hosts, proxy_configured, web_access_mode=web_access_mode
    )
    print("Authorized QA Proxy Session route diagnostic:")
    print("  hostname: %s" % (hostname or "(unknown)",))
    print("  route decision: %s" % (decision,))
    print("  routing_mode: %s" % (routing_mode,))
    print("  web_access_mode: %s" % (web_access_mode,))
    if profile_id:
        print("  profile_id: %s" % (profile_id,))
    if assignment_id:
        print("  assignment_id: %s" % (assignment_id,))


def install_qa_allowlist_guards(context, approved_hosts, web_access_mode="restricted_allowlist"):
    """Restricted mode: page-level host allowlist via Playwright routes.

    Full Web must NOT install a catch-all host allowlist. Public HTTP(S)
    enforcement is the local QA gateway (PROXY) plus private/SSRF blocks there.
    Installing context.route("**/*") while the operator wait blocks on input()
    freezes route.continue_() and is the production cause of address-bar URLs
    updating with no qa_request / no CONNECT.
    """

    mode = str(web_access_mode or "restricted_allowlist").strip().lower()
    full_web = mode in {"full_web", "full_web_proxied", "full-web"}
    if full_web:
        print(
            "qa_allowlist_guards mode=full_web page_host_routes=skipped "
            "enforcement=gateway_only",
            flush=True,
        )
        return

    def on_route(route):
        request = route.request
        url = request.url or ""
        scheme = ""
        try:
            from urllib.parse import urlparse

            scheme = (urlparse(url).scheme or "").lower()
        except Exception:
            scheme = ""
        if url.startswith("about:") or url.startswith("data:") or url.startswith("blob:"):
            route.continue_()
            return
        if scheme and scheme not in {"http", "https"}:
            print("Blocked non-http(s) navigation in QA Proxy Session: scheme=%s" % (scheme,))
            try:
                route.abort("connectionrefused")
            except Exception:
                route.abort()
            return
        host = _normalize_hostname(url)
        if is_private_or_unsafe_network_host(host):
            resource_type = getattr(request, "resource_type", None)
            is_document = resource_type == "document"
            try:
                is_navigation = bool(request.is_navigation_request())
            except Exception:
                is_navigation = is_document
            if is_document or is_navigation:
                print(
                    "Blocked private/unsafe navigation in QA Proxy Session: hostname=%s"
                    % (host or "(unknown)",)
                )
                route.fulfill(
                    status=403,
                    content_type="text/html; charset=utf-8",
                    body=qa_website_block_page_html(host),
                )
                return
            try:
                route.abort("connectionrefused")
            except Exception:
                route.abort()
            return
        if is_hostname_allowlisted(host, approved_hosts) or not is_permanently_blocked_qa_host(host):
            route.continue_()
            return
        resource_type = getattr(request, "resource_type", None)
        is_document = resource_type == "document"
        try:
            is_navigation = bool(request.is_navigation_request())
        except Exception:
            is_navigation = is_document
        if is_document or is_navigation:
            print(
                "Blocked restricted navigation in QA Proxy Session: hostname=%s"
                % (host or "(unknown)",)
            )
            route.fulfill(
                status=403,
                content_type="text/html; charset=utf-8",
                body=qa_website_block_page_html(host),
            )
            return
        print(
            "Blocked restricted request in QA Proxy Session: hostname=%s type=%s"
            % (host or "(unknown)", resource_type or "unknown")
        )
        try:
            route.abort("connectionrefused")
        except Exception:
            route.abort()

    context.route("**/*", on_route)
    print(
        "qa_allowlist_guards mode=restricted_allowlist page_host_routes=installed",
        flush=True,
    )


def print_browser_geo_environment_diagnostic(page):
    """Non-mutating timezone/locale/geolocation probe — no secrets."""
    try:
        metrics = page.evaluate(
            """() => {
                let timezone = null;
                try {
                    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
                } catch (e) {}
                let locale = null;
                try {
                    locale = Intl.DateTimeFormat().resolvedOptions().locale || null;
                } catch (e) {}
                let languages = null;
                try {
                    languages = Array.isArray(navigator.languages)
                        ? navigator.languages.slice(0, 5).join(",")
                        : (navigator.language || null);
                } catch (e) {}
                return {
                    timezone: timezone,
                    locale: locale,
                    languages: languages,
                    geolocationApi: typeof navigator.geolocation,
                };
            }"""
        )
    except Exception as exc:
        print("Could not read browser geo/locale diagnostics (%s)." % (exc,))
        return
    print("Browser geo/locale configuration:")
    print("  timezone: %s" % (metrics.get("timezone"),))
    print("  locale: %s" % (metrics.get("locale"),))
    print("  languages: %s" % (metrics.get("languages"),))
    print("  geolocationApi: %s" % (metrics.get("geolocationApi"),))


def build_verified_tls_context():
    """Verified TLS for FundGenius token redemption — never disable certificate checks."""
    import ssl
    import sys

    try:
        import certifi
    except ImportError:
        print(
            "certifi is required for verified HTTPS token redemption. "
            "Re-run the Browser Profiles setup command to install dependencies."
        )
        sys.exit(2)

    # Permanent fix for macOS/system Python CERTIFICATE_VERIFY_FAILED:
    # use Mozilla CA bundle from certifi. Never use unverified SSL contexts.
    context = ssl.create_default_context(cafile=certifi.where())
    print("tls_context=verified_certifi")
    return context


def qa_launch_token_error_message(detail):
    text = str(detail or "").lower()
    if "already used" in text:
        return "This launch command has already been used. Generate a new one from FundElite."
    if "launch token has expired" in text:
        return "This launch command has expired. Generate a new one from FundElite."
    return None


def load_authorized_qa_proxy_config():
    """Redeem a short-lived launch token. Secrets never live in this launcher file."""
    import json
    import os
    import sys
    import urllib.error
    import urllib.request

    token = (os.environ.get("FUNDELITE_QA_PROXY_LAUNCH_TOKEN") or "").strip()
    if not token:
        print(
            "Authorized QA Proxy requires a short-lived launch token. "
            "Issue one from FundGenius Browser Profiles, then set "
            "FUNDELITE_QA_PROXY_LAUNCH_TOKEN before running this launcher."
        )
        sys.exit(2)

    request = urllib.request.Request(
        AUTHORIZED_QA_PROXY_LAUNCH_CONFIG_URL,
        data=json.dumps({"token": token}).encode("utf-8"),
        headers={"Content-Type": "application/json", "Accept": "application/json"},
        method="POST",
    )
    ssl_context = build_verified_tls_context()
    try:
        with urllib.request.urlopen(request, timeout=30, context=ssl_context) as response:
            payload = json.loads(response.read().decode("utf-8"))
    except urllib.error.HTTPError as exc:
        detail = ""
        try:
            detail = exc.read().decode("utf-8")
        except Exception:
            detail = str(exc)
        friendly = qa_launch_token_error_message(detail)
        if friendly:
            print(friendly)
        print("Authorized QA Proxy cannot be used: %s" % (detail or exc,))
        sys.exit(3)
    except Exception as exc:
        print("Authorized QA Proxy cannot be used: %s" % (exc,))
        sys.exit(3)

    set_qa_upstream_provider(payload.get("provider"))
    host = str(payload.get("host") or "").strip()
    port = payload.get("port")
    protocol = str(payload.get("protocol") or "http").strip().lower()
    username = payload.get("username")
    password = payload.get("password")
    startup_url = payload.get("startupUrl") or payload.get("startup_url")
    approved_hosts = payload.get("approvedHosts") or payload.get("approved_hosts") or []
    if not isinstance(approved_hosts, list):
        approved_hosts = []
    profile_id = payload.get("profileId") or payload.get("profile_id")
    assignment_id = payload.get("assignmentId") or payload.get("assignment_id")
    routing_mode = (
        payload.get("routingMode")
        or payload.get("routing_mode")
        or AUTHORIZED_QA_PROXY_ROUTING_MODE
    )
    web_access_mode = str(
        payload.get("webAccessMode")
        or payload.get("web_access_mode")
        or "restricted_allowlist"
    ).strip().lower() or "restricted_allowlist"
    qa_browser_display = normalize_qa_browser_display(
        payload.get("qaBrowserDisplay") or payload.get("qa_browser_display")
    )
    if not host or not port:
        print("Authorized QA Proxy cannot be used: incomplete launch configuration.")
        sys.exit(3)

    scheme = "socks5" if protocol == "socks5" else "http"
    proxy = {
        "server": "%s://%s:%s" % (scheme, host, port),
    }
    if username:
        proxy["username"] = username
    if password:
        proxy["password"] = password
    print(
        "Authorized QA Proxy Session — all browser traffic in this session uses the approved QA proxy."
    )
    if web_access_mode in {"full_web", "full_web_proxied", "full-web"}:
        print(
            "Session routing_mode=%s web_access_mode=full_web with geoip aligned to the proxy exit. "
            "Public HTTP(S) sites are allowed through the proxy. Private networks remain blocked. "
            "Primary Browser remains separate and direct."
            % (routing_mode,)
        )
    else:
        print(
            "Session routing_mode=%s web_access_mode=restricted_allowlist with geoip aligned to the proxy exit. "
            "Non-allowlisted hosts are blocked. Primary Browser remains separate and direct."
            % (routing_mode,)
        )
    # Never print token, username, or password.
    return {
        "proxy": proxy,
        "startup_url": startup_url if isinstance(startup_url, str) else None,
        "approved_hosts": [str(item) for item in approved_hosts if item is not None],
        "profile_id": profile_id if isinstance(profile_id, str) else None,
        "assignment_id": assignment_id if isinstance(assignment_id, str) else None,
        "routing_mode": routing_mode if isinstance(routing_mode, str) else AUTHORIZED_QA_PROXY_ROUTING_MODE,
        "web_access_mode": web_access_mode,
        "targeting": payload.get("targeting") if isinstance(payload.get("targeting"), dict) else {},
        "requested_targeting": (
            payload.get("requestedTargeting")
            if isinstance(payload.get("requestedTargeting"), dict)
            else {}
        ),
        "qa_browser_display": qa_browser_display,
        "website_access_session_token": (
            (payload.get("websiteAccess") or payload.get("website_access") or {}).get("sessionToken")
            or (payload.get("websiteAccess") or payload.get("website_access") or {}).get("session_token")
            or ""
        ),
        "website_access_decide_url": website_access_decide_url(payload),
        "provider": QA_UPSTREAM_PROVIDER["id"],
        "upstream_keepalive_seconds": payload.get("upstreamKeepaliveSeconds"),
    }
`;
}

export const CAMOUFOX_LAUNCH_TIMEOUT_SECONDS = 90;

/**
 * Shared launcher safety: stage logs, sanitized errors, SIGINT, bounded spawn timeout.
 * Supervisor stays free of Camoufox imports so the worker can load Playwright cleanly.
 */
function buildCamoufoxLaunchSafetyPython() {
  return `
import atexit
import os
import signal
import subprocess
import sys
import threading
import time


CAMOUFOX_LAUNCH_TIMEOUT_SECONDS = int(
    os.environ.get("FUNDELITE_CAMOUFOX_LAUNCH_TIMEOUT", "${CAMOUFOX_LAUNCH_TIMEOUT_SECONDS}")
)
_LAUNCH_STATUS_STARTED = "started"
_LAUNCH_STATUS_CONNECTED = "connected"
_LAUNCH_STATUS_FAILED = "failed"
_camoufox_cm_holder = []
_secret_redactions = []


def _register_secret_redaction(value):
    text = str(value or "").strip()
    if text and text not in _secret_redactions:
        _secret_redactions.append(text)


def sanitize_launcher_error_text(text):
    """Never expose launch tokens or proxy passwords in launcher errors/logs."""
    out = str(text or "")
    token = (os.environ.get("FUNDELITE_QA_PROXY_LAUNCH_TOKEN") or "").strip()
    if token:
        out = out.replace(token, "[REDACTED]")
    for secret in list(_secret_redactions):
        if secret:
            out = out.replace(secret, "[REDACTED]")
    try:
        import re
        out = re.sub(r"(password\\s*[:=]\\s*)(\\S+)", r"\\1[REDACTED]", out, flags=re.I)
        out = re.sub(r"(proxy-authorization:\\s*)(\\S+)", r"\\1[REDACTED]", out, flags=re.I)
    except Exception:
        pass
    return out


def stage_log(name, detail=""):
    message = "stage=%s" % (name,)
    if detail:
        message = "%s %s" % (message, sanitize_launcher_error_text(detail))
    print(message, flush=True)


def launch_status_path():
    return os.path.join(PROFILE_PATH, ".fundelite_launch_status")


def write_launch_status(value):
    try:
        os.makedirs(PROFILE_PATH, exist_ok=True)
        with open(launch_status_path(), "w", encoding="utf-8") as handle:
            handle.write(str(value or ""))
    except Exception:
        pass


def read_launch_status():
    try:
        with open(launch_status_path(), "r", encoding="utf-8") as handle:
            return handle.read().strip()
    except Exception:
        return ""


def clear_launch_status():
    try:
        os.remove(launch_status_path())
    except Exception:
        pass


def iter_descendant_pids(root_pid):
    try:
        out = subprocess.check_output(
            ["pgrep", "-P", str(root_pid)],
            text=True,
            stderr=subprocess.DEVNULL,
        )
    except Exception:
        return
    for line in out.splitlines():
        try:
            child = int(line.strip())
        except ValueError:
            continue
        yield child
        for descendant in iter_descendant_pids(child):
            yield descendant


def terminate_descendant_processes(root_pid=None, *, force=False):
    pid = int(root_pid or os.getpid())
    sig = signal.SIGKILL if force else signal.SIGTERM
    for child in list(iter_descendant_pids(pid)):
        try:
            os.kill(child, sig)
        except ProcessLookupError:
            pass
        except Exception:
            pass
    if root_pid is not None and int(root_pid) != os.getpid():
        try:
            os.kill(int(root_pid), sig)
        except ProcessLookupError:
            pass
        except Exception:
            pass


def close_camoufox_context_quiet():
    while _camoufox_cm_holder:
        cm = _camoufox_cm_holder.pop()
        try:
            cm.__exit__(None, None, None)
        except Exception:
            pass


def cleanup_browser_processes(reason="", root_pid=None):
    if reason:
        stage_log("browser_cleanup", reason)
    close_camoufox_context_quiet()
    terminate_descendant_processes(root_pid=root_pid, force=False)
    time.sleep(0.4)
    terminate_descendant_processes(root_pid=root_pid, force=True)


def install_launcher_signal_handlers(root_pid_getter=None):
    def _handler(signum, frame):
        stage_log("interrupted", "signal=%s" % (signum,))
        root = None
        try:
            root = root_pid_getter() if root_pid_getter else None
        except Exception:
            root = None
        cleanup_browser_processes(reason="signal", root_pid=root)
        raise SystemExit(130 if signum == signal.SIGINT else 143)

    signal.signal(signal.SIGINT, _handler)
    signal.signal(signal.SIGTERM, _handler)


def fail_launch(message, *, exit_code=5):
    safe = sanitize_launcher_error_text(message)
    stage_log("browser_spawn_failed", "error=%s" % (safe,))
    write_launch_status(_LAUNCH_STATUS_FAILED)
    print("Camoufox launch failed: %s" % (safe,), flush=True)
    cleanup_browser_processes(reason="launch_failed")
    raise SystemExit(exit_code)


def relay_worker_output(proc, stop_event):
    """Forward sanitized worker stdout/stderr lines to the supervisor terminal."""
    stream = getattr(proc, "stdout", None)
    if stream is None:
        return
    try:
        for raw in iter(stream.readline, ""):
            if stop_event.is_set():
                break
            line = sanitize_launcher_error_text(raw.rstrip("\\n"))
            if line:
                print(line, flush=True)
    except Exception as exc:
        stage_log("worker_log_relay_error", "error=%s" % (sanitize_launcher_error_text(exc),))
`;
}

export const generateCamoufoxProfileScript = ({
  clientId,
  profileName,
  startupUrl = DEFAULT_STARTUP_URL,
  fallbackWidth,
  fallbackHeight,
  fallbackOs,
  width,
  height,
  authorizedQaProxyEnabled = false,
  authorizedQaProxyLaunchConfigUrl,
} = {}) => {
  const safeProfileName = sanitizeBrowserProfileName(profileName);
  const userDataDir = buildBrowserProfilePath({ clientId, profileName: safeProfileName });
  const resolvedStartupUrl = resolveBrowserProfileStartupUrl(startupUrl);
  const safeStartupUrl = isSafeStartupUrl(resolvedStartupUrl)
    ? resolvedStartupUrl
    : DEFAULT_STARTUP_URL;
  const proxyBlock = authorizedQaProxyEnabled
    ? buildAuthorizedQaProxyPythonBlock({
      launchConfigUrl: authorizedQaProxyLaunchConfigUrl,
    })
    : '\nAUTHORIZED_QA_PROXY_ENABLED = False\n';

  const script = `${authorizedQaProxyEnabled ? `${qaLauncherVersionMarker()}
${qaLauncherCompatibilityMarker()}
` : ''}import os
import platform
import sys
${authorizedQaProxyEnabled ? `
QA_LAUNCHER_TEMPLATE_VERSION = "${QA_LAUNCHER_TEMPLATE_VERSION}"
` : ''}
# IMPORTANT: Do NOT import camoufox/browserforge/playwright at module import time.
# The supervisor process must stay free of those native bindings. The worker imports
# them only after stage=worker_started so a hung import is visible and Ctrl+C works.

${buildDeviceSettingsPython({
    fallbackWidth: fallbackWidth || width,
    fallbackHeight: fallbackHeight || height,
    fallbackOs,
  })}${proxyBlock}
def is_safe_startup_url(url):
    if not isinstance(url, str):
        return False
    trimmed = url.strip()
    if not trimmed:
        return False
    if trimmed.startswith("http://") or trimmed.startswith("https://"):
        return True
    return False


def print_geometry_diagnostics(page, logical_width, logical_height, window_width, window_height):
    """Non-mutating diagnostics only — never print tokens or provider credentials."""
    print("Detected logical display: %s x %s" % (logical_width, logical_height))
    print("Chosen browser window: %s x %s" % (window_width, window_height))
    try:
        metrics = page.evaluate(
            """() => ({
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                innerWidth: window.innerWidth,
                innerHeight: window.innerHeight,
                outerWidth: window.outerWidth,
                outerHeight: window.outerHeight,
                devicePixelRatio: window.devicePixelRatio,
            })"""
        )
    except Exception as exc:
        print("Could not read page geometry diagnostics (%s)." % (exc,))
        return
    print("page screen.width / screen.height: %s x %s" % (metrics.get("screenWidth"), metrics.get("screenHeight")))
    print("page innerWidth / innerHeight: %s x %s" % (metrics.get("innerWidth"), metrics.get("innerHeight")))
    print("page outerWidth / outerHeight: %s x %s" % (metrics.get("outerWidth"), metrics.get("outerHeight")))
    print("devicePixelRatio: %s" % (metrics.get("devicePixelRatio"),))
    if metrics.get("screenWidth") in (960, "960") or metrics.get("screenHeight") in (540, "540"):
        print("WARNING: page screen is a fake 960x540 viewport inside the outer window.")
    if metrics.get("innerWidth") and window_width and int(metrics.get("innerWidth") or 0) < int(window_width) * 0.7:
        print("WARNING: inner viewport is much smaller than the outer work-area window.")


PROFILE_PATH = "${escapePythonString(userDataDir)}"
STARTUP_URL = "${escapePythonString(safeStartupUrl)}"

${buildCamoufoxLaunchSafetyPython()}
${authorizedQaProxyEnabled ? `
# Cookies stay on Firefox's own handling; the saved QA profile must never be wiped on exit.
QA_COOKIE_COMPAT_PREFS = {
    "privacy.sanitize.sanitizeOnShutdown": False,
    "privacy.clearOnShutdown.cookies": False,
    "privacy.clearOnShutdown_v2.cookiesAndStorage": False,
}

# Firefox sends loopback traffic around the configured proxy by default; route it to the
# QA gateway so localhost / 127.0.0.0/8 / ::1 are refused like every other private host.
QA_NETWORK_SAFETY_PREFS = {
    "network.proxy.allow_hijacking_localhost": True,
    "network.proxy.no_proxies_on": "",
}

# Same values as Camoufox's defaults, pinned: each QA session starts on a blank page and a
# previous (possibly expired) session's tabs are never reopened under a new proxy identity.
# Cookies and other profile data persist as before.
QA_SESSION_RESTORE_PREFS = {
    "browser.startup.page": 0,
    "browser.sessionstore.resume_from_crash": False,
}

# file://, resource://, chrome://, privileged about: pages and OS-handled schemes (ftp, smb, ...)
# never reach the HTTP gateway, so QA sessions load a parent-process content policy that rejects
# every top-level document that is not http(s) before Firefox opens the underlying resource.
QA_PROTOCOL_GUARD_ADDON_ID = "qa-protocol-guard@fundelite.ai"
QA_PROTOCOL_GUARD_DIR_NAME = "authorized_qa_protocol_guard"
QA_PROTOCOL_GUARD_PREFS = {
    # Profile-local: lets this unsigned add-on register the content policy. Primary Browser
    # profiles never receive this pref or the add-on.
    "extensions.experiments.enabled": True,
}
QA_PROTOCOL_GUARD_SELF_TEST_URL = "file:///fundelite-qa-protocol-guard-self-test/"
QA_PROTOCOL_GUARD_ACTIVATION_TIMEOUT_SECONDS = 20
QA_PROTOCOL_GUARD_BLOCKED_ERROR = "NS_ERROR_BLOCKED_BY_POLICY"
QA_PROTOCOL_GUARD_BLOCK_PAGE_TITLE = "Blocked by Authorized QA Browser security policy"
QA_PROTOCOL_GUARD_SAFE_ABOUT_PAGES = ("blank", "srcdoc", "newtab", "home", "neterror", "certerror")

QA_PROTOCOL_GUARD_API_JS = """"use strict";
const CONTRACT_ID = "@fundelite.ai/qa-protocol-guard;1";
const CLASS_ID = Components.ID("{5f0c6f7e-2d4b-4a8e-9b1a-6c2f3e4d5a61}");
const { setTimeout: guardSetTimeout } = ChromeUtils.importESModule("resource://gre/modules/Timer.sys.mjs");
const BLOCK_PAGE_DELAY_MS = 150;
const SAFE_ABOUT_PAGES = new Set(["blank", "srcdoc", "newtab", "home", "neterror", "certerror"]);
const BLOCK_PAGE_HEAD =
  '<!doctype html><meta charset="utf-8"><title>Blocked by Authorized QA Browser security policy</title>' +
  '<meta name="fundelite-qa-protocol-guard" content="blocked">';
const BLOCK_PAGE_PREFIX = "data:text/html;charset=utf-8," + encodeURIComponent(BLOCK_PAGE_HEAD);

function blockPageUrl(scheme) {
  const body =
    '<body style="font:15px/1.5 -apple-system,Segoe UI,sans-serif;max-width:640px;margin:15vh auto;padding:0 24px;color:#1f2937">' +
    '<h1 style="font-size:20px">Blocked by Authorized QA Browser security policy.</h1>' +
    "<p>Authorized QA Browser sessions can only open public http:// and https:// websites.</p>" +
    "<p>Blocked address type: " + String(scheme || "unknown").slice(0, 32) + ":</p></body>";
  return BLOCK_PAGE_PREFIX + encodeURIComponent(body);
}

function isAllowedTopLevel(uri) {
  const scheme = uri.scheme;
  if (scheme === "http" || scheme === "https") return true;
  if (scheme === "blob") return uri.spec.startsWith("blob:https://") || uri.spec.startsWith("blob:http://");
  if (scheme === "about") return SAFE_ABOUT_PAGES.has(uri.filePath.toLowerCase());
  if (scheme === "data") return uri.spec.startsWith(BLOCK_PAGE_PREFIX);
  return false;
}

function isAllowedSubframe(uri) {
  return isAllowedTopLevel(uri) || uri.scheme === "data" || uri.scheme === "javascript";
}

const guardPolicy = {
  classDescription: "FundElite Authorized QA protocol guard",
  contractID: CONTRACT_ID,
  classID: CLASS_ID,
  QueryInterface: ChromeUtils.generateQI(["nsIContentPolicy", "nsIFactory"]),
  createInstance(iid) {
    return this.QueryInterface(iid);
  },
  shouldLoad(uri, loadInfo) {
    const type = loadInfo.externalContentPolicyType;
    const isTopLevel = type === Ci.nsIContentPolicy.TYPE_DOCUMENT;
    if (!isTopLevel && type !== Ci.nsIContentPolicy.TYPE_SUBDOCUMENT) {
      return Ci.nsIContentPolicy.ACCEPT;
    }
    const bc = loadInfo.browsingContext || loadInfo.targetBrowsingContext;
    if (bc && !bc.isContent) {
      return Ci.nsIContentPolicy.ACCEPT;
    }
    if (isTopLevel ? isAllowedTopLevel(uri) : isAllowedSubframe(uri)) {
      return Ci.nsIContentPolicy.ACCEPT;
    }
    if (isTopLevel && bc) {
      const target = Services.io.newURI(blockPageUrl(uri.scheme));
      // Let the rejected navigation finish as NS_ERROR_BLOCKED_BY_POLICY (the launcher self-test
      // relies on it) before the block page replaces it.
      guardSetTimeout(() => {
        try {
          bc.loadURI(target, { triggeringPrincipal: Services.scriptSecurityManager.getSystemPrincipal() });
        } catch (e) {}
      }, BLOCK_PAGE_DELAY_MS);
    }
    return Ci.nsIContentPolicy.REJECT_POLICY;
  },
  shouldProcess() {
    return Ci.nsIContentPolicy.ACCEPT;
  },
};

this.qaProtocolGuard = class extends ExtensionAPI {
  onStartup() {
    const registrar = Components.manager.QueryInterface(Ci.nsIComponentRegistrar);
    if (!registrar.isContractIDRegistered(CONTRACT_ID)) {
      registrar.registerFactory(CLASS_ID, guardPolicy.classDescription, CONTRACT_ID, guardPolicy);
      Services.catMan.addCategoryEntry("content-policy", CONTRACT_ID, CONTRACT_ID, false, true);
    }
  }
  getAPI() {
    return { qaProtocolGuard: {} };
  }
};
"""


def _qa_protocol_guard_ns_error_token(text):
    """Extract the leading NS_ERROR_* token from an exception string, or None.

    NS_ERROR_* constants are fixed Firefox symbols (no secrets, paths, or URLs),
    so they are safe to log verbatim."""
    marker = "NS_ERROR_"
    upper = str(text or "").upper()
    idx = upper.find(marker)
    if idx < 0:
        return None
    token = marker
    for ch in upper[idx + len(marker):]:
        if ch.isalnum() or ch == "_":
            token += ch
        else:
            break
    return token


def classify_qa_protocol_guard_probe_outcome(text):
    """Classify a self-test probe result into a safe (category, error_token) pair.

    Only fixed Firefox symbols and fixed labels are returned — never the raw
    exception text (which could contain URLs, paths, or other sensitive data)."""
    raw = str(text or "")
    upper = raw.upper()
    if "NS_ERROR_BLOCKED_BY_POLICY" in upper:
        return "blocked_by_policy", "NS_ERROR_BLOCKED_BY_POLICY"
    if "NS_ERROR_FILE_NOT_FOUND" in upper:
        return "file_not_found", "NS_ERROR_FILE_NOT_FOUND"
    if "NS_BINDING_ABORTED" in upper:
        return "navigation_aborted", "NS_BINDING_ABORTED"
    ns_token = _qa_protocol_guard_ns_error_token(raw)
    if ns_token:
        return "other_ns_error", ns_token
    if "TIMEOUT" in upper:
        return "timeout", "TimeoutError"
    if raw == "loaded":
        return "loaded", "loaded"
    return "playwright_error", "<playwright_error>"


def write_qa_protocol_guard_addon(profile_path):
    """Rewritten on every launch so an edited or stale copy can never weaken the guard."""
    import json

    stage_log("qa_protocol_guard_install_start")
    addon_dir = os.path.join(os.path.dirname(os.path.abspath(profile_path)), QA_PROTOCOL_GUARD_DIR_NAME)
    try:
        os.makedirs(addon_dir, exist_ok=True)
        manifest = {
            "manifest_version": 2,
            "name": "FundElite Authorized QA protocol guard",
            "version": "1.0",
            "browser_specific_settings": {"gecko": {"id": QA_PROTOCOL_GUARD_ADDON_ID}},
            "experiment_apis": {
                "qaProtocolGuard": {
                    "schema": "schema.json",
                    "parent": {
                        "scopes": ["addon_parent"],
                        "script": "api.js",
                        "events": ["startup"],
                        "paths": [["qaProtocolGuard"]],
                    },
                }
            },
        }
        files = {
            "manifest.json": json.dumps(manifest, indent=2),
            "schema.json": json.dumps([{"namespace": "qaProtocolGuard", "functions": []}]),
            "api.js": QA_PROTOCOL_GUARD_API_JS,
        }
        for name, body in files.items():
            with open(os.path.join(addon_dir, name), "w", encoding="utf-8") as fh:
                fh.write(body)
    except Exception as exc:
        stage_log("qa_protocol_guard_install_failed", "reason=%s" % (type(exc).__name__,))
        raise
    stage_log(
        "qa_protocol_guard_install_complete",
        "addon_path_exists=%s manifest_exists=%s api_script_exists=%s"
        % (
            "1" if os.path.isdir(addon_dir) else "0",
            "1" if os.path.isfile(os.path.join(addon_dir, "manifest.json")) else "0",
            "1" if os.path.isfile(os.path.join(addon_dir, "api.js")) else "0",
        ),
    )
    return addon_dir


def qa_navigation_url_allowed(url):
    """Launcher-side mirror of the content policy's top-level allowlist."""
    text = str(url or "").strip()
    lowered = text.lower()
    if lowered.startswith("http://") or lowered.startswith("https://"):
        return True
    if lowered.startswith("blob:http://") or lowered.startswith("blob:https://"):
        return True
    if lowered.startswith("about:"):
        page_name = lowered[len("about:"):].split("?", 1)[0].split("#", 1)[0]
        return page_name in QA_PROTOCOL_GUARD_SAFE_ABOUT_PAGES
    if lowered.startswith("data:text/html"):
        return QA_PROTOCOL_GUARD_BLOCK_PAGE_TITLE.lower().replace(" ", "%20") in lowered
    return text == ""


def activate_qa_protocol_guard(browser):
    """Fail closed unless Firefox rejects a local file:// load by policy before any disk access.

    The self-test path does not exist, so even an inactive guard can never read a real file.
    Camoufox starts add-on experiments when a content tab is created, hence the probe tab."""
    stage_log("qa_protocol_guard_activation_start")
    stage_log("qa_protocol_guard_verification_start")
    deadline = time.time() + QA_PROTOCOL_GUARD_ACTIVATION_TIMEOUT_SECONDS
    active = False
    attempts = 0
    last_category = None
    last_error_token = None
    probe_page = browser.new_page()
    try:
        while time.time() < deadline:
            attempts += 1
            stage_log("qa_protocol_guard_verification_attempt", "attempt=%s" % (attempts,))
            try:
                probe_page.goto(QA_PROTOCOL_GUARD_SELF_TEST_URL, timeout=3000)
                outcome = "loaded"
            except Exception as exc:
                outcome = str(exc)
            last_category, last_error_token = classify_qa_protocol_guard_probe_outcome(outcome)
            stage_log(
                "qa_protocol_guard_verification_attempt_result",
                "attempt=%s outcome=%s error_token=%s"
                % (attempts, last_category, last_error_token),
            )
            if QA_PROTOCOL_GUARD_BLOCKED_ERROR in outcome:
                active = True
                break
            probe_page.wait_for_timeout(200)
    finally:
        try:
            probe_page.close()
        except Exception:
            pass
    if not active:
        stage_log(
            "qa_protocol_guard_verification_failed",
            "attempts=%s last_outcome=%s last_error_token=%s"
            % (attempts, last_category or "unknown", last_error_token or "-"),
        )
        stage_log("qa_protocol_guard_activation_failed")
        raise RuntimeError(
            "Authorized QA Browser protocol guard could not be verified; the QA session was closed."
        )
    stage_log(
        "qa_protocol_guard_verification_passed",
        "attempt=%s outcome=blocked_by_policy" % (attempts,),
    )
    try:
        pages = list(browser.pages)
    except Exception:
        pages = []
    for existing_page in pages:
        if not qa_navigation_url_allowed(getattr(existing_page, "url", "")):
            try:
                existing_page.close()
            except Exception:
                pass
    stage_log("qa_protocol_guard_active")
    print("Local files and internal browser pages: blocked (only http:// and https:// allowed)", flush=True)


def qa_excluded_default_addons():
    """Camoufox's bundled uBlock Origin skips redirect/cookie-sync hops ($urlskip) and
    hosts-blocks redirect domains, so legitimate cookie handshakes never complete.
    Network safety for QA sessions is enforced by the local gateway, not by an ad blocker."""
    try:
        from camoufox.addons import DefaultAddons
    except Exception:
        return None
    return [DefaultAddons.UBO]


def print_qa_cookie_session_diagnostics(page, profile_path, proxy_enabled, private_network_protection, content_blocker_off):
    """Cookie/session posture only — never prints cookie values or credentials."""
    try:
        cookies_enabled = bool(page.evaluate("() => navigator.cookieEnabled"))
    except Exception:
        cookies_enabled = None
    if cookies_enabled is None:
        cookie_mode = "unknown"
    else:
        cookie_mode = "enabled" if cookies_enabled else "DISABLED"
    print("Cookie mode: %s" % (cookie_mode,), flush=True)
    print("Persistent profile: %s" % (os.path.abspath(profile_path),), flush=True)
    print("QA proxy: %s" % ("enabled" if proxy_enabled else "disabled"), flush=True)
    print(
        "Private network protection: %s" % ("enabled" if private_network_protection else "disabled"),
        flush=True,
    )
    print(
        "Content blocker extension: %s" % ("off" if content_blocker_off else "Camoufox default"),
        flush=True,
    )


def resolve_proxy_exit_ip(proxy):
    """Resolve proxy exit IP with a hard timeout so Camoufox never blocks in public_ip()."""
    import re
    import urllib.request

    if not isinstance(proxy, dict):
        raise ValueError("Proxy configuration is missing")
    server = str(proxy.get("server") or "").strip()
    if not server:
        raise ValueError("Proxy server is missing")
    username = proxy.get("username")
    password = proxy.get("password")
    if password:
        _register_secret_redaction(password)
    if username:
        _register_secret_redaction(username)

    proxy_url = server
    if username or password:
        from urllib.parse import urlsplit, urlunsplit

        parts = urlsplit(server)
        auth = "%s:%s" % (username or "", password or "")
        netloc = "%s@%s" % (auth, parts.netloc)
        proxy_url = urlunsplit((parts.scheme, netloc, parts.path, parts.query, parts.fragment))

    # Use the same verified CA bundle as token redemption (macOS Python often lacks system CAs).
    ssl_context = build_verified_tls_context()
    handlers = [
        urllib.request.ProxyHandler({"http": proxy_url, "https": proxy_url}),
        urllib.request.HTTPSHandler(context=ssl_context),
    ]
    opener = urllib.request.build_opener(*handlers)
    ip_endpoints = (
        "https://api.ipify.org",
        "https://checkip.amazonaws.com",
        "https://icanhazip.com",
    )
    last_error = None
    for endpoint in ip_endpoints:
        try:
            with opener.open(endpoint, timeout=8) as response:
                ip = response.read().decode("utf-8", errors="replace").strip()
            if re.match(r"^(?:\\d{1,3}\\.){3}\\d{1,3}$", ip) or ":" in ip:
                return ip
            last_error = ValueError("Unexpected IP payload")
        except Exception as exc:
            last_error = exc
    raise RuntimeError(
        "Timed out or failed resolving proxy exit IP for GeoIP (%s)"
        % (sanitize_launcher_error_text(last_error),)
    )


def lookup_exit_ip_location(ip_address):
    """Resolve public geo for an exit IP. Never logs secrets."""
    import json
    import urllib.request

    ip = str(ip_address or "").strip()
    if not ip:
        raise ValueError("Exit IP is missing")
    ssl_context = build_verified_tls_context()
    endpoints = (
        "https://ipapi.co/%s/json/" % (ip,),
        "https://ipinfo.io/%s/json" % (ip,),
    )
    last_error = None
    for endpoint in endpoints:
        try:
            req = urllib.request.Request(endpoint, headers={"User-Agent": "FundElite-QA-Launcher/1.0"})
            with urllib.request.urlopen(req, timeout=10, context=ssl_context) as response:
                payload = json.loads(response.read().decode("utf-8", errors="replace"))
            if not isinstance(payload, dict):
                continue
            country = (
                payload.get("country_code")
                or payload.get("country")
                or payload.get("countryCode")
                or ""
            )
            country = str(country).strip().upper()
            if len(country) > 2:
                # ipinfo uses "US" already; keep first two if longer
                country = country[:2]
            region = str(
                payload.get("region")
                or payload.get("regionName")
                or payload.get("state")
                or ""
            ).strip()
            city = str(payload.get("city") or "").strip()
            timezone = str(payload.get("timezone") or "").strip()
            # ipapi: latitude/longitude; ipinfo: loc as "lat,lon"
            latitude = None
            longitude = None
            raw_lat = payload.get("latitude")
            raw_lon = payload.get("longitude")
            if raw_lat is not None and raw_lon is not None:
                try:
                    latitude = float(raw_lat)
                    longitude = float(raw_lon)
                except (TypeError, ValueError):
                    latitude = None
                    longitude = None
            if latitude is None or longitude is None:
                loc = str(payload.get("loc") or "").strip()
                if "," in loc:
                    parts = loc.split(",", 1)
                    try:
                        latitude = float(parts[0].strip())
                        longitude = float(parts[1].strip())
                    except (TypeError, ValueError):
                        latitude = None
                        longitude = None
            if country:
                result = {
                    "ip": ip,
                    "country": country,
                    "region": region,
                    "city": city,
                    "timezone": timezone or None,
                    "postal": str(payload.get("postal") or "").strip() or None,
                }
                if latitude is not None and longitude is not None:
                    result["latitude"] = latitude
                    result["longitude"] = longitude
                return result
        except Exception as exc:
            last_error = exc
    raise RuntimeError(
        "Could not verify exit IP location (%s)"
        % (sanitize_launcher_error_text(last_error),)
    )


def evaluate_qa_location_match(targeting, exit_geo):
    """Canonical geo match for QA launch/preflight. Mirrors backend semantics.

    Returns status: matched | compatible | unverified | mismatch
    California target + Colorado exit → mismatch (never compatible).
    """
    targeting = targeting or {}
    exit_geo = exit_geo or {}
    if not exit_geo:
        return "unverified"
    # Treat missing available flag as available when country/region present.
    if exit_geo.get("available") is False and not (
        exit_geo.get("country") or exit_geo.get("region") or exit_geo.get("city")
    ):
        return "unverified"

    sentinel = {
        "all", "all_states", "all_cities", "all_zips", "all_asns",
        "all states", "all cities", "all zips", "all asns",
        "__all_states__", "__all_cities__", "__all_zips__", "__all_asns__", "",
    }

    def _specific(value):
        raw = str(value or "").strip().lower()
        if raw in sentinel:
            return False
        normalized = " ".join(
            str(value or "").strip().lower().replace("_", " ").replace("-", " ").split()
        )
        if not normalized or normalized in sentinel:
            return False
        return True

    def _norm_country(value):
        return str(value or "").strip().lower()[:2]

    def _norm_region(value):
        text = str(value or "").strip().lower().replace("_", " ").replace("-", " ")
        return " ".join(text.split())

    _US_STATE_ALIASES = {
        "al": "alabama", "ak": "alaska", "az": "arizona", "ar": "arkansas",
        "ca": "california", "co": "colorado", "ct": "connecticut", "de": "delaware",
        "fl": "florida", "ga": "georgia", "hi": "hawaii", "id": "idaho",
        "il": "illinois", "in": "indiana", "ia": "iowa", "ks": "kansas",
        "ky": "kentucky", "la": "louisiana", "me": "maine", "md": "maryland",
        "ma": "massachusetts", "mi": "michigan", "mn": "minnesota", "ms": "mississippi",
        "mo": "missouri", "mt": "montana", "ne": "nebraska", "nv": "nevada",
        "nh": "new hampshire", "nj": "new jersey", "nm": "new mexico", "ny": "new york",
        "nc": "north carolina", "nd": "north dakota", "oh": "ohio", "ok": "oklahoma",
        "or": "oregon", "pa": "pennsylvania", "ri": "rhode island", "sc": "south carolina",
        "sd": "south dakota", "tn": "tennessee", "tx": "texas", "ut": "utah",
        "vt": "vermont", "va": "virginia", "wa": "washington", "wv": "west virginia",
        "wi": "wisconsin", "wy": "wyoming", "dc": "district of columbia",
    }

    def _canonical_us_region(value):
        region = _norm_region(value)
        if not region:
            return ""
        return _US_STATE_ALIASES.get(region, region)

    def _regions_agree(left, right):
        a = _canonical_us_region(left)
        b = _canonical_us_region(right)
        if not a or not b:
            return False
        return a == b or a in b or b in a

    target_country = _norm_country(targeting.get("country") or "us")
    exit_country = _norm_country(exit_geo.get("country"))
    if target_country and exit_country and target_country != exit_country:
        return "mismatch"

    target_state = _norm_region(targeting.get("state") or targeting.get("region"))
    exit_region = _norm_region(exit_geo.get("region") or exit_geo.get("state"))
    state_requested = _specific(target_state)
    if state_requested and exit_region and not _regions_agree(target_state, exit_region):
        return "mismatch"

    target_zip = str(targeting.get("zip") or "").strip()
    exit_postal = str(exit_geo.get("postal") or "").strip()
    zip_requested = _specific(target_zip)
    if zip_requested and exit_postal and target_zip == exit_postal:
        return "matched"

    target_city = _norm_region(targeting.get("city"))
    exit_city = _norm_region(exit_geo.get("city"))
    city_requested = _specific(target_city)
    if city_requested and exit_city and target_city == exit_city:
        return "matched"

    if state_requested and exit_region and _regions_agree(target_state, exit_region):
        return "compatible"
    if state_requested and not exit_region:
        return "unverified"
    if city_requested or zip_requested:
        if exit_country and target_country and exit_country == target_country:
            return "mismatch"
        return "unverified"
    if target_country and exit_country and target_country == exit_country:
        return "compatible"
    return "unverified"


def format_qa_location_label(parts):
    bits = []
    for key in ("city", "state", "region", "zip", "postal", "country"):
        value = (parts or {}).get(key)
        if not value:
            continue
        text = str(value).strip()
        low = text.lower()
        if low in {
            "all", "all_states", "all_cities", "all_zips",
            "__all_states__", "__all_cities__", "__all_zips__", "",
        }:
            continue
        if key in {"zip", "postal"}:
            bits.append("ZIP %s" % (text,))
        elif key == "country":
            bits.append(text.upper())
        else:
            bits.append(text)
    return ", ".join(bits) if bits else "unknown"


def locale_bundle_for_exit_location(location):
    """
    Derive timezone/locale/languages/geolocation from verified proxy-exit location only.
    Computer/VPN identity never feeds this — only the resolved exit IP geo lookup.
    Prefer verified timezone and lat/lon from the lookup. Never fabricate capital-city
    coordinates (e.g. no DC for US, no London for GB). If lat/lon are missing, omit
    geolocation rather than inventing coords.
    """
    location = location or {}
    country = str(location.get("country") or "").upper()
    verified_timezone = str(location.get("timezone") or "").strip() or None
    region = str(location.get("region") or location.get("state") or "").strip().lower()
    region = region.replace("_", " ").replace("-", " ")
    # Prefer region-accurate US timezones over a generic East-coast fallback.
    us_region_timezones = {
        "california": "America/Los_Angeles",
        "oregon": "America/Los_Angeles",
        "washington": "America/Los_Angeles",
        "nevada": "America/Los_Angeles",
        "arizona": "America/Phoenix",
        "colorado": "America/Denver",
        "utah": "America/Denver",
        "new mexico": "America/Denver",
        "wyoming": "America/Denver",
        "montana": "America/Denver",
        "idaho": "America/Boise",
        "alaska": "America/Anchorage",
        "hawaii": "Pacific/Honolulu",
        "texas": "America/Chicago",
        "illinois": "America/Chicago",
        "new york": "America/New_York",
        "florida": "America/New_York",
        "georgia": "America/New_York",
        "pennsylvania": "America/New_York",
        "massachusetts": "America/New_York",
        "virginia": "America/New_York",
        "district of columbia": "America/New_York",
    }
    region_timezone = None
    for key, tz in us_region_timezones.items():
        if key in region:
            region_timezone = tz
            break
    locale_by_country = {
        "US": {
            "locale": "en-US",
            "languages": ["en-US", "en"],
            "timezone_fallback": "America/New_York",
        },
        "GB": {
            "locale": "en-GB",
            "languages": ["en-GB", "en"],
            "timezone_fallback": "Europe/London",
        },
        "DE": {
            "locale": "de-DE",
            "languages": ["de-DE", "de"],
            "timezone_fallback": "Europe/Berlin",
        },
        "FR": {
            "locale": "fr-FR",
            "languages": ["fr-FR", "fr"],
            "timezone_fallback": "Europe/Paris",
        },
        "ES": {
            "locale": "es-ES",
            "languages": ["es-ES", "es"],
            "timezone_fallback": "Europe/Madrid",
        },
        "BR": {
            "locale": "pt-BR",
            "languages": ["pt-BR", "pt"],
            "timezone_fallback": "America/Sao_Paulo",
        },
        "JP": {
            "locale": "ja-JP",
            "languages": ["ja-JP", "ja"],
            "timezone_fallback": "Asia/Tokyo",
        },
        "AU": {
            "locale": "en-AU",
            "languages": ["en-AU", "en"],
            "timezone_fallback": "Australia/Sydney",
        },
    }
    base = dict(locale_by_country.get(country) or {
        "locale": "en-US",
        "languages": ["en-US", "en"],
        "timezone_fallback": "UTC",
    })
    bundle = {
        "locale": base["locale"],
        "languages": list(base["languages"]),
        "timezone": verified_timezone or region_timezone or base["timezone_fallback"],
        "country": country or "US",
    }
    lat = location.get("latitude")
    lon = location.get("longitude")
    try:
        if lat is not None and lon is not None:
            lat_f = float(lat)
            lon_f = float(lon)
            bundle["geolocation"] = {"latitude": lat_f, "longitude": lon_f}
    except (TypeError, ValueError):
        pass
    return bundle


def apply_verified_exit_locale(context, location):
    """Force context locale/timezone from verified exit; geolocation only when lat/lon real."""
    bundle = locale_bundle_for_exit_location(location)
    try:
        context.set_extra_http_headers({"Accept-Language": ",".join(bundle["languages"])})
    except Exception:
        pass
    geo = bundle.get("geolocation") if isinstance(bundle.get("geolocation"), dict) else None
    if geo and geo.get("latitude") is not None and geo.get("longitude") is not None:
        try:
            if hasattr(context, "set_geolocation"):
                context.set_geolocation({
                    "latitude": float(geo["latitude"]),
                    "longitude": float(geo["longitude"]),
                    "accuracy": 50,
                })
            if hasattr(context, "grant_permissions"):
                context.grant_permissions(["geolocation"])
        except Exception as exc:
            print("Could not apply verified geolocation (%s)." % (sanitize_launcher_error_text(exc),))
    # else: skip geolocation entirely — do not set wrong city
    # Camoufox/Playwright locale + timezone when supported on the context.
    for setter_name, value in (
        ("set_locale", bundle["locale"]),
        ("set_timezone_id", bundle["timezone"]),
    ):
        setter = getattr(context, setter_name, None)
        if callable(setter):
            try:
                setter(value)
            except Exception:
                pass
    return bundle


def observe_browser_public_ip(page, timeout_ms=15000):
    """Fetch public IP from inside the live Camoufox page (uses browser proxy path)."""
    import json
    import re

    endpoints = (
        "https://api.ipify.org?format=json",
        "https://checkip.amazonaws.com",
        "https://icanhazip.com",
    )
    last_error = None
    ip_re = re.compile(r"^(?:\\d{1,3}\\.){3}\\d{1,3}$")
    for endpoint in endpoints:
        try:
            result = page.evaluate(
                """async ({ url, timeoutMs }) => {
                    const controller = new AbortController();
                    const timer = setTimeout(() => controller.abort(), timeoutMs);
                    try {
                        const response = await fetch(url, {
                            signal: controller.signal,
                            cache: "no-store",
                        });
                        const text = (await response.text() || "").trim();
                        return { ok: !!response.ok, text: text };
                    } finally {
                        clearTimeout(timer);
                    }
                }""",
                {"url": endpoint, "timeoutMs": int(timeout_ms)},
            )
            if not isinstance(result, dict) or not result.get("ok"):
                last_error = ValueError("IP fetch failed for %s" % (endpoint,))
                continue
            text = str(result.get("text") or "").strip()
            ip = text
            if text.startswith("{"):
                try:
                    payload = json.loads(text)
                    ip = str((payload or {}).get("ip") or "").strip()
                except Exception:
                    last_error = ValueError("Invalid JSON IP payload")
                    continue
            ip = ip.split()[0] if ip else ""
            if ip_re.match(ip) or (":" in ip and len(ip) >= 3):
                return ip
            last_error = ValueError("Unexpected IP payload")
        except Exception as exc:
            last_error = exc
    raise RuntimeError(
        "Could not observe browser public IP (%s)"
        % (sanitize_launcher_error_text(last_error),)
    )


# 911Proxy endpoints accept CONNECT at once but sometimes deliver the first response byte
# only after ~20-21s (observed on real pages and IP checks), and some connections never
# answer. Each round races the hosts on fresh connections through the same endpoint; a
# later round only follows a round with no IP at all. Bounded: rounds x (deadline + guard).
QA_EXACT_EXIT_VERIFY_URLS = (
    "https://api.ipify.org?format=json",
    "https://checkip.amazonaws.com",
    "https://icanhazip.com",
)
QA_EXACT_EXIT_VERIFY_ROUND_DEADLINE_MS = 25000
QA_EXACT_EXIT_VERIFY_MAX_ROUNDS = 2
QA_EXACT_EXIT_VERIFY_FETCH_JS = """async ({ urls, deadlineMs }) => {
    const started = Date.now();
    const controllers = urls.map(() => new AbortController());
    const attempts = urls.map((url) => ({ url: url, outcome: "pending", status: 0, headersMs: null, elapsedMs: null, ip: "" }));
    let timedOut = false;
    const timer = setTimeout(() => {
        timedOut = true;
        controllers.forEach((c) => c.abort());
    }, deadlineMs);
    const extractIp = (text) => {
        let ip = (text || "").trim();
        if (ip.startsWith("{")) {
            try {
                ip = String((JSON.parse(ip) || {}).ip || "").trim();
            } catch (e) {
                return "";
            }
        }
        if (ip.length > 45) return "";
        return /^[0-9.]+$/.test(ip) || (ip.indexOf(":") >= 0 && /^[0-9a-fA-F:.]+$/.test(ip)) ? ip : "";
    };
    const run = async (i) => {
        const attempt = attempts[i];
        try {
            const response = await fetch(attempt.url, { signal: controllers[i].signal, cache: "no-store" });
            attempt.status = response.status;
            attempt.headersMs = Date.now() - started;
            const text = await response.text();
            attempt.elapsedMs = Date.now() - started;
            if (!response.ok) {
                attempt.outcome = "http_error";
                return false;
            }
            attempt.ip = extractIp(text);
            attempt.outcome = attempt.ip ? "ip" : "bad_body";
            return !!attempt.ip;
        } catch (err) {
            attempt.elapsedMs = Date.now() - started;
            const aborted = !!err && err.name === "AbortError";
            attempt.outcome = aborted ? (timedOut ? "timeout" : "aborted") : "network_error";
            return false;
        }
    };
    try {
        const winner = await Promise.race([
            new Promise((resolve) => {
                let pending = urls.length;
                urls.forEach((_, i) => run(i).then((ok) => {
                    if (ok) resolve(i);
                    else if (--pending === 0) resolve(-1);
                }));
            }),
            new Promise((resolve) => setTimeout(() => resolve(-1), deadlineMs + 2000)),
        ]);
        return {
            winner: winner,
            timedOut: timedOut,
            elapsedMs: Date.now() - started,
            attempts: attempts.map((a) => Object.assign({}, a, { outcome: a.outcome === "pending" ? "not_needed" : a.outcome })),
        };
    } finally {
        clearTimeout(timer);
        controllers.forEach((c) => c.abort());
    }
}"""


def _qa_page_closed(page):
    try:
        return bool(page.is_closed())
    except Exception:
        return False


def observe_exact_match_browser_public_ip(
    page,
    round_deadline_ms=QA_EXACT_EXIT_VERIFY_ROUND_DEADLINE_MS,
    max_rounds=QA_EXACT_EXIT_VERIFY_MAX_ROUNDS,
):
    """Browser-visible public IP for exact-match providers, observed inside the live page.

    The first host to return an IP-shaped body decides; the caller still requires an exact
    match, so a different IP fails at once and is never retried. Only a round in which no
    host returned an IP (timeout, abort, HTTP error, malformed body) leads to another round.
    A closed page or any page.evaluate error fails immediately. Logs carry hosts, outcomes
    and timings only — never response bodies.
    """
    import ipaddress
    from urllib.parse import urlsplit

    last_error = "no rounds attempted"
    for round_no in range(1, max(1, int(max_rounds)) + 1):
        if _qa_page_closed(page):
            stage_log("qa_browser_ip_probe", "round=%s outcome=page_closed" % (round_no,))
            raise RuntimeError("Could not observe browser public IP (page closed)")
        try:
            result = page.evaluate(
                QA_EXACT_EXIT_VERIFY_FETCH_JS,
                {"urls": list(QA_EXACT_EXIT_VERIFY_URLS), "deadlineMs": int(round_deadline_ms)},
            )
        except Exception as exc:
            stage_log(
                "qa_browser_ip_probe",
                "round=%s outcome=evaluate_error page_closed=%s error=%s"
                % (round_no, "yes" if _qa_page_closed(page) else "no", sanitize_launcher_error_text(exc)),
            )
            raise RuntimeError(
                "Could not observe browser public IP (%s)" % (sanitize_launcher_error_text(exc),)
            )
        result = result if isinstance(result, dict) else {}
        attempts = [a for a in (result.get("attempts") or []) if isinstance(a, dict)]
        for attempt in attempts:
            stage_log(
                "qa_browser_ip_probe",
                "round=%s host=%s outcome=%s http_status=%s headers_ms=%s elapsed_ms=%s deadline_ms=%s"
                % (
                    round_no,
                    urlsplit(str(attempt.get("url") or "")).hostname or "-",
                    attempt.get("outcome") or "unknown",
                    attempt.get("status") or "-",
                    "-" if attempt.get("headersMs") is None else attempt.get("headersMs"),
                    "-" if attempt.get("elapsedMs") is None else attempt.get("elapsedMs"),
                    int(round_deadline_ms),
                ),
            )
        winner = result.get("winner")
        if isinstance(winner, int) and not isinstance(winner, bool) and 0 <= winner < len(attempts):
            candidate = str(attempts[winner].get("ip") or "").strip()
            try:
                ipaddress.ip_address(candidate)
                return candidate
            except ValueError:
                last_error = "unexpected IP payload"
                continue
        outcomes = sorted(set(str(a.get("outcome") or "unknown") for a in attempts)) or ["no_result"]
        last_error = "no IP within %s ms; outcomes=%s" % (int(round_deadline_ms), ",".join(outcomes))
    raise RuntimeError("Could not observe browser public IP (%s)" % (last_error,))


QA_EXACT_EXIT_MATCH_PROVIDERS = {"911proxy"}


def verify_qa_browser_exit_ip(page, expected_exit_ip, requested_targeting, preflight_location):
    """
    Fail-closed verification that the launched browser traverses the QA proxy
    AND the observed exit satisfies the requested QA location.

    Never use client_direct_ip for geoip/fingerprint.
    FUNDELITE_QA_COMPARE_DIRECT_IP is proof-only (inequality check).

    Residential IP rotation is expected:
      preflight IP A + browser IP B with target-compatible geo → PASS (compatible)

    Geo mismatch is NOT a proxy-path failure:
      Target California + observed Colorado → FAIL location mismatch
      (truthful error text; do not claim the browser skipped the proxy)
    """
    expected = str(expected_exit_ip or "").strip()
    compare_direct = str(os.environ.get("FUNDELITE_QA_COMPARE_DIRECT_IP") or "").strip()

    def _fail_proxy_path(extra=None):
        if extra:
            print(sanitize_launcher_error_text(extra))
        print("QA Browser proxy verification failed.")
        print("The browser is not exiting through the expected QA proxy.")
        print("Session closed for safety.")
        print_qa_relaunch_guidance(
            "FundElite QA Session Ended Safely",
            [
                "We could not verify that the browser was using",
                "the expected QA proxy IP.",
                "",
                "For your protection, this QA Browser was closed.",
            ],
            "To try again:",
        )
        raise SystemExit(6)

    def _fail_location_mismatch(observed_ip, observed_location):
        requested_label = format_qa_location_label(requested_targeting)
        observed_label = format_qa_location_label(observed_location) or format_qa_location_label(
            preflight_location
        )
        stage_log(
            "qa_browser_location_mismatch",
            "requested=%s observed=%s"
            % (requested_label.replace(" ", "_"), observed_label.replace(" ", "_")),
        )
        print("QA Browser location verification failed.")
        print("Requested: %s" % (requested_label,))
        print("Observed proxy exit: %s" % (observed_label,))
        if observed_ip:
            print("Observed exit IP: %s" % (observed_ip,))
        print(
            "The QA session was closed because the proxy exit does not satisfy "
            "the requested location."
        )
        raise SystemExit(6)

    if not expected:
        stage_log("qa_browser_exit_verification", "expected= observed= matched=false")
        _fail_proxy_path()

    try:
        if QA_UPSTREAM_PROVIDER["id"] in QA_EXACT_EXIT_MATCH_PROVIDERS:
            observed = observe_exact_match_browser_public_ip(page)
        else:
            observed = observe_browser_public_ip(page)
    except Exception as exc:
        stage_log(
            "qa_browser_exit_verification",
            "expected=%s observed= matched=false error=%s"
            % (expected, sanitize_launcher_error_text(exc)),
        )
        _fail_proxy_path(exc)

    observed = str(observed or "").strip()
    if compare_direct and observed and observed == compare_direct:
        stage_log(
            "qa_browser_exit_verification",
            "expected=%s observed=%s matched=false reason=matches_direct_ip"
            % (expected, observed),
        )
        print(
            "qa_browser_exit_verification expected=%s observed=%s matched=false reason=matches_direct_ip"
            % (expected, observed)
        )
        _fail_proxy_path(
            "Browser public IP matches the computer/direct IP — QA proxy path was not used."
        )

    if QA_UPSTREAM_PROVIDER["id"] in QA_EXACT_EXIT_MATCH_PROVIDERS and observed != expected:
        # One fixed endpoint per session: a different browser exit is not this endpoint.
        stage_log(
            "qa_browser_exit_verification",
            "expected=%s observed=%s matched=false reason=exit_ip_changed"
            % (expected, observed or "unknown"),
        )
        _fail_proxy_path()

    observed_location = None
    try:
        observed_location = lookup_exit_ip_location(observed)
    except Exception as exc:
        print(
            "QA Browser exit IP lookup failed (%s)."
            % (sanitize_launcher_error_text(exc),)
        )
        if observed == expected and preflight_location:
            observed_location = dict(preflight_location)
            observed_location["available"] = True
        else:
            stage_log(
                "qa_browser_exit_verification",
                "expected=%s observed=%s matched=false reason=geo_unverified"
                % (expected, observed),
            )
            _fail_proxy_path(exc)

    if isinstance(observed_location, dict) and "available" not in observed_location:
        observed_location = dict(observed_location)
        observed_location["available"] = True

    geo_status = evaluate_qa_location_match(requested_targeting, observed_location)
    if geo_status == "mismatch":
        stage_log(
            "qa_browser_exit_verification",
            "expected=%s observed=%s matched=false reason=location_mismatch"
            % (expected, observed),
        )
        print(
            "qa_browser_exit_verification expected=%s observed=%s matched=false reason=location_mismatch"
            % (expected, observed)
        )
        _fail_location_mismatch(observed, observed_location)

    if observed and observed == expected:
        match_status = "true" if geo_status == "matched" else (
            "compatible" if geo_status == "compatible" else "true"
        )
    elif geo_status in {"matched", "compatible"}:
        match_status = "compatible"
    else:
        # Rotated IP with unverified geo: only accept when preflight was already compatible.
        pre_status = evaluate_qa_location_match(
            requested_targeting,
            dict(preflight_location or {}, available=True) if preflight_location else {},
        )
        match_status = "compatible" if pre_status in {"matched", "compatible"} else "false"

    stage_log(
        "qa_browser_exit_verification",
        "expected=%s observed=%s matched=%s"
        % (expected, observed or "unknown", match_status),
    )
    print(
        "qa_browser_exit_verification expected=%s observed=%s matched=%s"
        % (expected, observed or "unknown", match_status)
    )
    if match_status == "false":
        _fail_proxy_path()
    return {
        "observed_ip": observed,
        "exact": bool(observed) and observed == expected,
        "location": observed_location if isinstance(observed_location, dict) else {},
    }



def print_verified_qa_exit_location(requested, verified, bundle):
    """Show requested vs verified exit location without secrets."""
    print("QA Test Location status:")
    print(
        "  requested: country=%s state=%s city=%s zip=%s"
        % (
            (requested or {}).get("country"),
            (requested or {}).get("state"),
            (requested or {}).get("city"),
            (requested or {}).get("zip"),
        )
    )
    print(
        "  verified_exit: ip=%s country=%s region=%s city=%s"
        % (
            (verified or {}).get("ip"),
            (verified or {}).get("country"),
            (verified or {}).get("region"),
            (verified or {}).get("city"),
        )
    )
    print(
        "  browser_locale_bundle: timezone=%s locale=%s languages=%s"
        % (
            (bundle or {}).get("timezone"),
            (bundle or {}).get("locale"),
            ",".join((bundle or {}).get("languages") or []),
        )
    )
    req_country = str((requested or {}).get("country") or "").lower()
    ver_country = str((verified or {}).get("country") or "").lower()
    if req_country and ver_country and req_country != ver_country:
        print(
            "WARNING: Requested country %s was not honored. Using verified exit country %s."
            % (req_country, ver_country)
        )


def navigate_startup_keep_open(page, startup_url, timeout_ms=QA_PROXY_STARTUP_NAVIGATION_TIMEOUT_MS):
    """Navigate to startup URL; on timeout keep the browser open with a sanitized warning."""
    try:
        started = time.time()
        page.goto(startup_url, wait_until="domcontentloaded", timeout=timeout_ms)
        stage_log("startup_navigation_complete", "url_host=%s elapsed_ms=%s" % (_normalize_hostname(startup_url), int((time.time() - started) * 1000)))
        inspect_startup_load(page, started)
        return True
    except Exception as exc:
        safe = sanitize_launcher_error_text(exc)
        stage_log("startup_navigation_timeout", "error=%s" % (safe,))
        print(
            "WARNING: FundElite startup navigation timed out or failed (%s). "
            "Keeping the QA browser window open."
            % (safe,),
            flush=True,
        )
        return False


def enter_qa_manual_operator_mode(browser):
    """Hand the verified session to the operator. From here the launcher only observes:
    no navigation, probe tabs, page.evaluate, reloads, clicks or form input. Routing,
    private-network blocks, keepalive and passive request/tunnel logging stay active."""
    log_qa_pages(browser, "qa_pages_after_startup")
    stage_log("qa_manual_operator_mode", "automation=off observation=passive")
    print(
        "Manual operator mode: FundElite will not navigate or script this browser. "
        "All traffic still goes through the verified QA proxy.",
        flush=True,
    )

def qa_safe_session_hash(proxy_config):
    """Short non-reversible hash of the proxy session identity, for sanitized logs.

    Never returns or logs the username, sessid, password, or proxy URL."""
    import hashlib

    username = str((proxy_config or {}).get("username") or "")
    return hashlib.sha256(username.encode("utf-8")).hexdigest()[:12]


def rotate_dataimpulse_session(proxy_config):
    """Return a copy of proxy_config with a fresh DataImpulse sticky-session id.

    Provider-scoped: only DataImpulse rotates. Every other component (host, port,
    base login/auth identity, country/state/city/ZIP/ASN targeting, password) is
    preserved verbatim; only the sessid.<id> component is replaced. Fails closed
    when the sessid is missing, malformed, or present more than once rather than
    constructing arbitrary credentials. Never logs the username, password, or URL.
    """
    if QA_UPSTREAM_PROVIDER.get("id") != "dataimpulse":
        return proxy_config
    import re
    import secrets

    if not isinstance(proxy_config, dict):
        raise ValueError("DataImpulse proxy configuration is missing")
    username = str(proxy_config.get("username") or "").strip()
    if not username:
        raise ValueError("DataImpulse proxy username is missing")
    pattern = r"(^|;)sessid\\.[a-z0-9]{8,32}(?=;|$)"
    if len(re.findall(pattern, username)) != 1:
        raise ValueError("DataImpulse proxy username must contain exactly one session id")
    match = re.search(pattern, username)
    new_username = (
        username[: match.start()]
        + match.group(1)
        + "sessid." + secrets.token_hex(8)
        + username[match.end():]
    )
    rotated = dict(proxy_config)
    rotated["username"] = new_username
    _register_secret_redaction(new_username)
    return rotated


def resolve_verified_proxy_exit(proxy_config, requested_targeting):
    """Bounded initial preflight retry; returns the exact upstream that passed.

    Returns (verified_proxy_config, exit_ip, exit_location, locale_bundle).
    DataImpulse rotates its sticky session after a mismatch so each retry genuinely
    re-selects an exit; other providers re-check the same authorized upstream
    (existing behavior). The returned config is the same object used for the Camoufox
    launch (no TOCTOU). Raises SystemExit(6) when every attempt mismatches.
    """
    max_geo_attempts = 3
    current_proxy_config = proxy_config
    exit_ip = None
    exit_location = None
    for attempt in range(1, max_geo_attempts + 1):
        exit_ip = resolve_proxy_exit_ip(current_proxy_config)
        stage_log(
            "after_proxy_exit_ip_resolution",
            "attempt=%s ip=%s" % (attempt, exit_ip),
        )
        stage_log("geoip_ready", "mode=resolved_exit_ip")
        print("Camoufox geoip aligned to resolved proxy exit IP.")
        stage_log("before_exit_location_lookup")
        exit_location = lookup_exit_ip_location(exit_ip)
        if isinstance(exit_location, dict):
            exit_location = dict(exit_location)
            exit_location["available"] = True
        stage_log("after_exit_location_lookup")
        geo_status = evaluate_qa_location_match(requested_targeting, exit_location)
        print(
            "qa_preflight_location_match status=%s attempt=%s requested=%s observed=%s"
            % (
                geo_status,
                attempt,
                format_qa_location_label(requested_targeting),
                format_qa_location_label(exit_location),
            ),
            flush=True,
        )
        stage_log(
            "qa_proxy_preflight",
            "provider=%s attempt=%s session_hash=%s status=%s"
            % (
                QA_UPSTREAM_PROVIDER.get("id"),
                attempt,
                qa_safe_session_hash(current_proxy_config),
                geo_status,
            ),
        )
        if geo_status != "mismatch":
            return (
                current_proxy_config,
                exit_ip,
                exit_location,
                locale_bundle_for_exit_location(exit_location),
            )
        if attempt < max_geo_attempts:
            rotated = rotate_dataimpulse_session(current_proxy_config)
            if rotated is not current_proxy_config:
                stage_log(
                    "qa_proxy_session_rotated",
                    "provider=%s attempt=%s session_hash=%s"
                    % (
                        QA_UPSTREAM_PROVIDER.get("id"),
                        attempt + 1,
                        qa_safe_session_hash(rotated),
                    ),
                )
            current_proxy_config = rotated
            print(
                "Preflight exit did not satisfy the requested QA location; "
                "retrying residential exit (%s/%s)..."
                % (attempt, max_geo_attempts),
                flush=True,
            )
    stage_log("proxy_exit_location_mismatch")
    print("Requested QA location:")
    print("  %s" % (format_qa_location_label(requested_targeting),))
    print("Provider exit:")
    print("  %s" % (format_qa_location_label(exit_location),))
    print(
        "The configured proxy did not satisfy the requested QA location. "
        "No browser was opened."
    )
    raise SystemExit(6)


# ---------------------------------------------------------------------------
# FundElite QA Session Monitor: TTL deadline, exit-IP health, local status bridge
# and one shared read-only browser extension. Server remains authoritative for the
# approved TTL; the launcher only enforces a local monotonic deadline and observes
# the already-verified session. It never rotates providers or sessions.
# ---------------------------------------------------------------------------
QA_PROXY_HEALTH_CHECK_INTERVAL_SECONDS = 30
QA_SESSION_MONITOR_VERSION = "v1"
QA_SESSION_MONITOR_DIR_NAME = "session-monitor"
QA_SESSION_MONITOR_STATUS_PATH = "/qa-session-status"
QA_SESSION_MONITOR_READY_PATH = "/qa-session-monitor-ready"
QA_SESSION_MONITOR_RUNTIME_VERIFY_TIMEOUT_SECONDS = 10

QA_SAFE_STATUS_FIELDS = (
    "provider", "status", "exitIp", "state", "city", "postal",
    "configuredTtlSeconds", "elapsedSeconds", "nominalRemainingSeconds",
    "exitChanged", "locationCompatible", "lastVerifiedAt",
    "lastVerifiedAgoSeconds", "healthCheckIntervalSeconds",
)


def classify_session_status(elapsed_seconds, ttl_seconds, remaining_seconds, *, location_compatible, exit_changed, error):
    """Higher-priority states win. Expiry always beats a (transient) exit-change flag so a
    changed exit can never hide the TTL countdown or the expired terminal state."""
    if not location_compatible:
        return "location_mismatch"
    if error:
        return "error"
    if remaining_seconds <= 0:
        return "expired"
    percentage = (elapsed_seconds / ttl_seconds) if ttl_seconds > 0 else 1.0
    if remaining_seconds <= 5 * 60:
        return "expiring_soon"
    if percentage >= 0.90:
        return "expiring_soon"
    if percentage >= 0.70:
        return "aging"
    if exit_changed:
        return "exit_changed"
    return "healthy"


class QaSessionDeadline(object):
    """Monotonic deadline anchored to the server-authorized start epoch.

    Remaining budget is derived from wall-clock at construction so a within-session
    browser restart never resets the deadline; countdown uses monotonic time."""
    def __init__(self, approved_ttl_seconds, started_epoch=None):
        if approved_ttl_seconds <= 0:
            raise ValueError("approved_ttl_seconds must be positive")
        import time as _time
        now_epoch = _time.time()
        self.approved_ttl_seconds = int(approved_ttl_seconds)
        self.started_epoch = started_epoch if started_epoch is not None else now_epoch
        remaining_budget = max(0.0, (self.started_epoch + self.approved_ttl_seconds) - now_epoch)
        self.started_monotonic = _time.monotonic()
        self.deadline_monotonic = self.started_monotonic + remaining_budget

    def elapsed_seconds(self):
        import time as _time
        return max(0, int(_time.time() - self.started_epoch))

    def remaining_seconds(self):
        import time as _time
        return max(0, int(self.deadline_monotonic - _time.monotonic()))

    def expired(self):
        import time as _time
        return _time.monotonic() >= self.deadline_monotonic


class QaProxySessionStatusStore(object):
    """Thread-safe in-memory status. Never persists IP history or credentials."""
    def __init__(self, initial):
        import threading
        self._lock = threading.Lock()
        self._status = dict(initial or {})

    def snapshot(self):
        with self._lock:
            return dict(self._status)

    def update(self, **values):
        with self._lock:
            for key, value in values.items():
                self._status[key] = value


def safe_status_payload(snapshot):
    """Allowlist-only serialization: never leaks credentials, sessid, tokens or upstream URLs."""
    return {key: snapshot.get(key) for key in QA_SAFE_STATUS_FIELDS}


def build_safe_monitor_payload(status_store, deadline):
    """Compute elapsed/remaining on demand (no per-second timer), then allowlist fields."""
    snapshot = status_store.snapshot()
    elapsed = deadline.elapsed_seconds()
    remaining = deadline.remaining_seconds()
    status = classify_session_status(
        elapsed_seconds=elapsed,
        ttl_seconds=deadline.approved_ttl_seconds,
        remaining_seconds=remaining,
        location_compatible=bool(snapshot.get("location_compatible", True)),
        exit_changed=bool(snapshot.get("exit_changed", False)),
        error=bool(snapshot.get("error", False)),
    )
    now_epoch = None
    last_verified = snapshot.get("last_verified_epoch")
    last_verified_ago = None
    if last_verified is not None:
        import time as _time
        last_verified_ago = max(0, int(_time.time() - float(last_verified)))
    return safe_status_payload({
        "provider": snapshot.get("provider"),
        "status": status,
        "exitIp": snapshot.get("verified_exit_ip"),
        "state": snapshot.get("state"),
        "city": snapshot.get("city"),
        "postal": snapshot.get("postal"),
        "configuredTtlSeconds": deadline.approved_ttl_seconds,
        "elapsedSeconds": elapsed,
        "nominalRemainingSeconds": remaining,
        "exitChanged": bool(snapshot.get("exit_changed", False)),
        "locationCompatible": bool(snapshot.get("location_compatible", True)),
        "lastVerifiedAt": snapshot.get("last_verified_at"),
        "lastVerifiedAgoSeconds": last_verified_ago,
        "healthCheckIntervalSeconds": QA_PROXY_HEALTH_CHECK_INTERVAL_SECONDS,
    })


class QaMonitorServer(object):
    """Read-only localhost status bridge, bound to 127.0.0.1 with a random per-launch
    capability token. Never exposes credentials, sessids, tokens or upstream URLs.

    Tracks a one-time extension startup handshake so the launcher can runtime-verify that the
    shared session-monitor extension actually loaded (not merely that its path was passed)."""
    def __init__(self, status_store, deadline):
        import secrets as _secrets
        self.status_store = status_store
        self.deadline = deadline
        self.capability = _secrets.token_urlsafe(24)
        self.server = None
        self.thread = None
        self.extension_ready = False
        self.extension_ready_at = None
        self.extension_version = None

    def start(self):
        import json as _json
        import threading as _threading
        from http.server import BaseHTTPRequestHandler, ThreadingHTTPServer

        status_store = self.status_store
        deadline = self.deadline
        capability = self.capability
        bridge = self

        def _send(self, status, body_bytes, content_type="application/json"):
            self.send_response(status)
            self.send_header("Content-Type", content_type)
            self.send_header("Cache-Control", "no-store")
            self.send_header("Content-Length", str(len(body_bytes)))
            self.end_headers()
            self.wfile.write(body_bytes)

        class Handler(BaseHTTPRequestHandler):
            def do_GET(self):
                if self.path != QA_SESSION_MONITOR_STATUS_PATH:
                    self.send_response(404)
                    self.end_headers()
                    return
                supplied = self.headers.get("X-FundElite-QA-Monitor", "")
                if not capability or supplied != capability:
                    self.send_response(403)
                    self.end_headers()
                    return
                body = _json.dumps(build_safe_monitor_payload(status_store, deadline), separators=(",", ":")).encode("utf-8")
                _send(self, 200, body)

            def do_POST(self):
                if self.path != QA_SESSION_MONITOR_READY_PATH:
                    self.send_response(404)
                    self.end_headers()
                    return
                supplied = self.headers.get("X-FundElite-QA-Monitor", "")
                if not capability or supplied != capability:
                    self.send_response(403)
                    self.end_headers()
                    return
                length = int(self.headers.get("Content-Length") or 0)
                raw = self.rfile.read(length) if length else b""
                try:
                    payload = _json.loads(raw.decode("utf-8", "replace") or "{}")
                except Exception:
                    payload = {}
                if not isinstance(payload, dict):
                    payload = {}
                if not bridge.extension_ready:
                    bridge.extension_ready = True
                    bridge.extension_ready_at = time.time()
                    bridge.extension_version = str(payload.get("extensionVersion") or "")[:32]
                _send(self, 200, b'{"ok":true}')

            def log_message(self, _format, *_args):
                return

        self.server = ThreadingHTTPServer(("127.0.0.1", 0), Handler)
        self.thread = _threading.Thread(target=self.server.serve_forever, name="fundelite-qa-monitor", daemon=True)
        self.thread.start()
        host, port = self.server.server_address
        return {"endpoint": "http://%s:%s%s" % (host, port, QA_SESSION_MONITOR_STATUS_PATH), "capability": self.capability}

    def stop(self):
        if self.server is not None:
            try:
                self.server.shutdown()
                self.server.server_close()
            except Exception:
                pass
            self.server = None


class QaProxyHealthMonitor(object):
    """Observes the verified exit IP and TTL deadline. Never rotates a provider session."""
    def __init__(self, proxy_config, status_store, requested_target, deadline, stop_event, resolve_fn=None, geo_fn=None):
        import threading as _threading
        self.proxy_config = proxy_config
        self.status_store = status_store
        self.requested_target = requested_target
        self.deadline = deadline
        self.stop_event = stop_event
        self.resolve_fn = resolve_fn or resolve_proxy_exit_ip
        self.geo_fn = geo_fn or lookup_exit_ip_location
        self.thread = _threading.Thread(target=self._run, name="qa-proxy-health-monitor", daemon=True)
        self.last_status = None

    def start(self):
        stage_log("qa_proxy_health_monitor_started", "interval_seconds=%s" % (QA_PROXY_HEALTH_CHECK_INTERVAL_SECONDS,))
        self.thread.start()
        return self

    def stop(self, reason="session_end"):
        self.stop_event.set()
        if self.thread.is_alive():
            self.thread.join(timeout=2)

    def _emit_transition(self, new_status):
        if new_status != self.last_status:
            self.last_status = new_status
            if new_status == "aging":
                stage_log("qa_proxy_session_aging")
            elif new_status == "expiring_soon":
                stage_log("qa_proxy_session_expiring_soon")

    def _run(self):
        while not self.stop_event.wait(QA_PROXY_HEALTH_CHECK_INTERVAL_SECONDS):
            self._check_once()

    def _check_once(self):
        """One health-check pass. Returns False when the session is terminal (expired or
        fail-closed mismatch); True when the session is still healthy."""
        import time as _time
        if self.deadline.expired():
            self.status_store.update(status="expired", nominal_remaining_seconds=0)
            QA_SESSION_TTL["expired"] = True
            stage_log("qa_proxy_session_ttl_expired")
            return False
        snapshot = self.status_store.snapshot()
        old_ip = snapshot.get("verified_exit_ip")
        try:
            new_ip = self.resolve_fn(self.proxy_config)
        except Exception:
            self.status_store.update(error=True)
            return True
        if not new_ip:
            return True
        if new_ip != old_ip:
            stage_log("qa_proxy_exit_changed")
            stage_log("qa_proxy_exit_revalidation_start")
            try:
                new_geo = self.geo_fn(new_ip)
                if isinstance(new_geo, dict):
                    new_geo = dict(new_geo)
                    new_geo["available"] = True
            except Exception:
                # Geo could not be resolved: fail closed.
                self.status_store.update(status="location_mismatch", location_compatible=False)
                stage_log("qa_proxy_exit_revalidation_failed", "reason=geo_unresolvable")
                return False
            status = evaluate_qa_location_match(self.requested_target, new_geo)
            if status == "mismatch":
                self.status_store.update(status="location_mismatch", location_compatible=False)
                stage_log("qa_proxy_exit_revalidation_failed", "status=%s" % (status,))
                return False
            # Compatible replacement exit after revalidation.
            self.status_store.update(
                verified_exit_ip=new_ip,
                state=(new_geo or {}).get("region") or (new_geo or {}).get("state"),
                city=(new_geo or {}).get("city"),
                postal=(new_geo or {}).get("postal"),
                location_compatible=True,
                exit_changed=True,
            )
            stage_log("qa_proxy_exit_revalidation_compatible")
        # Successful (stable or revalidated) check: update last-verified timestamp only.
        self.status_store.update(error=False, last_verified_epoch=_time.time())
        self._emit_transition(classify_session_status(
            elapsed_seconds=self.deadline.elapsed_seconds(),
            ttl_seconds=self.deadline.approved_ttl_seconds,
            remaining_seconds=self.deadline.remaining_seconds(),
            location_compatible=bool(self.status_store.snapshot().get("location_compatible", True)),
            exit_changed=bool(self.status_store.snapshot().get("exit_changed", False)),
            error=False,
        ))
        return True


def stop_qa_session_monitor():
    """Stop the health monitor + status server; never removes the shared extension or profile."""
    monitor = QA_SESSION_MONITOR
    health = monitor.get("health")
    if health is not None:
        try:
            health.stop("session_end")
        except Exception:
            pass
        monitor["health"] = None
    server = monitor.get("server")
    if server is not None:
        try:
            server.stop()
        except Exception:
            pass
        monitor["server"] = None


def _verify_qa_session_monitor_runtime():
    """Wait for the shared session-monitor extension to announce itself over the bridge.

    This is observability only: a missing handshake is logged as unavailable and never
    closes the browser (the protocol guard, not the monitor, is the security boundary)."""
    server = QA_SESSION_MONITOR.get("server")
    if server is None:
        stage_log("qa_session_monitor_runtime_verification_failed", "reason=no_status_bridge")
        return
    if getattr(server, "extension_ready", False):
        stage_log(
            "qa_session_monitor_runtime_verified",
            "version=%s" % (getattr(server, "extension_version", "") or ""),
        )
        return
    stage_log("qa_session_monitor_runtime_verification_start")
    deadline = time.time() + QA_SESSION_MONITOR_RUNTIME_VERIFY_TIMEOUT_SECONDS
    while time.time() < deadline:
        if getattr(server, "extension_ready", False):
            stage_log(
                "qa_session_monitor_runtime_verified",
                "version=%s" % (getattr(server, "extension_version", "") or ""),
            )
            return
        time.sleep(0.2)
    stage_log("qa_session_monitor_runtime_verification_failed", "reason=no_ready_handshake")


def qa_session_monitor_extension_root():
    return os.path.join(os.path.expanduser("~"), ".fundelite", "qa-browser", "extensions", QA_SESSION_MONITOR_DIR_NAME, QA_SESSION_MONITOR_VERSION)


QA_SESSION_MONITOR_ADDON_ID = "qa-session-monitor@fundelite.ai"

QA_SESSION_MONITOR_MANIFEST = '''{
  "manifest_version": 2,
  "name": "FundElite QA Session Monitor",
  "version": "1.0.0",
  "description": "Read-only status for the current FundElite Authorized QA Proxy session.",
  "browser_specific_settings": {
    "gecko": {
      "id": "qa-session-monitor@fundelite.ai"
    }
  },
  "icons": {
    "16": "icons/icon-16.png",
    "32": "icons/icon-32.png",
    "48": "icons/icon-48.png"
  },
  "browser_action": {
    "default_title": "FundElite QA Session",
    "default_popup": "popup.html",
    "default_icon": {
      "16": "icons/icon-16.png",
      "32": "icons/icon-32.png",
      "48": "icons/icon-48.png"
    }
  },
  "background": {
    "scripts": ["background.js"]
  },
  "permissions": [],
  "experiment_apis": {
    "qaSessionMonitor": {
      "schema": "schema.json",
      "parent": {
        "scopes": ["addon_parent"],
        "script": "api.js",
        "paths": [["qaSessionMonitor"]]
      }
    }
  }
}'''

QA_SESSION_MONITOR_SCHEMA = '[{"namespace":"qaSessionMonitor","functions":[{"name":"getSessionConfig","type":"function","parameters":[]}]}]'

QA_SESSION_MONITOR_API_JS = '''"use strict";
this.qaSessionMonitor = class extends ExtensionAPI {
  getAPI() {
    return {
      qaSessionMonitor: {
        getSessionConfig() {
          let endpoint = "";
          let capability = "";
          try {
            endpoint = Services.prefs.getStringPref("extensions.fundeliteQaMonitor.endpoint", "");
            capability = Services.prefs.getStringPref("extensions.fundeliteQaMonitor.capability", "");
          } catch (e) {}
          return { endpoint, capability };
        }
      }
    };
  }
};
'''

QA_SESSION_MONITOR_BACKGROUND_JS = '''"use strict";
async function announceReady() {
  try {
    const cfg = await browser.qaSessionMonitor.getSessionConfig();
    if (!cfg || !cfg.endpoint || !cfg.capability) return;
    const base = cfg.endpoint.replace(/\\/qa-session-status\\/?$/, "");
    await fetch(base + "/qa-session-monitor-ready", {
      method: "POST",
      headers: {
        "X-FundElite-QA-Monitor": cfg.capability,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status: "ready", extensionVersion: "1.0.0" }),
    });
  } catch (e) {
    /* The launcher reports qa_session_monitor_runtime_verification_failed on timeout. */
  }
}
announceReady();
'''

QA_SESSION_MONITOR_POPUP_HTML = '''<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>FundElite QA Session</title>
  <link rel="stylesheet" href="popup.css">
</head>
<body>
  <main class="monitor">
    <header class="monitor__header">
      <div>
        <div class="monitor__eyebrow">FundElite</div>
        <h1>QA Proxy</h1>
      </div>
      <span id="statusBadge" class="badge">Loading</span>
    </header>
    <dl class="grid">
      <dt>Provider</dt><dd id="provider">-</dd>
      <dt>Exit IP</dt><dd id="exitIp">-</dd>
      <dt>Location</dt><dd id="location">-</dd>
      <dt>Configured TTL</dt><dd id="ttl">-</dd>
      <dt>Elapsed</dt><dd id="elapsed">-</dd>
      <dt>Nominal Remaining</dt><dd id="remaining">-</dd>
      <dt>Exit</dt><dd id="exitStatus">-</dd>
      <dt>Last Verified</dt><dd id="lastVerified">-</dd>
    </dl>
  </main>
  <script src="popup.js"></script>
</body>
</html>
'''

QA_SESSION_MONITOR_POPUP_CSS = '''.monitor { padding: 14px; font-family: system-ui, sans-serif; min-width: 320px; }
.monitor__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.monitor__eyebrow { font-size: 11px; opacity: 0.65; }
h1 { margin: 2px 0 0; font-size: 18px; }
.grid { display: grid; grid-template-columns: 130px 1fr; gap: 8px 12px; margin: 0; }
dt { opacity: 0.66; }
dd { margin: 0; text-align: right; font-weight: 600; overflow-wrap: anywhere; }
.badge { border-radius: 999px; padding: 5px 9px; font-size: 11px; font-weight: 700; background: rgba(0,0,0,0.08); }
'''

QA_SESSION_MONITOR_POPUP_JS = '''const REFRESH_MS = 5000;
function formatDuration(totalSeconds) {
  const value = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = Math.floor(value % 60);
  if (hours > 0) return hours + "h " + minutes + "m";
  if (minutes > 0) return minutes + "m " + seconds + "s";
  return seconds + "s";
}
function setText(id, value) {
  const element = document.getElementById(id);
  if (element) {
    element.textContent = (value == null || value === "") ? "-" : String(value);
  }
}
function renderStatus(data) {
  setText("provider", data.provider);
  setText("exitIp", data.exitIp);
  const location = [data.city, data.state, data.postal].filter(Boolean).join(", ");
  setText("location", location || "");
  setText("ttl", formatDuration(data.configuredTtlSeconds));
  setText("elapsed", formatDuration(data.elapsedSeconds));
  setText("remaining", formatDuration(data.nominalRemainingSeconds));
  setText("exitStatus", data.exitChanged ? "Changed" : "Stable");
  setText("lastVerified", data.lastVerifiedAgoSeconds == null ? "" : data.lastVerifiedAgoSeconds + "s ago");
  setText("statusBadge", String(data.status || "unknown"));
}
async function refresh() {
  try {
    const config = await browser.qaSessionMonitor.getSessionConfig();
    if (!config || !config.endpoint) throw new Error("monitor_unavailable");
    const headers = config.capability ? { "X-FundElite-QA-Monitor": config.capability } : {};
    const response = await fetch(config.endpoint, { headers: headers, cache: "no-store" });
    if (!response.ok) throw new Error("monitor_request_failed");
    renderStatus(await response.json());
  } catch (e) {
    setText("statusBadge", "Unavailable");
  }
}
refresh();
setInterval(refresh, REFRESH_MS);
'''


def _qa_monitor_icon_png(size):
    """Generate a tiny solid-color PNG (FundElite green) without any binary asset in the repo."""
    import struct
    import zlib

    def chunk(kind, data):
        return (
            struct.pack(">I", len(data)) + kind + data
            + struct.pack(">I", zlib.crc32(kind + data) & 0xffffffff)
        )

    ihdr = struct.pack(">IIBBBBB", size, size, 8, 6, 0, 0, 0)  # 8-bit RGBA
    row = b"\\x00" + b"".join(bytes((16, 185, 129, 255)) for _ in range(size))
    idat = zlib.compress(row * size)
    return (
        b"\\x89PNG\\r\\n\\x1a\\n"
        + chunk(b"IHDR", ihdr)
        + chunk(b"IDAT", idat)
        + chunk(b"IEND", b"")
    )


def ensure_shared_qa_monitor_extension():
    """Write (once, idempotently) the single shared session-monitor extension package.

    Returns its directory path. The package is shared across all QA profiles; only the
    tiny per-session endpoint/capability (delivered via prefs) differs."""
    stage_log("qa_session_monitor_extension_prepare")
    version_dir = qa_session_monitor_extension_root()
    files = {
        "manifest.json": QA_SESSION_MONITOR_MANIFEST,
        "schema.json": QA_SESSION_MONITOR_SCHEMA,
        "api.js": QA_SESSION_MONITOR_API_JS,
        "background.js": QA_SESSION_MONITOR_BACKGROUND_JS,
        "popup.html": QA_SESSION_MONITOR_POPUP_HTML,
        "popup.css": QA_SESSION_MONITOR_POPUP_CSS,
        "popup.js": QA_SESSION_MONITOR_POPUP_JS,
    }
    icons = {
        "icons/icon-16.png": 16,
        "icons/icon-32.png": 32,
        "icons/icon-48.png": 48,
    }
    try:
        os.makedirs(version_dir, exist_ok=True)
        all_valid = True
        for name in files:
            if not os.path.isfile(os.path.join(version_dir, name)):
                all_valid = False
                break
        for rel in icons:
            if not os.path.isfile(os.path.join(version_dir, rel)):
                all_valid = False
                break
        if not all_valid:
            for name, body in files.items():
                tmp = os.path.join(version_dir, name + ".tmp")
                with open(tmp, "w", encoding="utf-8") as fh:
                    fh.write(body)
                os.replace(tmp, os.path.join(version_dir, name))
            os.makedirs(os.path.join(version_dir, "icons"), exist_ok=True)
            for rel, size in icons.items():
                tmp = os.path.join(version_dir, rel + ".tmp")
                with open(tmp, "wb") as fh:
                    fh.write(_qa_monitor_icon_png(size))
                os.replace(tmp, os.path.join(version_dir, rel))
        total = sum(os.path.getsize(os.path.join(version_dir, n)) for n in os.listdir(version_dir) if os.path.isfile(os.path.join(version_dir, n)))
        total += sum(os.path.getsize(os.path.join(version_dir, r)) for r in icons)
        stage_log("qa_session_monitor_extension_ready", "shared_size_bytes=%s" % (total,))
    except Exception as exc:
        stage_log("qa_session_monitor_extension_failed", "reason=%s" % (type(exc).__name__,))
        raise
    return version_dir


` : ''}
def open_browser_page(browser):
    """Prefer an existing page — Browser.new_page can hang forever on some Camoufox builds."""
    try:
        pages = list(browser.pages) if getattr(browser, "pages", None) else []
    except Exception:
        pages = []
    if pages:
        return pages[0]
    stage_log("browser_spawn_started", "action=new_page")
    return browser.new_page()


def run_camoufox_session():
    """Worker body — redeem token / Camoufox only after stage=worker_started."""
    # First observable worker log — before token redemption and before Camoufox import.
    stage_log("worker_started", "pid=%s" % (os.getpid(),))
${authorizedQaProxyEnabled ? `    stage_log("qa_launcher_version", "version=%s" % (QA_LAUNCHER_TEMPLATE_VERSION,))
` : ''}    write_launch_status(_LAUNCH_STATUS_STARTED)
    install_launcher_signal_handlers()
    atexit.register(cleanup_browser_processes, reason="atexit")
${authorizedQaProxyEnabled ? `    if AUTHORIZED_QA_PROXY_ENABLED:
        acquire_qa_runtime_lock()
` : ''}
    stage_log("before_camoufox_import")
    import_t0 = time.time()
    try:
        from browserforge.fingerprints import Screen
        from camoufox.sync_api import Camoufox
    except Exception as exc:
        fail_launch("Camoufox import failed: %s" % (exc,), exit_code=5)
    stage_log("after_camoufox_import", "elapsed_seconds=%.2f" % (time.time() - import_t0,))

    camoufox_os, logical_width, logical_height, window_width, window_height = get_device_settings()
    try:
        window_x = int(os.environ.get("FUNDELITE_WINDOW_X") or 0)
        window_y = int(os.environ.get("FUNDELITE_WINDOW_Y") or 0)
    except Exception:
        window_x, window_y = 0, 0

    print("Device: %s" % (platform.system(),))
    print("Detected logical display: %s x %s" % (logical_width, logical_height))
    print("Chosen browser window: %s x %s origin=%s,%s" % (window_width, window_height, window_x, window_y))
    print("Camoufox profile OS: %s" % (camoufox_os,))

    resolved_startup_url = STARTUP_URL if is_safe_startup_url(STARTUP_URL) else "${DEFAULT_STARTUP_URL}"
    if resolved_startup_url != STARTUP_URL:
        print("Refusing invalid startup URL %r. Using %s." % (STARTUP_URL, resolved_startup_url))

${authorizedQaProxyEnabled ? `    proxy_config = None
    proxy_approved_hosts = []
    proxy_profile_id = None
    proxy_assignment_id = None
    proxy_routing_mode = "browser_context_global"
    proxy_web_access_mode = "restricted_allowlist"
    camoufox_geoip = False
    requested_targeting = {}
    verified_exit_location = None
    verified_locale_bundle = None
    qa_gateway_port = None
    qa_upstream_proxy = None
    qa_upstream_keepalive = None
    proxy_launch = {}

    if AUTHORIZED_QA_PROXY_ENABLED:
        stage_log("before_token_redemption")
        proxy_launch = load_authorized_qa_proxy_config()
        stage_log("token_redemption_complete")
        stage_log("token_redeemed")
        proxy_config = proxy_launch.get("proxy")
        proxy_approved_hosts = proxy_launch.get("approved_hosts") or []
        proxy_profile_id = proxy_launch.get("profile_id")
        proxy_assignment_id = proxy_launch.get("assignment_id")
        proxy_routing_mode = proxy_launch.get("routing_mode") or AUTHORIZED_QA_PROXY_ROUTING_MODE
        proxy_web_access_mode = str(proxy_launch.get("web_access_mode") or "restricted_allowlist").strip().lower()
        requested_targeting = proxy_launch.get("requested_targeting") or proxy_launch.get("targeting") or {}
        proxy_startup_url = proxy_launch.get("startup_url")
        if is_safe_startup_url(proxy_startup_url):
            resolved_startup_url = proxy_startup_url.strip()
            print("Authorized QA Proxy startup URL: %s" % (resolved_startup_url,))
        else:
            print(
                "Authorized QA Proxy Session launch config did not include a usable startup URL."
            )
            raise SystemExit(4)
        if not proxy_config:
            print("Authorized QA Proxy Session cannot start without proxy configuration.")
            raise SystemExit(4)
        full_web = proxy_web_access_mode in {"full_web", "full_web_proxied", "full-web"}
        if not full_web:
            if not proxy_approved_hosts:
                print("Authorized QA Proxy Session cannot start without a server allowlist.")
                raise SystemExit(4)
            if not is_hostname_allowlisted(resolved_startup_url, proxy_approved_hosts):
                print(
                    "Authorized QA Proxy Session startup URL host is not allowlisted. Refusing launch."
                )
                raise SystemExit(4)
        elif is_private_or_unsafe_network_host(resolved_startup_url):
            print(
                "Authorized QA Proxy Session startup URL targets a private network. Refusing launch."
            )
            raise SystemExit(4)
        if proxy_config.get("password"):
            _register_secret_redaction(proxy_config.get("password"))
        if proxy_config.get("username"):
            _register_secret_redaction(proxy_config.get("username"))
        stage_log("before_proxy_exit_ip_resolution")
        try:
            (
                proxy_config,
                camoufox_geoip,
                verified_exit_location,
                verified_locale_bundle,
            ) = resolve_verified_proxy_exit(proxy_config, requested_targeting)
        except SystemExit:
            raise
        except Exception as exc:
            if is_provider_endpoint_expired_error(exc):
                fail_launch(qa_provider_endpoint_expired_message(), exit_code=5)
            fail_launch("GeoIP exit-IP probe failed: %s" % (exc,), exit_code=5)

        display_pref = proxy_launch.get("qa_browser_display") or os.environ.get("FUNDELITE_QA_DISPLAY") or QA_BROWSER_DISPLAY_CURRENT
        os.environ["FUNDELITE_QA_DISPLAY"] = normalize_qa_browser_display(display_pref)
        selected = select_display_geometry(os.environ["FUNDELITE_QA_DISPLAY"], camoufox_os)
        camoufox_os, logical_width, logical_height, window_width, window_height = selected[:5]
        window_x, window_y = selected[5], selected[6]
        website_access_token = proxy_launch.get("website_access_session_token") or ""
        website_access_url = proxy_launch.get("website_access_decide_url") or ""

        def website_access_decision_fn(host):
            return fetch_website_access_decision(host, website_access_token, website_access_url)

        qa_gateway = start_qa_allowlist_gateway(
            proxy_config,
            proxy_approved_hosts,
            website_access_decision_fn,
            proxy_web_access_mode,
        )
        qa_upstream_proxy = proxy_config
        proxy_config = {"server": qa_gateway["server"]}
        qa_gateway_port = qa_gateway["port"]
        stage_log("gateway_started", "port=%s" % (qa_gateway_port,))
        print(
            "web_access_mode=%s routing_mode=%s"
            % (
                proxy_web_access_mode,
                "full_web_proxied" if full_web else (proxy_routing_mode or "browser_context_global"),
            ),
            flush=True,
        )

        # QA Session Monitor: server-authorized TTL deadline + read-only localhost status bridge.
        approved_ttl_seconds = int(proxy_launch.get("approvedTtlSeconds") or 3600)
        authorized_started_at = proxy_launch.get("authorizedSessionStartedAt")
        try:
            authorized_started_at = float(authorized_started_at) if authorized_started_at is not None else None
        except (TypeError, ValueError):
            authorized_started_at = None
        qa_session_deadline = QaSessionDeadline(approved_ttl_seconds, started_epoch=authorized_started_at)
        qa_status_store = QaProxySessionStatusStore({
            "provider": QA_UPSTREAM_PROVIDER["id"],
            "verified_exit_ip": camoufox_geoip if isinstance(camoufox_geoip, str) else None,
            "state": (verified_exit_location or {}).get("region") or (verified_exit_location or {}).get("state"),
            "city": (verified_exit_location or {}).get("city"),
            "postal": (verified_exit_location or {}).get("postal"),
            "location_compatible": True,
            "exit_changed": False,
            "error": False,
            "last_verified_epoch": None,
            "last_verified_at": None,
        })
        qa_monitor_server = QaMonitorServer(qa_status_store, qa_session_deadline)
        try:
            qa_monitor_config = qa_monitor_server.start()
        except Exception as exc:
            qa_monitor_config = None
            stage_log("qa_monitor_status_bridge_unavailable", "reason=%s" % (type(exc).__name__,))
        QA_SESSION_MONITOR["server"] = qa_monitor_server
        QA_SESSION_MONITOR["status_store"] = qa_status_store
        QA_SESSION_MONITOR["deadline"] = qa_session_deadline
        QA_SESSION_MONITOR["monitor_config"] = qa_monitor_config
        stage_log("qa_session_monitor_started", "ttl_seconds=%s" % (approved_ttl_seconds,))

    print("Authorized QA Proxy Session — all browser traffic in this session uses the approved QA proxy.")
    print("Isolated profile path: %s" % (PROFILE_PATH,))

` : ''}
    stage_log("before_fingerprint_construction")
    screen_constraints = Screen(
        min_width=logical_width,
        max_width=logical_width,
        min_height=logical_height,
        max_height=logical_height,
    )
    stage_log("fingerprint_generated", "attempt=monitor_matched")
    print(
        "Camoufox screen fingerprint pinned to monitor: %s x %s (min=max, not a random 960x540)"
        % (logical_width, logical_height)
    )
    print(
        "Browser outer window matches usable work area: %s x %s at %s,%s"
        % (window_width, window_height, window_x, window_y)
    )
    print("fingerprint_attempt=monitor_matched")

    def build_camoufox_launch_kwargs(screen=None):
        """Build Camoufox kwargs. Fingerprint screen matches the selected monitor."""
        inner_height = max(MIN_HEIGHT, int(window_height) - FIREFOX_CHROME_HEIGHT_PX)
        kwargs = {
            "headless": False,
            "os": camoufox_os,
            "persistent_context": True,
            "user_data_dir": PROFILE_PATH,
            "window": (window_width, window_height),
            "config": {
                "screen.width": int(logical_width),
                "screen.height": int(logical_height),
                "screen.availWidth": int(window_width),
                "screen.availHeight": int(window_height),
                "screen.availLeft": int(window_x or 0),
                "screen.availTop": max(0, int(window_y or 0)),
                "window.outerWidth": int(window_width),
                "window.outerHeight": int(window_height),
                "window.innerWidth": int(window_width),
                "window.innerHeight": int(inner_height),
                "window.screenX": int(window_x or 0),
                "window.screenY": int(window_y or 0),
            },
            "firefox_user_prefs": {
                "ui.systemUsesDarkTheme": 0,
                "browser.theme.content-theme": 0,
                "browser.theme.toolbar-theme": 0,
            },
        }
        if screen is not None:
            kwargs["screen"] = screen
${authorizedQaProxyEnabled ? `        if proxy_config:
            kwargs["proxy"] = proxy_config
            # Prefer concrete IP (str). Avoid geoip=True which calls public_ip() without a parent timeout.
            kwargs["geoip"] = camoufox_geoip
            if verified_locale_bundle and verified_locale_bundle.get("locale"):
                kwargs["locale"] = verified_locale_bundle.get("locale")
            # Apply verified exit timezone at construction time — post-enter
            # set_timezone_id is not reliable on all Camoufox builds.
            if verified_locale_bundle and verified_locale_bundle.get("timezone"):
                kwargs["timezone_id"] = verified_locale_bundle.get("timezone")
        excluded_addons = qa_excluded_default_addons()
        if excluded_addons:
            kwargs["exclude_addons"] = excluded_addons
        kwargs["firefox_user_prefs"].update(QA_COOKIE_COMPAT_PREFS)
        kwargs["firefox_user_prefs"].update(QA_NETWORK_SAFETY_PREFS)
        kwargs["firefox_user_prefs"].update(QA_SESSION_RESTORE_PREFS)
        kwargs["firefox_user_prefs"].update(QA_PROTOCOL_GUARD_PREFS)
        kwargs["addons"] = [write_qa_protocol_guard_addon(PROFILE_PATH)]
        try:
            kwargs["addons"].append(ensure_shared_qa_monitor_extension())
        except Exception:
            pass
        monitor_config = QA_SESSION_MONITOR.get("monitor_config") or {}
        if monitor_config.get("endpoint"):
            kwargs["firefox_user_prefs"]["extensions.fundeliteQaMonitor.endpoint"] = monitor_config["endpoint"]
        if monitor_config.get("capability"):
            kwargs["firefox_user_prefs"]["extensions.fundeliteQaMonitor.capability"] = monitor_config["capability"]
        stage_log("qa_session_monitor_extension_attached")
` : ''}        return kwargs

    def open_camoufox_with_fingerprint_fallback():
        """Try monitor-matched Screen min=max, then default screen on BrowserForge failure."""
        stage_log("before_camoufox_construction", "attempt=monitor_matched")
        ctor_t0 = time.time()
        cm = Camoufox(**build_camoufox_launch_kwargs(screen=screen_constraints))
        stage_log(
            "after_camoufox_construction",
            "attempt=constrained elapsed_seconds=%.2f" % (time.time() - ctor_t0,),
        )
        try:
            stage_log("before_camoufox_enter", "attempt=monitor_matched")
            stage_log("browser_spawn_started", "attempt=monitor_matched")
            enter_t0 = time.time()
            browser = cm.__enter__()
            stage_log(
                "after_camoufox_enter",
                "attempt=constrained elapsed_seconds=%.2f" % (time.time() - enter_t0,),
            )
            _camoufox_cm_holder.append(cm)
            return cm, browser
        except ValueError as exc:
            message = str(exc)
            if "No headers based on this input can be generated" not in message:
                raise
            stage_log("fingerprint_generated", "attempt=default_screen_fallback")
            print("fingerprint_fallback=default_screen")
            try:
                cm.__exit__(*sys.exc_info())
            except Exception:
                pass
            stage_log("before_camoufox_construction", "attempt=default_screen_fallback")
            cm = Camoufox(**build_camoufox_launch_kwargs(screen=None))
            stage_log("after_camoufox_construction", "attempt=default_screen_fallback")
            stage_log("before_camoufox_enter", "attempt=default_screen_fallback")
            stage_log("browser_spawn_started", "attempt=default_screen_fallback")
            browser = cm.__enter__()
            stage_log("after_camoufox_enter", "attempt=default_screen_fallback")
            _camoufox_cm_holder.append(cm)
            return cm, browser

    try:
        _camoufox_cm, browser = open_camoufox_with_fingerprint_fallback()
        stage_log("browser_connected")
${authorizedQaProxyEnabled ? `        # Reported as connected only after the guard is verified, so the supervisor's launch
        # timeout still terminates the browser if activation hangs.
        activate_qa_protocol_guard(browser)
        _verify_qa_session_monitor_runtime()
` : ''}        write_launch_status(_LAUNCH_STATUS_CONNECTED)
${authorizedQaProxyEnabled ? `        install_qa_allowlist_guards(browser, proxy_approved_hosts, proxy_web_access_mode)
        if verified_exit_location:
            verified_locale_bundle = apply_verified_exit_locale(browser, verified_exit_location)
            print_verified_qa_exit_location(
                requested_targeting,
                verified_exit_location,
                verified_locale_bundle,
            )
` : ''}        page = open_browser_page(browser)
        place_browser_on_selected_display(page, window_x, window_y, window_width, window_height)
        print_geometry_diagnostics(page, logical_width, logical_height, window_width, window_height)
${authorizedQaProxyEnabled ? `
        print_authorized_qa_proxy_route_diagnostic(
            url_or_host=resolved_startup_url,
            approved_hosts=proxy_approved_hosts,
            proxy_configured=bool(proxy_config),
            routing_mode=proxy_routing_mode,
            profile_id=proxy_profile_id,
            assignment_id=proxy_assignment_id,
            web_access_mode=proxy_web_access_mode,
        )
        print_authorized_qa_proxy_route_diagnostic(
            url_or_host="https://www.google.com/",
            approved_hosts=proxy_approved_hosts,
            proxy_configured=bool(proxy_config),
            routing_mode=proxy_routing_mode,
            profile_id=proxy_profile_id,
            assignment_id=proxy_assignment_id,
            web_access_mode=proxy_web_access_mode,
        )
        print_browser_geo_environment_diagnostic(page)
        print_qa_cookie_session_diagnostics(
            page,
            PROFILE_PATH,
            proxy_enabled=bool(proxy_config),
            private_network_protection=bool(qa_gateway_port),
            content_blocker_off=bool(qa_excluded_default_addons()),
        )
        attach_qa_network_diagnostics_to_context(browser, proxy_approved_hosts, proxy_web_access_mode)
        attach_qa_network_diagnostics(page, proxy_approved_hosts, proxy_web_access_mode)
        if qa_gateway_port and proxy_web_access_mode not in {"full_web", "full_web_proxied", "full-web"}:
            print_google_block_probe(qa_gateway_port)
        qa_exit_verification = verify_qa_browser_exit_ip(page, camoufox_geoip, requested_targeting, verified_exit_location)
        try:
            start_qa_ip_usage_reporter(proxy_launch, qa_exit_verification)
        except Exception:
            print("qa_ip_usage_report status=unavailable", flush=True)
        # Session established: keep this launch's own endpoint from idling out.
        qa_upstream_keepalive = start_qa_upstream_keepalive(
            qa_upstream_proxy,
            proxy_launch.get("provider"),
            proxy_launch.get("upstream_keepalive_seconds"),
            expected_exit_ip=camoufox_geoip,
        )
        # Observe the verified exit IP and TTL deadline. Never rotates the provider session.
        if QA_SESSION_MONITOR.get("status_store") is not None:
            import threading as _qa_monitor_threading
            _qa_monitor_stop = _qa_monitor_threading.Event()
            QA_SESSION_MONITOR["stop_event"] = _qa_monitor_stop
            _qa_health = QaProxyHealthMonitor(
                qa_upstream_proxy,
                QA_SESSION_MONITOR["status_store"],
                requested_targeting,
                QA_SESSION_MONITOR["deadline"],
                _qa_monitor_stop,
            )
            QA_SESSION_MONITOR["health"] = _qa_health
            _qa_health.start()
` : ''}
        if page.url in ("about:blank", ""):
${authorizedQaProxyEnabled ? `            navigate_startup_keep_open(page, resolved_startup_url)
` : `            page.goto(resolved_startup_url, wait_until="domcontentloaded")
`}
${authorizedQaProxyEnabled ? `        enter_qa_manual_operator_mode(browser)
        set_qa_runtime_state("active")
        wait_until_operator_enter(
            page,
            "Authorized QA Proxy Session open. Close the browser window or press Ctrl+C here when "
            "finished. To start another QA session, open a NEW Terminal tab.",
        )
` : `        input("Use the browser normally. Press Enter here when finished.")
`}    except SystemExit:
        raise
    except Exception as exc:
        fail_launch(exc, exit_code=5)
    finally:
${authorizedQaProxyEnabled ? `        if qa_upstream_keepalive is not None:
            qa_upstream_keepalive.stop("session_end")
        stop_qa_ip_usage_reporter()
        stop_qa_session_monitor()
` : ''}        close_camoufox_context_quiet()


def run_camoufox_supervisor():
    """
    Parent process that stays responsive to Ctrl+C and enforces launch timeout.
    Does not import Camoufox. Does not use start_new_session (breaks macOS GUI/WindowServer).
    Relays sanitized worker logs. Redeem happens only inside the worker after worker_started.
    """
    worker_pid_box = {"pid": None}
    install_launcher_signal_handlers(root_pid_getter=lambda: worker_pid_box["pid"])
    clear_launch_status()

    # Log first so a hung display probe is distinguishable from a hung worker.
    stage_log("supervisor_started", "timeout_seconds=%s" % (CAMOUFOX_LAUNCH_TIMEOUT_SECONDS,))
${authorizedQaProxyEnabled ? `    stage_log("qa_launcher_version", "version=%s" % (QA_LAUNCHER_TEMPLATE_VERSION,))
` : ''}    stage_log("before_supervisor_display_measure")
    # Measure display on the supervisor main thread (AppKit/Quartz/Win32 only — no Tk).
    camoufox_os, logical_width, logical_height, window_width, window_height = get_device_settings()
    stage_log(
        "after_supervisor_display_measure",
        "os=%s logical=%sx%s window=%sx%s"
        % (camoufox_os, logical_width, logical_height, window_width, window_height),
    )
    env = os.environ.copy()
    env["FUNDELITE_CAMOUFOX_WORKER"] = "1"
    env["FUNDELITE_CAMOUFOX_OS"] = str(camoufox_os)
    env["FUNDELITE_LOGICAL_WIDTH"] = str(logical_width)
    env["FUNDELITE_LOGICAL_HEIGHT"] = str(logical_height)
    env["FUNDELITE_WINDOW_WIDTH"] = str(window_width)
    env["FUNDELITE_WINDOW_HEIGHT"] = str(window_height)

    # Keep the worker in the same session so headed Camoufox can talk to WindowServer on macOS.
    # Kill process trees via pgrep descendants instead of setsid/killpg.
    # Token redemption happens only inside the worker after stage=worker_started, so a failed
    # Popen cannot burn a one-time launch token.
    try:
        proc = subprocess.Popen(
            [sys.executable, "-u", os.path.abspath(__file__)],
            env=env,
            stdout=subprocess.PIPE,
            stderr=subprocess.STDOUT,
            text=True,
            bufsize=1,
        )
    except Exception as exc:
        stage_log("browser_spawn_failed", "error=worker_spawn %s" % (sanitize_launcher_error_text(exc),))
        raise SystemExit(5)
    worker_pid_box["pid"] = proc.pid
    stage_log("worker_spawned", "pid=%s" % (proc.pid,))

    stop_relay = threading.Event()
    relay_thread = threading.Thread(
        target=relay_worker_output,
        args=(proc, stop_relay),
        name="camoufox-worker-log-relay",
        daemon=True,
    )
    relay_thread.start()

    def _kill_worker(reason):
        cleanup_browser_processes(reason=reason, root_pid=proc.pid)
        try:
            proc.terminate()
        except Exception:
            pass
        deadline = time.time() + 5
        while proc.poll() is None and time.time() < deadline:
            time.sleep(0.1)
        if proc.poll() is None:
            try:
                proc.kill()
            except Exception:
                pass

    def _handle(signum, frame):
        stage_log("interrupted", "signal=%s" % (signum,))
        _kill_worker("signal")
${authorizedQaProxyEnabled ? `        release_qa_runtime_lock_for_pid(proc.pid)
` : ''}        stop_relay.set()
        raise SystemExit(130 if signum == signal.SIGINT else 143)

    signal.signal(signal.SIGINT, _handle)
    signal.signal(signal.SIGTERM, _handle)

    deadline = time.time() + max(15, CAMOUFOX_LAUNCH_TIMEOUT_SECONDS)
    saw_worker_started = False
    connected = False
    failed = False
    while proc.poll() is None and time.time() < deadline:
        status = read_launch_status()
        if status == _LAUNCH_STATUS_STARTED:
            saw_worker_started = True
        if status == _LAUNCH_STATUS_CONNECTED:
            connected = True
            break
        if status == _LAUNCH_STATUS_FAILED:
            failed = True
            break
        time.sleep(0.25)

    # Worker may still be finishing fail_launch cleanup after writing FAILED.
    # Do not mis-classify that as launch_timeout.
    if (
        proc.poll() is None
        and not connected
        and not failed
        and read_launch_status() not in (_LAUNCH_STATUS_CONNECTED, _LAUNCH_STATUS_FAILED)
    ):
        detail = "launch_timeout timeout_seconds=%s worker_started=%s" % (
            CAMOUFOX_LAUNCH_TIMEOUT_SECONDS,
            "yes" if saw_worker_started or read_launch_status() == _LAUNCH_STATUS_STARTED else "no",
        )
        stage_log("browser_spawn_failed", "error=%s" % (detail,))
        print(
            "Camoufox launch timed out after %s seconds. Terminating browser child processes."
            % (CAMOUFOX_LAUNCH_TIMEOUT_SECONDS,),
            flush=True,
        )
        _kill_worker("launch_timeout")
${authorizedQaProxyEnabled ? `        release_qa_runtime_lock_for_pid(proc.pid)
` : ''}        stop_relay.set()
        try:
            relay_thread.join(timeout=2)
        except Exception:
            pass
        exit_code = proc.poll()
        stage_log(
            "worker_exit",
            "status=%s" % (exit_code if exit_code is not None else "killed",),
        )
        clear_launch_status()
        raise SystemExit(5)

    return_code = proc.wait()
${authorizedQaProxyEnabled ? `    release_qa_runtime_lock_for_pid(proc.pid)
` : ''}    stop_relay.set()
    try:
        relay_thread.join(timeout=2)
    except Exception:
        pass
    stage_log("worker_exit", "status=%s" % (return_code,))
    clear_launch_status()
    raise SystemExit(return_code if return_code is not None else 1)


if __name__ == "__main__":
    if os.environ.get("FUNDELITE_CAMOUFOX_WORKER") == "1":
        run_camoufox_session()
    else:
        run_camoufox_supervisor()
`;
  return ensureRawLauncherText(script);
};

export const BROWSER_PROFILE_BOOTSTRAP_NOTE =
  'This one-time command creates or reuses the FundElite Camoufox environment, installs Camoufox GeoIP dependencies if needed, downloads the browser, creates your secure profile, and opens it.';

export const POWERSHELL_FROM_CMD_SWITCH_COMMAND = 'powershell -NoExit';

export const FUNDELITE_CAMOUFOX_VENV_MISSING_LINES = [
  'FundElite Camoufox environment is not installed.',
  'Run the First-time setup command first.',
];

export const WINDOWS_PYTHON_MISSING_MESSAGE =
  'Python is required. Install it from https://www.python.org/downloads/ and select Add Python to PATH, then run this command again.';

export const MACOS_PYTHON_MISSING_MESSAGE =
  'Python is required. Install it from https://www.python.org/downloads/ or with Homebrew (brew install python), then run this command again.';

export const LINUX_PYTHON_MISSING_MESSAGE =
  'Python is required. Install Python 3 with your package manager (for example: sudo apt install python3 python3-pip), then run this command again.';

function unixPythonMissingMessage(os) {
  return normalizeCamoufoxOs(os) === CAMOUFOX_OS_LINUX
    ? LINUX_PYTHON_MISSING_MESSAGE
    : MACOS_PYTHON_MISSING_MESSAGE;
}

/** Shared Windows PowerShell: prefer `py`, fall back to `python`. */
function buildWindowsPythonResolveBlock() {
  return `Write-Host "Detecting Python..."
$Python = $null
$PythonVersion = $null
foreach ($candidate in @('py', 'python')) {
  $cmd = Get-Command $candidate -ErrorAction SilentlyContinue
  if (-not $cmd) { continue }
  try {
    $versionOutput = & $candidate --version 2>&1 | Out-String
    if ($versionOutput -match 'Python') {
      $Python = $candidate
      $PythonVersion = $versionOutput.Trim()
      break
    }
  } catch {
  }
}
if (-not $Python) {
  Write-Host "${WINDOWS_PYTHON_MISSING_MESSAGE}"
  exit 1
}
Write-Host ("Python found: " + $PythonVersion)`;
}

/** Managed FundElite Camoufox venv — avoids Homebrew PEP 668 and installs camoufox[geoip]. */
export const FUNDELITE_CAMOUFOX_VENV_DIR = '~/.fundelite-camoufox-venv';
export const FUNDELITE_CAMOUFOX_VENV_PYTHON_UNIX = '$HOME/.fundelite-camoufox-venv/bin/python';
export const FUNDELITE_CAMOUFOX_VENV_PYTHON_WINDOWS = '$env:USERPROFILE\\.fundelite-camoufox-venv\\Scripts\\python.exe';

/** Shared Windows PowerShell: create/reuse FundElite venv, install camoufox[geoip], fetch browser. */
function buildWindowsCamoufoxEnsureBlock() {
  return `Write-Host "Checking FundElite Camoufox environment..."
$FundEliteVenv = Join-Path $env:USERPROFILE ".fundelite-camoufox-venv"
$CamoufoxPy = Join-Path $FundEliteVenv "Scripts\\python.exe"
if (-not (Test-Path $CamoufoxPy)) {
  Write-Host "Creating FundElite Camoufox virtual environment..."
  & $Python -m venv $FundEliteVenv
  if ($LASTEXITCODE -ne 0 -or -not (Test-Path $CamoufoxPy)) {
    throw "Failed to create FundElite Camoufox virtual environment."
  }
} else {
  Write-Host "Reusing FundElite Camoufox virtual environment."
}
$Python = $CamoufoxPy

Write-Host "Checking Camoufox GeoIP dependencies..."
& $Python -c "import camoufox, certifi, geoip2" 2>$null | Out-Null
if ($LASTEXITCODE -ne 0) {
  Write-Host "Installing camoufox[geoip] and certifi into the FundElite environment..."
  & $Python -m pip install -U pip
  if ($LASTEXITCODE -ne 0) {
    throw "Failed to upgrade pip in the FundElite Camoufox environment."
  }
  & $Python -m pip install -U "camoufox[geoip]" certifi
  if ($LASTEXITCODE -ne 0) {
    throw "Failed to install camoufox[geoip]/certifi in the FundElite Camoufox environment."
  }
  Write-Host "Camoufox GeoIP dependencies installed."
} else {
  Write-Host "Reusing installed Camoufox GeoIP dependencies."
}

Write-Host "Downloading or reusing Camoufox browser..."
$fetchLines = & $Python -m camoufox fetch 2>&1 | ForEach-Object { "$_" }
$fetchText = ($fetchLines -join [Environment]::NewLine)
if ($fetchText) { Write-Host $fetchText }
if ($LASTEXITCODE -ne 0) {
  throw "Failed to fetch the Camoufox browser binary."
}
if ($fetchText -match '(?i)already|exist|skip|up.to.date|reus|cached') {
  Write-Host "Reusing existing Camoufox browser binary."
} elseif ($fetchText -match '(?i)download|fetch|install|extract|unpack') {
  Write-Host "Downloaded Camoufox browser binary."
} else {
  Write-Host "Camoufox browser binary is ready."
}`;
}

function generateUnixSetupCommand({
  script,
  scriptFilename,
  userDataDir,
  overwrite,
  os,
  authorizedQaProxyEnabled = false,
}) {
  const missingPythonMessage = unixPythonMissingMessage(os);
  const writeBlock = `cat > ${scriptFilename} <<'PY'
${script}PY`;
  const writeSection = overwrite
    ? `${writeBlock}

echo "Launcher ${scriptFilename} was regenerated. Existing profile data in ${userDataDir} was kept."`
    : `if [ -f ${scriptFilename} ]; then
  echo "Launcher ${scriptFilename} already exists. Use Regenerate launcher to replace it without deleting profile data."
else
${writeBlock}
fi`;
  const launchSection = authorizedQaProxyEnabled
    ? `echo "QA Browser launcher installed: ${scriptFilename}"
echo "Do NOT run this file directly."
echo "Back in FundElite, click I've completed setup, then Launch QA Session for a short-lived token command."`
    : `echo "Opening secure browser profile..."
"$CAMOUFOX_PY" -u ${scriptFilename}`;

  return ensureRawLauncherText(`cd ~

echo "Detecting Python..."
PYTHON=""
if command -v python3 >/dev/null 2>&1; then
  PYTHON="python3"
elif command -v python >/dev/null 2>&1; then
  PYTHON="python"
fi
if [ -z "$PYTHON" ]; then
  echo "${missingPythonMessage}"
  exit 1
fi
echo "Python found: $($PYTHON --version 2>&1)"

echo "Checking FundElite Camoufox environment..."
FUNDELITE_VENV="$HOME/.fundelite-camoufox-venv"
CAMOUFOX_PY="$FUNDELITE_VENV/bin/python"
if [ ! -x "$CAMOUFOX_PY" ]; then
  echo "Creating FundElite Camoufox virtual environment..."
  "$PYTHON" -m venv "$FUNDELITE_VENV"
  if [ ! -x "$CAMOUFOX_PY" ]; then
    echo "Failed to create FundElite Camoufox virtual environment."
    exit 1
  fi
else
  echo "Reusing FundElite Camoufox virtual environment."
fi

echo "Checking Camoufox GeoIP dependencies..."
if ! "$CAMOUFOX_PY" -c "import camoufox, certifi, geoip2" >/dev/null 2>&1; then
  echo "Installing camoufox[geoip] and certifi into the FundElite environment..."
  "$CAMOUFOX_PY" -m pip install -U pip
  "$CAMOUFOX_PY" -m pip install -U "camoufox[geoip]" certifi
  echo "Camoufox GeoIP dependencies installed."
else
  echo "Reusing installed Camoufox GeoIP dependencies."
fi

echo "Downloading or reusing Camoufox browser..."
FETCH_OUT="$("$CAMOUFOX_PY" -m camoufox fetch 2>&1)"
FETCH_STATUS=$?
echo "$FETCH_OUT"
if [ $FETCH_STATUS -ne 0 ]; then
  echo "Failed to fetch the Camoufox browser binary."
  exit 1
fi
case "$FETCH_OUT" in
  *already*|*exist*|*skip*|*cached*|*reus*)
    echo "Reusing existing Camoufox browser binary."
    ;;
  *download*|*fetch*|*install*|*extract*)
    echo "Downloaded Camoufox browser binary."
    ;;
  *)
    echo "Camoufox browser binary is ready."
    ;;
esac

${writeSection}

mkdir -p ${userDataDir}
wc -l ${scriptFilename}
${launchSection}`);
}

function generateWindowsSetupCommand({
  script,
  scriptFilename,
  userDataDir,
  overwrite,
  authorizedQaProxyEnabled = false,
}) {
  const windowsProfileDir = String(userDataDir).replace(/\//g, '\\');
  const writeBlock = `$launcher = @'
${script}'@
$utf8 = New-Object System.Text.UTF8Encoding $false
[System.IO.File]::WriteAllText($scriptPath, $launcher, $utf8)`;
  const writeSection = overwrite
    ? `${writeBlock}
Write-Host "Launcher ${scriptFilename} was regenerated. Existing profile data in ${windowsProfileDir} was kept."`
    : `if (Test-Path -LiteralPath $scriptPath) {
  Write-Host "Launcher ${scriptFilename} already exists. Use Regenerate launcher to replace it without deleting profile data."
} else {
${writeBlock}
}`;
  const launchSection = authorizedQaProxyEnabled
    ? `Write-Host "QA Browser launcher installed: ${scriptFilename}"
Write-Host "Do NOT run this file directly."
Write-Host "Back in FundElite, click I've completed setup, then Launch QA Session for a short-lived token command."`
    : `Write-Host "Opening secure browser profile..."
& $Python -u $scriptPath`;

  return ensureRawLauncherText(`Set-Location $HOME
$scriptPath = Join-Path $PWD '${scriptFilename}'
$profileDir = Join-Path $PWD '${windowsProfileDir}'

${buildWindowsPythonResolveBlock()}

${buildWindowsCamoufoxEnsureBlock()}

${writeSection}

New-Item -ItemType Directory -Force -Path $profileDir | Out-Null
${launchSection}`);
}

/** Max Base64 characters per CMD `echo` line (keeps each line well under cmd.exe's 8191 limit). */
export const WINDOWS_CMD_BOOTSTRAP_B64_CHUNK_SIZE = 3000;

/** cmd.exe CreateProcess / command-line practical hard limit. */
export const WINDOWS_CMD_MAX_COMMAND_LENGTH = 8191;

/** UTF-16LE Base64 for PowerShell script payloads (chunked CMD bootstrap + tests). */
export function encodePowerShellEncodedCommand(script) {
  const source = String(script ?? '');
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(source, 'utf16le').toString('base64');
  }
  const bytes = [];
  for (let i = 0; i < source.length; i += 1) {
    const code = source.charCodeAt(i);
    bytes.push(code & 0xff, (code >> 8) & 0xff);
  }
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Reconstruct UTF-16LE PowerShell source from a chunked CMD bootstrap that
 * writes Base64 via `echo` into %TEMP% and invokes a short PowerShell -Command.
 */
export function reconstructPowerShellFromCmdBootstrap(cmdBootstrap) {
  const text = String(cmdBootstrap ?? '');
  const chunkRe = /^(?:>{1,2})"\%FCL_B64\%" echo (.+)$/gm;
  const chunks = [];
  let match = chunkRe.exec(text);
  while (match) {
    chunks.push(match[1]);
    match = chunkRe.exec(text);
  }
  if (!chunks.length) {
    throw new Error('CMD bootstrap missing Base64 echo chunks');
  }
  const encoded = chunks.join('').replace(/\s+/g, '');
  return Buffer.from(encoded, 'base64').toString('utf16le');
}

export function maxCmdBootstrapLineLength(cmdBootstrap) {
  return String(cmdBootstrap ?? '')
    .split(/\r?\n/)
    .reduce((max, line) => Math.max(max, line.length), 0);
}

function generateWindowsCmdBootstrapCommand(powershellSetupCommand) {
  const encoded = encodePowerShellEncodedCommand(powershellSetupCommand);
  const chunkSize = WINDOWS_CMD_BOOTSTRAP_B64_CHUNK_SIZE;
  const chunks = [];
  for (let i = 0; i < encoded.length; i += chunkSize) {
    chunks.push(encoded.slice(i, i + chunkSize));
  }
  if (!chunks.length) {
    chunks.push('');
  }

  const lines = [
    '@echo off',
    'REM FundGenius Browser Profile setup',
    'REM This CMD bootstrap opens PowerShell. Do not paste PowerShell syntax into Command Prompt.',
    'set "FCL_B64=%TEMP%\\fcl-cmd-setup-%RANDOM%%RANDOM%.b64"',
    'if exist "%FCL_B64%" del /f /q "%FCL_B64%" >nul 2>&1',
  ];
  chunks.forEach((chunk, index) => {
    const redirect = index === 0 ? '>' : '>>';
    lines.push(`${redirect}"%FCL_B64%" echo ${chunk}`);
  });
  // Short -Command only: read chunked Base64, decode UTF-16LE, run ScriptBlock.
  lines.push(
    'powershell -NoProfile -ExecutionPolicy Bypass -Command "& { $p=$args[0]; $b=[IO.File]::ReadAllText($p) -replace \'\\s\',\'\'; $s=[Text.Encoding]::Unicode.GetString([Convert]::FromBase64String($b)); & ([ScriptBlock]::Create($s)); if ($null -ne $LASTEXITCODE) { exit [int]$LASTEXITCODE } }" "%FCL_B64%"',
  );
  lines.push('set "FCL_ERR=%ERRORLEVEL%"');
  lines.push('del /f /q "%FCL_B64%" >nul 2>&1');
  lines.push('exit /b %FCL_ERR%');
  return ensureRawLauncherText(lines.join('\n'));
}

export function generateFirstTimeSetupCommand({
  script,
  scriptFilename,
  userDataDir,
  os,
  terminal,
  authorizedQaProxyEnabled = false,
}) {
  const normalizedScript = normalizePythonSource(script);
  const target = normalizeTerminalTarget(terminal)
    || inferTerminalTargetFromOs(os);
  if (target === TERMINAL_TARGET_WINDOWS_POWERSHELL || target === TERMINAL_TARGET_WINDOWS_CMD) {
    const powershell = generateWindowsSetupCommand({
      script: normalizedScript,
      scriptFilename,
      userDataDir,
      overwrite: false,
      authorizedQaProxyEnabled,
    });
    if (target === TERMINAL_TARGET_WINDOWS_CMD) {
      return generateWindowsCmdBootstrapCommand(powershell);
    }
    return powershell;
  }
  return generateUnixSetupCommand({
    script: normalizedScript,
    scriptFilename,
    userDataDir,
    overwrite: false,
    os: camoufoxOsForTerminalTarget(target, os),
    authorizedQaProxyEnabled,
  });
}

export function generateRegenerateLauncherCommand({
  script,
  scriptFilename,
  userDataDir,
  os,
  terminal,
  authorizedQaProxyEnabled = false,
}) {
  const normalizedScript = normalizePythonSource(script);
  const target = normalizeTerminalTarget(terminal)
    || inferTerminalTargetFromOs(os);
  if (target === TERMINAL_TARGET_WINDOWS_POWERSHELL || target === TERMINAL_TARGET_WINDOWS_CMD) {
    const powershell = generateWindowsSetupCommand({
      script: normalizedScript,
      scriptFilename,
      userDataDir,
      overwrite: true,
      authorizedQaProxyEnabled,
    });
    if (target === TERMINAL_TARGET_WINDOWS_CMD) {
      return generateWindowsCmdBootstrapCommand(powershell);
    }
    return powershell;
  }
  return generateUnixSetupCommand({
    script: normalizedScript,
    scriptFilename,
    userDataDir,
    overwrite: true,
    os: camoufoxOsForTerminalTarget(target, os),
    authorizedQaProxyEnabled,
  });
}

export function getBrowserProfileRunCommand(scriptFilename, os, terminal, options = {}) {
  const launcherType = options.launcherType || PRIMARY_LAUNCHER;
  let filename = String(scriptFilename || '').trim();
  if (!filename) return '';
  if (launcherType === QA_AUTHORIZED_LAUNCHER) {
    // Tokenless QA re-run is never a valid Primary-style reopen.
    return getAuthorizedQaProxyTokenRequiredHintCommand(os, terminal);
  }
  if (launcherType === PRIMARY_LAUNCHER) {
    filename = assertPrimaryBrowserScriptFilename(filename);
  }
  const target = normalizeTerminalTarget(terminal)
    || (isWindowsCamoufoxOs(os) ? TERMINAL_TARGET_WINDOWS_POWERSHELL : TERMINAL_TARGET_UNIX);
  const [missingLine1, missingLine2] = FUNDELITE_CAMOUFOX_VENV_MISSING_LINES;

  // Do not run ensureRawLauncherText here: Markdown unescape treats "\." as an
  // escaped period and would corrupt Windows paths like %USERPROFILE%\.fundelite-...
  if (target === TERMINAL_TARGET_WINDOWS_CMD) {
    return [
      '@echo off',
      'set "CAMOUFOX_PY=%USERPROFILE%\\.fundelite-camoufox-venv\\Scripts\\python.exe"',
      'if not exist "%CAMOUFOX_PY%" (',
      `  echo ${missingLine1}`,
      `  echo ${missingLine2}`,
      '  exit /b 1',
      ')',
      `"%CAMOUFOX_PY%" -u ${filename}`,
    ].join('\n');
  }

  if (target === TERMINAL_TARGET_WINDOWS_POWERSHELL) {
    return [
      '$CamoufoxPy = Join-Path $env:USERPROFILE ".fundelite-camoufox-venv\\Scripts\\python.exe"',
      'if (-not (Test-Path -LiteralPath $CamoufoxPy)) {',
      `  Write-Host "${missingLine1}"`,
      `  Write-Host "${missingLine2}"`,
      '  exit 1',
      '}',
      `& $CamoufoxPy -u ${filename}`,
    ].join('\n');
  }

  return [
    'CAMOUFOX_PY="$HOME/.fundelite-camoufox-venv/bin/python"',
    'if [ ! -x "$CAMOUFOX_PY" ]; then',
    `  echo "${missingLine1}"`,
    `  echo "${missingLine2}"`,
    '  exit 1',
    'fi',
    `"$CAMOUFOX_PY" -u ${filename}`,
  ].join('\n');
}

/** Shell stub: QA session must never be reopened without a fresh launch token. */
export function getAuthorizedQaProxyTokenRequiredHintCommand(os, terminal) {
  const target = normalizeTerminalTarget(terminal)
    || (isWindowsCamoufoxOs(os) ? TERMINAL_TARGET_WINDOWS_POWERSHELL : TERMINAL_TARGET_UNIX);
  const line1 = 'QA Browser requires a short-lived Launch QA Session token.';
  const line2 = 'Return to FundElite and click Launch QA Session. Do not run this launcher directly.';
  if (target === TERMINAL_TARGET_WINDOWS_CMD) {
    return ['@echo off', `echo ${line1}`, `echo ${line2}`, 'exit /b 1'].join('\n');
  }
  if (target === TERMINAL_TARGET_WINDOWS_POWERSHELL) {
    return [
      `Write-Host "${line1}"`,
      `Write-Host "${line2}"`,
      'exit 1',
    ].join('\n');
  }
  return [
    `echo "${line1}"`,
    `echo "${line2}"`,
    'exit 1',
  ].join('\n');
}

export function getBrowserProfileSetupCopyLabel(terminal) {
  const target = normalizeTerminalTarget(terminal) || TERMINAL_TARGET_UNIX;
  if (target === TERMINAL_TARGET_WINDOWS_POWERSHELL) {
    return 'Copy PowerShell setup command';
  }
  if (target === TERMINAL_TARGET_WINDOWS_CMD) {
    return 'Copy Windows Command Prompt setup command';
  }
  return 'Copy macOS/Linux setup command';
}

export function getBrowserProfileEditCommand(scriptFilename, os) {
  const camoufoxOs = normalizeCamoufoxOs(os) || CAMOUFOX_OS_MACOS;
  if (camoufoxOs === CAMOUFOX_OS_WINDOWS) return `notepad ${scriptFilename}`;
  if (camoufoxOs === CAMOUFOX_OS_LINUX) return `xdg-open ${scriptFilename}`;
  return `open -e ${scriptFilename}`;
}

export const generateBrowserProfileCommands = ({
  clientId,
  profileName,
  scriptFilename,
  script,
  startupUrl,
  os,
  terminal,
  fallbackWidth,
  fallbackHeight,
  fallbackOs,
  width,
  height,
  authorizedQaProxyEnabled = false,
  authorizedQaProxyLaunchConfigUrl,
} = {}) => {
  const safeProfileName = sanitizeBrowserProfileName(profileName);
  const userDataDir = buildBrowserProfilePath({ clientId, profileName: safeProfileName });
  const filename = buildBrowserProfileScriptFilename(clientId, safeProfileName, scriptFilename);
  const terminalTarget = normalizeTerminalTarget(terminal)
    || inferTerminalTargetFromOs(os || fallbackOs);
  const camoufoxOs = camoufoxOsForTerminalTarget(terminalTarget, os || fallbackOs);
  const resolvedScript = ensureRawLauncherText(script || generateCamoufoxProfileScript({
    clientId,
    profileName: safeProfileName,
    startupUrl,
    fallbackWidth: fallbackWidth || width,
    fallbackHeight: fallbackHeight || height,
    fallbackOs: camoufoxOs,
    authorizedQaProxyEnabled,
    authorizedQaProxyLaunchConfigUrl,
  }));
  const powershellSetupCommand = generateWindowsSetupCommand({
    script: normalizePythonSource(resolvedScript),
    scriptFilename: filename,
    userDataDir,
    overwrite: false,
    authorizedQaProxyEnabled,
  });
  const setupCommand = generateFirstTimeSetupCommand({
    script: resolvedScript,
    scriptFilename: filename,
    userDataDir,
    os: camoufoxOs,
    terminal: terminalTarget,
    authorizedQaProxyEnabled,
  });
  const regenerateCommand = generateRegenerateLauncherCommand({
    script: resolvedScript,
    scriptFilename: filename,
    userDataDir,
    os: camoufoxOs,
    terminal: terminalTarget,
    authorizedQaProxyEnabled,
  });
  const runCommand = getBrowserProfileRunCommand(
    filename,
    camoufoxOs,
    terminalTarget,
    {
      launcherType: authorizedQaProxyEnabled
        ? QA_AUTHORIZED_LAUNCHER
        : PRIMARY_LAUNCHER,
    },
  );
  const mkdirCommand = isWindowsCamoufoxOs(camoufoxOs)
    ? `New-Item -ItemType Directory -Force -Path "${String(userDataDir).replace(/\//g, '\\')}" | Out-Null`
    : `mkdir -p ${userDataDir}`;

  return {
    setupCommand: ensureRawLauncherText(setupCommand),
    powershellSetupCommand: ensureRawLauncherText(powershellSetupCommand),
    runAgainCommand: runCommand,
    editCommand: getBrowserProfileEditCommand(filename, camoufoxOs),
    runCommand,
    mkdirCommand,
    combinedCommand: ensureRawLauncherText(setupCommand),
    regenerateCommand: ensureRawLauncherText(regenerateCommand),
    scriptFilename: filename,
    userDataDir,
    os: camoufoxOs,
    terminal: terminalTarget,
    displayMode: DISPLAY_MODE_ADAPTIVE,
  };
};

export function isAuthorizedQaProxyAssignmentActive(proxy) {
  return Boolean(
    proxy
    && proxy.badge === PROXY_BADGE_AUTHORIZED
    && proxy.status === 'active'
    && proxy.credentialSaved !== false,
  );
}

export function enrichBrowserProfileRecord(profile, clientId, deviceSettings = {}) {
  if (!profile || !clientId) return profile;

  const profileName = profile.profileName || profile.profile_name;
  const storedScriptFilename = profile.scriptFilename || profile.script_filename;
  const startupUrl = resolveBrowserProfileStartupUrl(profile);
  const displayMode = resolveDisplayMode(profile);
  const terminalTarget = normalizeTerminalTarget(deviceSettings.terminal)
    || inferTerminalTargetFromOs(
      deviceSettings.os || profile.detectedOs || profile.detected_os,
    );
  const camoufoxOs = camoufoxOsForTerminalTarget(
    terminalTarget,
    deviceSettings.os || profile.detectedOs || profile.detected_os,
  );
  const fallback = resolveFallbackDisplaySize({
    width: deviceSettings.fallbackWidth || profile.detectedScreenWidth || profile.detected_screen_width || profile.width,
    height: deviceSettings.fallbackHeight || profile.detectedScreenHeight || profile.detected_screen_height || profile.height,
  });
  const proxy = normalizeAuthorizedQaProxySummary(
    profile.authorizedQaProxy || profile.authorized_qa_proxy || null,
  );
  const authorizedQaProxyLaunchConfigUrl = deviceSettings.authorizedQaProxyLaunchConfigUrl
    || resolveAuthorizedQaProxyLaunchConfigUrl();

  // Primary Browser launcher is always direct — never embed proxy/geoip here.
  const script = generateCamoufoxProfileScript({
    clientId,
    profileName,
    startupUrl,
    fallbackWidth: fallback.width,
    fallbackHeight: fallback.height,
    fallbackOs: camoufoxOs,
    authorizedQaProxyEnabled: false,
  });
  const commands = generateBrowserProfileCommands({
    clientId,
    profileName,
    scriptFilename: storedScriptFilename,
    script,
    startupUrl,
    os: camoufoxOs,
    terminal: terminalTarget,
    fallbackWidth: fallback.width,
    fallbackHeight: fallback.height,
    fallbackOs: camoufoxOs,
    authorizedQaProxyEnabled: false,
  });

  let authorizedQaProxySession = null;
  if (isAuthorizedQaProxyAssignmentActive(proxy)) {
    const sessionScript = generateCamoufoxProfileScript({
      clientId,
      profileName: AUTHORIZED_QA_PROXY_SESSION_SLUG,
      startupUrl: FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL,
      fallbackWidth: fallback.width,
      fallbackHeight: fallback.height,
      fallbackOs: camoufoxOs,
      authorizedQaProxyEnabled: true,
      authorizedQaProxyLaunchConfigUrl,
    });
    const sessionCommands = generateBrowserProfileCommands({
      clientId,
      profileName: AUTHORIZED_QA_PROXY_SESSION_SLUG,
      script: sessionScript,
      startupUrl: FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL,
      os: camoufoxOs,
      terminal: terminalTarget,
      fallbackWidth: fallback.width,
      fallbackHeight: fallback.height,
      fallbackOs: camoufoxOs,
      authorizedQaProxyEnabled: true,
      authorizedQaProxyLaunchConfigUrl,
    });
    authorizedQaProxySession = {
      displayName: AUTHORIZED_QA_PROXY_SESSION_DISPLAY_NAME,
      profileName: AUTHORIZED_QA_PROXY_SESSION_SLUG,
      scriptFilename: sessionCommands.scriptFilename,
      userDataDir: sessionCommands.userDataDir,
      script: sessionScript,
      commands: sessionCommands,
      networkMode: 'global_proxy_geo_aligned',
      banner: AUTHORIZED_QA_PROXY_SESSION_BANNER,
    };
  }

  return {
    ...profile,
    profileName,
    displayName: getBrowserProfileDisplayName(profile),
    isPrimary: isPrimaryBrowserProfile(profile),
    displayMode,
    userDataDir: profile.userDataDir || profile.user_data_dir || commands.userDataDir,
    scriptFilename: storedScriptFilename || commands.scriptFilename,
    startupUrl,
    searchEngine: profile.searchEngine || profile.search_engine || DEFAULT_SEARCH_ENGINE,
    script,
    commands,
    terminal: terminalTarget,
    authorizedQaProxy: proxy,
    authorizedQaProxySession,
    primaryNetworkMode: 'direct',
  };
}

function agencyQuery(agencyId) {
  return new URLSearchParams({ agency_id: agencyId }).toString();
}

function assertScopedClientId(clientId) {
  const scopedId = resolveBrowserProfileClientId({ id: clientId, clientId });
  if (!scopedId || !CLIENT_ID_PATTERN.test(scopedId)) {
    throw new Error('Client ID is required.');
  }
  return scopedId;
}

export async function createBrowserProfile({
  clientId,
  agencyId,
  profileName,
  displayName,
  width,
  height,
  startupUrl = DEFAULT_STARTUP_URL,
  searchEngine = DEFAULT_SEARCH_ENGINE,
  displayMode = DISPLAY_MODE_ADAPTIVE,
  detectedOs,
  detectedScreenWidth,
  detectedScreenHeight,
  devicePixelRatio,
  os,
}) {
  const scopedClientId = assertScopedClientId(clientId);
  const safeProfileName = sanitizeBrowserProfileName(profileName);
  const params = agencyQuery(agencyId);
  const fallback = resolveFallbackDisplaySize({
    width,
    height,
    screenWidth: detectedScreenWidth,
    screenHeight: detectedScreenHeight,
  });

  const payload = {
    agency_id: agencyId,
    profile_name: safeProfileName,
    width: fallback.width,
    height: fallback.height,
    display_mode: displayMode || DISPLAY_MODE_ADAPTIVE,
    detected_os: normalizeCamoufoxOs(detectedOs || os) || undefined,
    detected_screen_width: fallback.width,
    detected_screen_height: fallback.height,
    device_pixel_ratio: devicePixelRatio,
    startup_url: resolveBrowserProfileStartupUrl(startupUrl),
    search_engine: searchEngine || DEFAULT_SEARCH_ENGINE,
  };

  if (displayName) {
    payload.display_name = displayName;
  } else if (safeProfileName === PRIMARY_BROWSER_PROFILE_SLUG) {
    payload.display_name = PRIMARY_BROWSER_DISPLAY_NAME;
  }

  try {
    const data = await fundingHqFetch(
      `/clients/${encodeURIComponent(scopedClientId)}/browser-profiles?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    return enrichBrowserProfileRecord(data, scopedClientId);
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to create browser profile.'));
  }
}

export async function listBrowserProfiles({ clientId, agencyId }) {
  const scopedClientId = assertScopedClientId(clientId);
  const params = agencyQuery(agencyId);

  try {
    const data = await fundingHqFetch(
      `/clients/${encodeURIComponent(scopedClientId)}/browser-profiles?${params}`,
    );
    const profiles = (data.profiles ?? []).map((profile) =>
      enrichBrowserProfileRecord(profile, scopedClientId),
    );
    return { profiles, total: data.total ?? profiles.length };
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to load browser profiles.'));
  }
}

export async function archiveBrowserProfile({ clientId, agencyId, profileId }) {
  const scopedClientId = assertScopedClientId(clientId);
  const params = agencyQuery(agencyId);

  try {
    return await fundingHqFetch(
      `/clients/${encodeURIComponent(scopedClientId)}/browser-profiles/${encodeURIComponent(profileId)}?${params}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }),
      },
    );
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to archive browser profile.'));
  }
}

export async function upgradeBrowserProfileToAdaptive({ clientId, agencyId, profileId }) {
  const scopedClientId = assertScopedClientId(clientId);
  const params = agencyQuery(agencyId);

  try {
    const data = await fundingHqFetch(
      `/clients/${encodeURIComponent(scopedClientId)}/browser-profiles/${encodeURIComponent(profileId)}?${params}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'upgrade_to_adaptive' }),
      },
    );
    return enrichBrowserProfileRecord(data, scopedClientId);
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to upgrade browser profile.'));
  }
}

export async function convertBrowserProfileToPrimary({ clientId, agencyId, profileId }) {
  const scopedClientId = assertScopedClientId(clientId);
  const params = agencyQuery(agencyId);

  try {
    const data = await fundingHqFetch(
      `/clients/${encodeURIComponent(scopedClientId)}/browser-profiles/${encodeURIComponent(profileId)}?${params}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'convert_to_primary' }),
      },
    );
    return enrichBrowserProfileRecord(data, scopedClientId);
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to convert browser profile.'));
  }
}

export function downloadBrowserProfileScript(script, scriptFilename) {
  if (!script || !scriptFilename) return;
  const blob = new Blob([script], { type: 'text/x-python;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = scriptFilename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}
